import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_URL, ENABLE_DSR } from "../config";
import { apiRequest, formatApiError } from "../apiRequest";
import DsrReportDetail from "../components/DsrReportDetail";
import DsrReportsWorkspace from "../components/DsrReportsWorkspace";
import DeleteUserDialog from "../components/DeleteUserDialog";
import BreakSettingsEditor from '../components/BreakSettingsEditor';
import DashboardControls from '../components/DashboardControls';
import { ArrowLeft } from "lucide-react";
import ArrTicker from "../components/ArrTicker";
import { ARR_REFRESH_INTERVAL_MS, formatARR } from "../utils/arr";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
Pie,
Cell
} from "recharts";
function getAuthHeaders(extraHeaders = {}) {
  const token = localStorage.getItem("token") || "";

  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`
  };
}

function handleAuthExpired(response) {
  if (response.status !== 401 && response.status !== 403) {
    return false;
  }

  localStorage.removeItem("token");
  localStorage.removeItem("user");
  window.location.reload();
  return true;
}

const DEFAULT_BREAK_LIMIT = 60 * 60 * 1000;
const BREAK_LIMIT_STORAGE_KEY = "salesDashboardDailyBreakLimitMinutes";
const ZOOM_ON_CALL_STALE_LIMIT = 30 * 60 * 1000;
const PIE_COLORS = [
  "#3b82f6",
  "#22c55e",
  "#f59e0b",
  "#ef4444",
  "#a855f7",
  "#06b6d4",
  "#eab308"
];
const ANALYTICS_TABS = [
  { key: "arr", label: "ARR Analytics" },
  { key: "teamArr", label: "Team ARR" },
  { key: "targetPace", label: "Target Pace" },
  { key: "salesTimeline", label: "Sales Timeline" },
  { key: "devices", label: "Devices" },
  { key: "lgs", label: "LGS Conversion" },
  { key: "breakReports", label: "Break Reports" },
  { key: "opsCenter", label: "Ops Center" },
  { key: "insights", label: "Insights" }
];
const TV_ANALYTICS_TAB_KEYS = new Set([
  "arr",
  "teamArr",
  "targetPace",
  "salesTimeline",
  "devices",
  "lgs",
  "insights"
]);

const AGENTS_REFRESH_MS = 30000;
const AGENTS_VERSION_REFRESH_MS = 2000;
const DASHBOARD_REFRESH_MS = ARR_REFRESH_INTERVAL_MS;
const PASSWORD_REQUESTS_REFRESH_MS = 120000;
const AGENT_STATUS_EVENT_KEY = "sales-floor-dashboard:agent-status-changed";
const DSR_ROLE_OPTIONS = [
  { value: "BDE", label: "BDE" },
  { value: "LGS", label: "LGS" },
  { value: "SR.LGS", label: "SR. LGS" },
  { value: "SR.BDE", label: "SR. BDE" },
  { value: "TL", label: "TL" },
  { value: "CPA", label: "CPA" },
  { value: "FS", label: "FS" }
];
const ACCOUNT_ROLE_LABELS = {
  admin: "Admin",
  agent: "Agent",
  tv: "TV Viewer",
  am: "Assistant Manager (AM)",
  qa: "Quality Auditor (QA)",
  sm: "Senior Manager (SM)"
};
const USER_TEAM_OPTIONS = ["Team Aaina", "Team Harsh"];

function formatDsrRoleLabel(role) {
  const normalized = String(role || "BDE").toUpperCase().replace(/[^A-Z0-9]+/g, ".");
  if (normalized === "SR.LGS") return "SR. LGS";
  if (normalized === "SR.BDE") return "SR. BDE";
  return String(role || "BDE").toUpperCase();
}

function runWhenVisible(callback) {
  if (document.visibilityState === "visible") {
    callback();
  }
}
const MONTH_INDEX = {
  Jan: 0,
  January: 0,
  Feb: 1,
  February: 1,
  Mar: 2,
  March: 2,
  Apr: 3,
  April: 3,
  May: 4,
  Jun: 5,
  June: 5,
  Jul: 6,
  July: 6,
  Aug: 7,
  August: 7,
  Sep: 8,
  September: 8,
  Oct: 9,
  October: 9,
  Nov: 10,
  November: 10,
  Dec: 11,
  December: 11
};
const MONTH_LABELS = {
  "01": "January",
  "02": "February",
  "03": "March",
  "04": "April",
  "05": "May",
  "06": "June",
  "07": "July",
  "08": "August",
  "09": "September",
  "10": "October",
  "11": "November",
  "12": "December"
};
const MONTH_OPTIONS = Object.entries(MONTH_LABELS).map(([value, label]) => ({
  value,
  label
}));

function getMonthLabel(month) {
  return MONTH_LABELS[month] || "All Months";
}

function getDefaultYtdThroughPeriod() {
  const now = new Date();
  let year = now.getFullYear();
  let month = now.getMonth();

  if (month === 0) {
    year -= 1;
    month = 12;
  }

  return {
    month: String(month).padStart(2, "0"),
    year: String(year)
  };
}

function formatInsightPercent(value) {
  const number = Number(value || 0);
  return `${number % 1 === 0 ? number.toFixed(0) : number.toFixed(1)}%`;
}

function formatTimer(startTime) {
  if (!startTime) return "0m 0s";

  const diff = Date.now() - Number(startTime);
  const minutes = Math.floor(diff / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);

  return `${minutes}m ${seconds}s`;
}

function formatTodayTimer(startTime) {
  if (!startTime) return "0m 0s";

  const diff = Date.now() - Math.max(Number(startTime), getTodayStartTimestamp());
  const minutes = Math.floor(Math.max(0, diff) / 60000);
  const seconds = Math.floor((Math.max(0, diff) % 60000) / 1000);

  return `${minutes}m ${seconds}s`;
}

function getTodayDateInput() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(new Date());

  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;

  return `${year}-${month}-${day}`;
}

function getPstDateParts(dateValue) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23"
  }).formatToParts(new Date(dateValue));
  const read = (type) => parts.find((part) => part.type === type)?.value;

  return {
    date: `${read("year")}-${read("month")}-${read("day")}`,
    hour: Number(read("hour") || 0),
    minute: Number(read("minute") || 0),
    second: Number(read("second") || 0)
  };
}

function getPstDayStartTimestamp(dateString) {
  const [year, month, day] = String(dateString || "")
    .split("-")
    .map((part) => Number(part));

  if (!year || !month || !day) return Date.now();

  return [7, 8]
    .map((utcHour) => Date.UTC(year, month - 1, day, utcHour, 0, 0, 0))
    .map((timestamp) => {
      const parts = getPstDateParts(timestamp);
      const localSeconds = parts.hour * 3600 + parts.minute * 60 + parts.second;
      return {
        timestamp,
        score:
          parts.date === dateString ? localSeconds : Number.MAX_SAFE_INTEGER
      };
    })
    .sort((left, right) => left.score - right.score)[0].timestamp;
}

function getTodayStartTimestamp() {
  return getPstDayStartTimestamp(getTodayDateInput());
}

function formatMilliseconds(ms) {
  if (!ms || ms < 0) return "0m 0s";

  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds}s`;
  }

  return `${minutes}m ${seconds}s`;
}

function getCelebrationDealType(popup) {
  const dealType = String(popup?.dealType || "").trim();

  if (dealType) {
    return dealType;
  }

  const outbound = Number(popup?.outboundARR || 0);
  const inbound = Number(popup?.inboundARR || 0);

  if (outbound > 0 && inbound > 0) return "Outbound + Inbound";
  if (outbound > 0) return "Outbound";
  if (inbound > 0) return "Inbound";

  return "ARR";
}

function getTargetAchievementSource(popup) {
  const dealType = String(popup?.dealType || "").trim().toLowerCase();

  if (dealType.includes("outbound")) return "Outbound";
  if (dealType.includes("inbound")) return "Inbound";

  const outbound = Number(popup?.outboundARR || 0);
  const inbound = Number(popup?.inboundARR || 0);

  if (outbound > 0 && outbound >= inbound) return "Outbound";
  if (inbound > 0) return "Inbound";

  return "Outbound";
}

function getInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }

  return (parts[0] || "A").slice(0, 2).toUpperCase();
}

function CelebrationRocketVisual() {
  return (
    <div className="celebration-art celebration-rocket-art" aria-hidden="true">
      <span className="celebration-orbit celebration-orbit-one" />
      <span className="celebration-orbit celebration-orbit-two" />
      <span className="celebration-orbit celebration-orbit-three" />
      <svg
        className="celebration-rocket-svg"
        viewBox="0 0 240 240"
        role="img"
        aria-label="Rocket"
      >
        <defs>
          <radialGradient id="rocketPlanetGlow" cx="50%" cy="44%" r="62%">
            <stop offset="0" stopColor="#8be9ff" stopOpacity="0.42" />
            <stop offset="0.58" stopColor="#0ea5e9" stopOpacity="0.2" />
            <stop offset="1" stopColor="#0f172a" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="rocketBody" x1="90" y1="30" x2="150" y2="178" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#fbfdff" />
            <stop offset="0.24" stopColor="#dff7ff" />
            <stop offset="0.58" stopColor="#37caff" />
            <stop offset="1" stopColor="#2563eb" />
          </linearGradient>
          <linearGradient id="rocketFin" x1="52" y1="132" x2="190" y2="184" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#1d4ed8" />
            <stop offset="0.5" stopColor="#0ea5e9" />
            <stop offset="1" stopColor="#67e8f9" />
          </linearGradient>
          <radialGradient id="rocketWindow" cx="50%" cy="42%" r="58%">
            <stop offset="0" stopColor="#ffffff" />
            <stop offset="0.46" stopColor="#9ff6ff" />
            <stop offset="1" stopColor="#0284c7" />
          </radialGradient>
          <linearGradient id="rocketFlame" x1="120" y1="154" x2="120" y2="226" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#e0fbff" />
            <stop offset="0.34" stopColor="#22d3ee" />
            <stop offset="0.72" stopColor="#2563eb" stopOpacity="0.52" />
            <stop offset="1" stopColor="#0f172a" stopOpacity="0" />
          </linearGradient>
          <filter id="rocketGlow" x="-45%" y="-45%" width="190%" height="190%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="0 0 0 0 0.05 0 0 0 0 0.65 0 0 0 0 1 0 0 0 .85 0"
            />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <circle cx="120" cy="120" r="88" fill="url(#rocketPlanetGlow)" />
        <path d="M32 173c36-21 119-28 176-8" fill="none" stroke="#38bdf8" strokeOpacity="0.34" strokeWidth="4" />
        <path d="M54 191c44-14 98-14 132 0" fill="none" stroke="#7dd3fc" strokeOpacity="0.25" strokeWidth="3" />
        <g filter="url(#rocketGlow)" transform="rotate(-14 120 120)">
          <path
            d="M120 23c30 26 45 58 45 96 0 31-11 59-32 83h-26c-21-24-32-52-32-83 0-38 15-70 45-96z"
            fill="url(#rocketBody)"
          />
          <path
            d="M79 136c-24 6-41 23-50 52 24-10 44-15 61-15z"
            fill="url(#rocketFin)"
          />
          <path
            d="M161 136c24 6 41 23 50 52-24-10-44-15-61-15z"
            fill="url(#rocketFin)"
          />
          <circle cx="120" cy="96" r="27" fill="url(#rocketWindow)" stroke="#e0f7ff" strokeWidth="6" />
          <circle cx="111" cy="87" r="8" fill="#ffffff" fillOpacity="0.78" />
          <path d="M108 174h24l-7 50c-2 11-13 11-16 0z" fill="url(#rocketFlame)" />
          <path d="M97 196c15 8 31 8 46 0" stroke="#67e8f9" strokeWidth="7" strokeLinecap="round" opacity="0.72" />
        </g>
      </svg>
    </div>
  );
}

function TargetAchievedTrophyEmblem() {
  return (
    <div className="target-achieved-emblem" aria-hidden="true">
      <span className="target-achieved-ring target-achieved-ring-outer" />
      <span className="target-achieved-ring target-achieved-ring-mid" />
      <span className="target-achieved-ring target-achieved-ring-inner" />
      <svg
        className="target-achieved-trophy-svg"
        viewBox="0 0 300 320"
        role="img"
        aria-label="Achievement trophy"
      >
        <defs>
          <radialGradient id="targetTrophyHalo" cx="50%" cy="48%" r="62%">
            <stop offset="0" stopColor="#fff7ad" stopOpacity="0.66" />
            <stop offset="0.42" stopColor="#f59e0b" stopOpacity="0.22" />
            <stop offset="1" stopColor="#030712" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="targetGoldFace" x1="88" y1="34" x2="204" y2="236" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#fff8c7" />
            <stop offset="0.16" stopColor="#fde047" />
            <stop offset="0.43" stopColor="#f59e0b" />
            <stop offset="0.66" stopColor="#c76a05" />
            <stop offset="1" stopColor="#78350f" />
          </linearGradient>
          <linearGradient id="targetGoldHighlight" x1="92" y1="56" x2="178" y2="144" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#fffced" />
            <stop offset="0.48" stopColor="#fde68a" />
            <stop offset="1" stopColor="#f59e0b" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="targetGoldBase" x1="86" y1="206" x2="214" y2="302" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#fde68a" />
            <stop offset="0.36" stopColor="#e18b08" />
            <stop offset="0.72" stopColor="#92400e" />
            <stop offset="1" stopColor="#451a03" />
          </linearGradient>
          <filter id="targetTrophyGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 .68 0 0 0 0 .06 0 0 0 .9 0"
            />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <ellipse cx="150" cy="154" rx="122" ry="130" fill="url(#targetTrophyHalo)" />
        <g className="target-achieved-laurel" fill="#d99b19">
          <path d="M81 226C46 188 39 120 76 72" fill="none" stroke="#d99b19" strokeLinecap="round" strokeWidth="5" />
          <ellipse cx="61" cy="203" rx="7" ry="18" transform="rotate(-34 61 203)" />
          <ellipse cx="51" cy="177" rx="7" ry="17" transform="rotate(-48 51 177)" />
          <ellipse cx="49" cy="149" rx="7" ry="16" transform="rotate(-64 49 149)" />
          <ellipse cx="56" cy="121" rx="7" ry="15" transform="rotate(-80 56 121)" />
          <ellipse cx="70" cy="94" rx="7" ry="15" transform="rotate(-98 70 94)" />
          <path d="M219 226c35-38 42-106 5-154" fill="none" stroke="#d99b19" strokeLinecap="round" strokeWidth="5" />
          <ellipse cx="239" cy="203" rx="7" ry="18" transform="rotate(34 239 203)" />
          <ellipse cx="249" cy="177" rx="7" ry="17" transform="rotate(48 249 177)" />
          <ellipse cx="251" cy="149" rx="7" ry="16" transform="rotate(64 251 149)" />
          <ellipse cx="244" cy="121" rx="7" ry="15" transform="rotate(80 244 121)" />
          <ellipse cx="230" cy="94" rx="7" ry="15" transform="rotate(98 230 94)" />
        </g>
        <g filter="url(#targetTrophyGlow)">
          <path
            d="M101 82H70c-18 0-32 14-32 32 0 42 33 68 78 75l6-28c-31-6-52-22-52-45 0-8 6-14 14-14h20z"
            fill="none"
            stroke="url(#targetGoldFace)"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="15"
          />
          <path
            d="M199 82h31c18 0 32 14 32 32 0 42-33 68-78 75l-6-28c31-6 52-22 52-45 0-8-6-14-14-14h-20z"
            fill="none"
            stroke="url(#targetGoldFace)"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="15"
          />
          <path
            d="M91 55h118c-4 78-22 128-59 128S95 133 91 55z"
            fill="url(#targetGoldFace)"
            stroke="#fff3bf"
            strokeOpacity="0.52"
            strokeWidth="3"
          />
          <path d="M113 70h68c-4 52-15 83-31 83s-29-31-37-83z" fill="url(#targetGoldHighlight)" opacity="0.72" />
          <path
            d="M150 91l11 24 26 3-19 18 5 26-23-13-23 13 5-26-19-18 26-3z"
            fill="#92400e"
            opacity="0.72"
          />
          <path d="M134 182h32v39h-32z" fill="url(#targetGoldBase)" />
          <path d="M105 216h90l16 29H89z" fill="url(#targetGoldBase)" />
          <rect x="74" y="241" width="152" height="27" rx="6" fill="url(#targetGoldBase)" />
          <rect x="96" y="267" width="108" height="18" rx="5" fill="#5f2c0b" />
          <rect x="104" y="240" width="92" height="10" rx="3" fill="#fde68a" opacity="0.42" />
        </g>
      </svg>
    </div>
  );
}

function TargetAchievedPopup({ popup, period, onClose }) {
  const source = getTargetAchievementSource(popup);

  return (
    <div className="target-achieved-stage">
      <section className="target-achieved-card" aria-label="Target Achieved">
        <div className="target-achieved-glow" aria-hidden="true" />
        <div className="target-achieved-sparkles" aria-hidden="true">
          {Array.from({ length: 22 }).map((_, index) => (
            <span key={index} />
          ))}
        </div>
        <button
          type="button"
          className="target-achieved-close"
          onClick={onClose}
          aria-label="Close target achieved popup"
        >
          ×
        </button>

        <div className="target-achieved-art-panel">
          <TargetAchievedTrophyEmblem />
        </div>

        <div className="target-achieved-copy">
          <h1>TARGET ACHIEVED</h1>
          <p>Outstanding performance!</p>

          <div className="target-achieved-person">
            <div className="target-achieved-avatar">
              {getInitials(popup.name)}
              <span />
            </div>
            <div>
              <strong>{popup.name}</strong>
              <small>You crushed the target!</small>
            </div>
          </div>
        </div>

        <div className="target-achieved-arr">
          <strong>{formatARR(popup.arr || 0)}</strong>
          <span>ARR</span>
        </div>

        <div className="target-achieved-badges">
          <span className="target-achieved-badge source">
            <i className="target-achieved-badge-icon target-achieved-source-icon" />
            {source}
          </span>
          <span className="target-achieved-badge team">
            <i className="target-achieved-badge-icon target-achieved-team-icon" />
            Team {popup.team || "Sales"}
          </span>
          <span className="target-achieved-badge period">
            <i className="target-achieved-badge-icon target-achieved-period-icon" />
            {period || "This Month"}
          </span>
        </div>
      </section>
    </div>
  );
}

function CelebrationPopup({ popup, period, onClose }) {
  const isTarget = popup.type === "target";

  if (isTarget) {
    return (
      <TargetAchievedPopup
        popup={popup}
        period={period}
        onClose={onClose}
      />
    );
  }

  const direction = getCelebrationDealType(popup);
  const title = "Big Deal Closed";

  return (
    <div className="celebration-overlay big-deal">
      <div className="celebration-backdrop-orb celebration-backdrop-orb-one" />
      <div className="celebration-backdrop-orb celebration-backdrop-orb-two" />

      <section className="celebration-card" aria-label={title}>
        <div className="celebration-timer" />
        <div className="celebration-horizon" aria-hidden="true" />
        <div className="celebration-particles" aria-hidden="true">
          {Array.from({ length: 16 }).map((_, index) => (
            <span key={index} />
          ))}
        </div>
        <button
          type="button"
          className="celebration-close"
          onClick={onClose}
          aria-label="Close celebration popup"
        >
          ×
        </button>

        <CelebrationRocketVisual />

        <div className="celebration-content">
          <h1>{title}</h1>
          <p>Another win for the team!</p>

          <div className="celebration-agent-row">
            <div className="celebration-avatar">
              {getInitials(popup.name)}
              <span className="celebration-status-dot" />
            </div>
            <div>
              <strong>{popup.name}</strong>
              <span>Deal successfully closed</span>
            </div>
          </div>
        </div>

        <div className="celebration-amount-panel">
          <strong>{formatARR(popup.arr || 0)}</strong>
          <small>ARR</small>
        </div>

        <div className="celebration-chips">
          <span className="celebration-chip primary">
            <span className="chip-icon chip-icon-direction" aria-hidden="true" />
            {direction}
          </span>
          <span className="celebration-chip team-chip">
            <span className="chip-icon chip-icon-team" aria-hidden="true" />
            Team {popup.team || "Sales"}
          </span>
          <span className="celebration-chip period-chip">
            <span className="chip-icon chip-icon-period" aria-hidden="true" />
            {period || "This Month"}
          </span>
        </div>
      </section>
    </div>
  );
}

function formatCompactNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatSeconds(seconds) {
  const totalSeconds = Number(seconds || 0);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }

  return `${remainingSeconds}s`;
}

function formatSyncTime(value) {
  if (!value) return "Not synced yet";

  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true
  }).format(new Date(value));
}

function normalizeLocalName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function csvEscape(value) {
  const text = String(value ?? "");

  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
}

export function buildCsvContent(headers, rows) {
  return [
    headers,
    ...rows
  ]
    .map((row) => row.map(csvEscape).join(","))
    .join("\n");
}

function downloadCsv(filename, csvContent) {
  const blob = new Blob([`\uFEFF${csvContent}`], {
    type: "text/csv;charset=utf-8;"
  });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function getMonthDateRange(month, year) {
  const monthNumber = Number(month || new Date().getMonth() + 1);
  const yearNumber = Number(year || new Date().getFullYear());
  const start = new Date(yearNumber, monthNumber - 1, 1);
  const end = new Date(yearNumber, monthNumber, 0);
  const toInput = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")}`;

  return {
    start: toInput(start),
    end: toInput(end),
    daysInMonth: end.getDate()
  };
}

function getRemainingDaysInMonth(month, year) {
  const monthNumber = Number(month || new Date().getMonth() + 1);
  const yearNumber = Number(year || new Date().getFullYear());
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const { daysInMonth } = getMonthDateRange(monthNumber, yearNumber);

  if (yearNumber < currentYear || (yearNumber === currentYear && monthNumber < currentMonth)) {
    return 0;
  }

  if (yearNumber > currentYear || monthNumber > currentMonth) {
    return daysInMonth;
  }

  return Math.max(daysInMonth - now.getDate() + 1, 1);
}

function normalizeLgsPayload(data) {
  const toNumber = (value) => Number.parseFloat(value) || 0;
  const summary = data?.summary || {};
  const topLgs = data?.topLGS || data?.topLgs || summary.topLgs;
  const rawLeaderboard = Array.isArray(data?.leaderboard)
    ? data.leaderboard
    : Array.isArray(data?.lgs)
      ? data.lgs
      : topLgs
        ? [topLgs]
        : [];
  const leaderboard = rawLeaderboard
    .map((item) => {
      const transferred = toNumber(
        item.transferred ?? item.leadsTransferred ?? item.leads_transfered
      );
      const converted = toNumber(
        item.converted ?? item.leadsConverted ?? item.leadConverted
      );

      return {
        name: item.name || item.lgsName || "Unknown",
        transferred,
        converted,
        conversionRate:
          item.conversionRate !== undefined
            ? toNumber(item.conversionRate)
            : transferred > 0
              ? Math.round((converted / transferred) * 10000) / 100
              : 0
      };
    })
    .sort((a, b) => {
      if (b.converted !== a.converted) return b.converted - a.converted;
      if (b.transferred !== a.transferred) return b.transferred - a.transferred;
      if (b.conversionRate !== a.conversionRate) {
        return b.conversionRate - a.conversionRate;
      }

      return a.name.localeCompare(b.name);
    });
  const totalTransferred =
    data?.totalTransferred ??
    summary.leadsTransferred ??
    leaderboard.reduce((sum, item) => sum + item.transferred, 0);
  const totalConverted =
    data?.totalConverted ??
    summary.leadsConverted ??
    leaderboard.reduce((sum, item) => sum + item.converted, 0);

  return {
    totalTransferred: toNumber(totalTransferred),
    totalConverted: toNumber(totalConverted),
    conversionRate: toNumber(data?.conversionRate ?? summary.conversionRate ?? 0),
    topLGS: leaderboard[0] || null,
    leaderboard
  };
}

function getZoomSourceLabel(source) {
  if (source === "member_availability") return "Live availability";
  if (source === "call_log") return "Call logs";
  if (source === "call_history") return "Zoom call history";
  if (source === "webhook_fallback") return "Live webhook fallback";
  return "Zoom";
}

function getStatusClass(status) {
  if (status === "On Call") return "status-on-call";
  if (status === "Break") return "status-break";
  if (status === "Meeting") return "status-meeting";
  if (status === "Logged Out") return "status-logged-out";
  return "status-available";
}

function formatPercentage(value) {
  const number = Number(value);

  if (value === null || value === undefined || Number.isNaN(number)) {
    return "N/A";
  }

  const prefix = number > 0 ? "+" : "";
  return `${prefix}${number.toFixed(1)}%`;
}

function getBreakLimit(agent, breakLimitMs = DEFAULT_BREAK_LIMIT) {
  return Number(agent.breakLimitMs || breakLimitMs || DEFAULT_BREAK_LIMIT);
}

function isBreakExceeded(agent, breakLimitMs = DEFAULT_BREAK_LIMIT) {
  const limit = getBreakLimit(agent, breakLimitMs);

  return getLiveDailyBreak(agent) > limit;
}

function getLiveDailyBreak(agent) {
  const today = getTodayDateInput();
  const savedBreak =
    agent.dailyBreakDate === today ? Number(agent.dailyBreakTime || 0) : 0;

  if (agent.status === "Break" && agent.statusStartTime) {
    const todayStartTime = getTodayStartTimestamp();
    const runningBreak =
      Date.now() - Math.max(Number(agent.statusStartTime), todayStartTime);
    return savedBreak + runningBreak;
  }

  return savedBreak;
}

function getRankIcon(rank) {
  if (rank === 1) return "🥇";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return `#${rank}`;
}
const tableHeaderStyle = {
  padding: "14px",
  color: "#fff",
  textAlign: "left"
};

const tableCellStyle = {
  padding: "14px",
  color: "#e2e8f0"
};
function getRankColor(rank) {
  if (rank === 1) return "#f59e0b";
  if (rank === 2) return "#94a3b8";
  if (rank === 3) return "#c2410c";
  return "#38bdf8";
}

const ARR_LEADERBOARD_TABS = {
  outbound: {
    label: "BDE Outbound",
    valueKey: "outboundARR",
    dataKey: "bdeOutbound"
  },
  inbound: {
    label: "BDE Inbound",
    valueKey: "inboundARR",
    dataKey: "bdeInbound"
  },
  srOutbound: {
    label: "Sr BDE Outbound",
    valueKey: "outboundARR",
    dataKey: "srBdeOutbound"
  },
  srInbound: {
    label: "Sr BDE Inbound",
    valueKey: "inboundARR",
    dataKey: "srBdeInbound"
  }
};

const EMPTY_ZOOM_TRENDS = {
  summary: {
    totalOutboundCalls: 0,
    totalInboundCalls: 0,
    totalCalls: 0,
    activeAgents: 0
  },
  daily: [],
  agents: []
};

const AGENTS_SNAPSHOT_CACHE_KEY = "sales-floor-dashboard:last-good-agents";

function readCachedAgentsSnapshot() {
  try {
    const cachedAgents = JSON.parse(
      localStorage.getItem(AGENTS_SNAPSHOT_CACHE_KEY) || "[]"
    );

    return Array.isArray(cachedAgents) ? cachedAgents : [];
  } catch {
    return [];
  }
}

function cacheAgentsSnapshot(nextAgents) {
  if (!Array.isArray(nextAgents) || nextAgents.length === 0) return;

  try {
    localStorage.setItem(
      AGENTS_SNAPSHOT_CACHE_KEY,
      JSON.stringify(nextAgents)
    );
  } catch {
    // TV mode should keep rendering even if browser storage is unavailable.
  }
}

function broadcastAgentStatusRefresh() {
  try {
    localStorage.setItem(AGENT_STATUS_EVENT_KEY, String(Date.now()));
  } catch {
    // Cross-tab refresh is best effort only.
  }

  window.dispatchEvent(new Event(AGENT_STATUS_EVENT_KEY));
}

export function getAdminPageAccess({ viewerMode = false, operationalManagerMode = false } = {}) {
  return {
    canUseAdminControls: !viewerMode && !operationalManagerMode,
    canReviewDsr: !viewerMode || operationalManagerMode,
    canViewOperationalReports: !viewerMode || operationalManagerMode
  };
}

function AdminPage({ viewerMode = false, operationalManagerMode = false, logout = null, onHome = null, initialOpenDsr = false } = {}) {

  const isReadOnlyViewer = Boolean(viewerMode);
  const { canUseAdminControls, canReviewDsr, canViewOperationalReports } = getAdminPageAccess({ viewerMode: isReadOnlyViewer, operationalManagerMode });

  const [agents, setAgents] = useState(readCachedAgentsSnapshot);

  const [arrData, setArrData] = useState({
    summary: {
      totalARR: 0,
      inboundARR: 0,
      outboundARR: 0,
      topOutboundPerformer: null,
      topInboundPerformer: null,
      topSrBdeOutboundPerformer: null,
      topSrBdeInboundPerformer: null
    },
    bdeOutbound: [],
    bdeInbound: [],
    srBdeOutbound: [],
    srBdeInbound: [],
    salesFeed: []
  });

  const [, setTick] = useState(0);
  const [showBreakSummary, setShowBreakSummary] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isTvMode, setIsTvMode] = useState(() => isReadOnlyViewer);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [showAgentManagement, setShowAgentManagement] = useState(false);
  const [showShiftManagement, setShowShiftManagement] = useState(false);
const [earlyShiftAgents, setEarlyShiftAgents] = useState([]);
  const [showArrLeaderboard, setShowArrLeaderboard] = useState(false);
  const [arrTab, setArrTab] = useState("outbound");
  const [leaderboardTeamFilter, setLeaderboardTeamFilter] = useState("all");
  const [leaderboardRoleFilter, setLeaderboardRoleFilter] = useState("all");
const [selectedMonth, setSelectedMonth] = useState(() => {
  return (
    localStorage.getItem("selectedMonth") ||
    String(new Date().getMonth() + 1).padStart(2, "0")
  );
});

const [selectedYear, setSelectedYear] = useState(() => {
  return (
    localStorage.getItem("selectedYear") ||
    String(new Date().getFullYear())
  );
});
const [ytdThroughMonth, setYtdThroughMonth] = useState(() => {
  const defaultPeriod = getDefaultYtdThroughPeriod();
  const savedMonth = localStorage.getItem("ytdThroughMonth");
  return MONTH_LABELS[savedMonth] ? savedMonth : defaultPeriod.month;
});
const [ytdThroughYear, setYtdThroughYear] = useState(() => {
  const defaultPeriod = getDefaultYtdThroughPeriod();
  return localStorage.getItem("ytdThroughYear") || defaultPeriod.year;
});
  const [arrLoading, setArrLoading] = useState(true);
const [arrError, setArrError] = useState(false);
const [celebrationPopup, setCelebrationPopup] = useState(null);
const [celebrationMuted] = useState(() => {
  return localStorage.getItem("celebrationSoundMuted") === "true";
});
const celebrationSoundRef = useRef(null);
const displayStatusClockRef = useRef(new Map());
const agentsVersionRef = useRef("");
const agentsVersionInFlightRef = useRef(false);
const [idleAgents, setIdleAgents] = useState([]);
const [showIdlePopup, setShowIdlePopup] = useState(false);
const [showHistoricalReports, setShowHistoricalReports] = useState(false);
const [selectedReportDate, setSelectedReportDate] = useState(getTodayDateInput);
const [selectedReportStartDate, setSelectedReportStartDate] = useState(getTodayDateInput);
const [selectedReportEndDate, setSelectedReportEndDate] = useState(getTodayDateInput);
const [historicalReports, setHistoricalReports] = useState([]);
const [reportsLoading, setReportsLoading] = useState(false);
const [showDsrReports, setShowDsrReports] = useState(initialOpenDsr);
const [dsrReports, setDsrReports] = useState([]);
const [dsrSummary, setDsrSummary] = useState({
  totalReports: 0,
  bdeReports: 0,
  lgsReports: 0,
  srLgsReports: 0,
  totalDials: 0,
  totalConnects: 0,
  totalNewLeads: 0,
  totalArrClosed: 0,
  totalDealsClosed: 0,
  totalCp: 0,
  totalArrFromCp: 0
});
const [dsrFilters, setDsrFilters] = useState({
  from: getTodayDateInput(),
  to: getTodayDateInput(),
  role: "all",
  team: "all",
  agent: ""
});
const [dsrLoading, setDsrLoading] = useState(false);
const [dsrError, setDsrError] = useState("");
const [missingDsrResult, setMissingDsrResult] = useState(null);
const [missingDsrLoading, setMissingDsrLoading] = useState(false);
const [missingDsrError, setMissingDsrError] = useState("");
const [dsrApprovals, setDsrApprovals] = useState([]);
const [dsrApprovalsLoading, setDsrApprovalsLoading] = useState(false);
const [dsrApprovalsError, setDsrApprovalsError] = useState("");
const [reviewingDsrApprovalId, setReviewingDsrApprovalId] = useState("");
const [expandedDsrReportId, setExpandedDsrReportId] = useState("");
const [deletingDsrReportId, setDeletingDsrReportId] = useState("");
const [savingDsrReviewId, setSavingDsrReviewId] = useState("");
const [salesAnalyticsTab, setSalesAnalyticsTab] = useState("arr");
const [opsCenterView, setOpsCenterView] = useState("actionCenter");
const [arrComparisonData, setArrComparisonData] = useState([]);
const [arrComparisonMetric, setArrComparisonMetric] = useState("total");
const [arrDrilldownMonth, setArrDrilldownMonth] = useState("");
const [arrLastSyncedAt, setArrLastSyncedAt] = useState(null);
const [, setArrComparisonLastSyncedAt] = useState(null);
const [agentSearch, setAgentSearch] = useState("");
const [activeFloorView, setActiveFloorView] = useState('all');
const [floorFocusRequest, setFloorFocusRequest] = useState(null);
useEffect(() => {
  if (!floorFocusRequest) return;
  const target = document.getElementById(`floor-${floorFocusRequest.view}`);
  target?.focus({ preventScroll: true });
  target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}, [floorFocusRequest]);
const focusFloorView = (view) => {
  setActiveFloorView(view);
  if (view === 'logged-out') setShowLoggedOutAgents(true);
  setFloorFocusRequest({ view });
};
const [showNotificationCenter, setShowNotificationCenter] = useState(false);
const [showLoggedOutAgents, setShowLoggedOutAgents] = useState(false);
const [breakSettings, setBreakSettings] = useState({
  breakLimitMs: Number(localStorage.getItem(BREAK_LIMIT_STORAGE_KEY) || 60) * 60000,
  breakLimitMinutes: Number(localStorage.getItem(BREAK_LIMIT_STORAGE_KEY) || 60),
  minBreakLimitMinutes: 5,
  maxBreakLimitMinutes: 240
});
const [breakLimitSaving, setBreakLimitSaving] = useState(false);
const [breakLimitFeedback, setBreakLimitFeedback] = useState("");
const [teamArrData, setTeamArrData] = useState({
  summary: {
    totalARR: 0,
    previousTotalARR: 0,
    growth: null,
    topTeam: null,
    teamCount: 0
  },
  teams: []
});
const [selectedTeamArrName, setSelectedTeamArrName] = useState("");
const [teamArrLoading, setTeamArrLoading] = useState(false);
const [teamArrError, setTeamArrError] = useState("");
const [, setTeamArrLastSyncedAt] = useState(null);
const [managerActionAgentId, setManagerActionAgentId] = useState("");
const [agentProfileId, setAgentProfileId] = useState("");
const [monthlyReportLoading, setMonthlyReportLoading] = useState(false);
const [monthlyReportError, setMonthlyReportError] = useState("");
const [monthlyBreakData, setMonthlyBreakData] = useState({
  summary: {
    agentCount: 0,
    breakCount: 0,
    exceededBreaks: 0,
    daysWithBreaks: 0
  },
  agents: [],
  sessions: []
});
const [breakReportMonth, setBreakReportMonth] = useState(() =>
  String(new Date().getMonth() + 1).padStart(2, "0")
);
const [breakReportYear, setBreakReportYear] = useState(() =>
  String(new Date().getFullYear())
);
const [breakReportAgent, setBreakReportAgent] = useState("");
const [selectedBreakReportAgent, setSelectedBreakReportAgent] = useState("");
const [breakReportLoading, setBreakReportLoading] = useState(false);
const [breakReportError, setBreakReportError] = useState("");
const [, setBreakReportLastSyncedAt] = useState(null);
const [zoomTrendData, setZoomTrendData] = useState({
  summary: {
    totalOutboundCalls: 0,
    totalInboundCalls: 0,
    totalCalls: 0,
    activeAgents: 0,
    days: 0,
    warning: "",
    stale: false,
    nextRetryAt: null
  },
  daily: [],
  agents: []
});
const [zoomTrendFrom, setZoomTrendFrom] = useState(getTodayDateInput);
const [zoomTrendTo, setZoomTrendTo] = useState(getTodayDateInput);
const [zoomTrendAgentId, setZoomTrendAgentId] = useState("");
const [zoomTrendLoading, setZoomTrendLoading] = useState(false);
const [zoomTrendError, setZoomTrendError] = useState("");
const [, setZoomTrendLastSyncedAt] = useState(null);
const [devicesBySource, setDevicesBySource] = useState({
  totalDevices: 0,
  topSource: null,
  sources: []
});
const [devicesMonth, setDevicesMonth] = useState(() =>
  String(new Date().getMonth() + 1).padStart(2, "0")
);
const [devicesYear, setDevicesYear] = useState(() =>
  String(new Date().getFullYear())
);
const [, setDevicesLastSyncedAt] = useState(null);
const [lgsMonth, setLgsMonth] = useState(() => {
  return (
    localStorage.getItem("lgsMonth") ||
    String(new Date().getMonth() + 1).padStart(2, "0")
  );
});

const [lgsYear, setLgsYear] = useState(() => {
  return (
    localStorage.getItem("lgsYear") ||
    String(new Date().getFullYear())
  );
});

const [lgsData, setLgsData] = useState({
  totalTransferred: 0,
  totalConverted: 0,
  conversionRate: 0,
  topLGS: null,
  leaderboard: []
});
const [, setLgsLastSyncedAt] = useState(null);
const [hubspotStatus, setHubspotStatus] = useState("follow_up");
const [hubspotOwnerId, setHubspotOwnerId] = useState("all");
const [hubspotLoading, setHubspotLoading] = useState(false);
const [hubspotError, setHubspotError] = useState("");
const [hubspotFollowups, setHubspotFollowups] = useState({
  summary: {
    total: 0,
    followUp: 0,
    pipeline: 0,
    inProcess: 0,
    topOwner: null
  },
  owners: [],
  leaderboard: [],
  statuses: [],
  statusProperty: null,
  truncated: false,
  countsExact: true
});
const [zoomDate, setZoomDate] = useState(getTodayDateInput);
const zoomAutoDateRef = useRef(getTodayDateInput());
const zoomCallSummaryInFlightRef = useRef(false);
const zoomAvailabilityInFlightRef = useRef(false);
const [zoomLoading, setZoomLoading] = useState(false);
const [zoomError, setZoomError] = useState("");
const [zoomLastSyncedAt, setZoomLastSyncedAt] = useState(null);
const [zoomAvailabilityLastSyncedAt, setZoomAvailabilityLastSyncedAt] =
  useState(null);
const [showZoomDiagnostics, setShowZoomDiagnostics] = useState(false);
const [zoomCallSummary, setZoomCallSummary] = useState({
  configured: false,
  summary: {
    date: getTodayDateInput(),
    siteName: "All Sites",
    totalOutboundCalls: 0,
    totalInboundCalls: 0,
    connectedCalls: 0,
    totalTalkSeconds: 0,
    recordsTotal: 0,
    recordsScanned: 0,
    recordsCounted: 0,
    source: "member_availability",
    pagesScanned: 0,
    warning: "",
    stale: false,
    nextRetryAt: null
  },
  agents: [],
  dashboardAgents: []
});
const [zoomAvailability, setZoomAvailability] = useState({
  configured: false,
  summary: {
    available: 0,
    onCall: 0,
    break: 0,
    unavailable: 0
  },
  agents: [],
  dashboardAgents: []
});
const [zoomAvailabilityError, setZoomAvailabilityError] = useState("");
const [adminStatusLoadingId, setAdminStatusLoadingId] = useState("");
const [adminStatusError, setAdminStatusError] = useState("");
const [adminStatusMessage, setAdminStatusMessage] = useState("");
const [showPasswordRequests, setShowPasswordRequests] = useState(false);
const [passwordResetRequests, setPasswordResetRequests] = useState([]);
const [passwordResetLoading, setPasswordResetLoading] = useState(false);
const [passwordResetError, setPasswordResetError] = useState("");
const [managedUsers, setManagedUsers] = useState([]);
const [userDrafts, setUserDrafts] = useState({});
const [roleAudit, setRoleAudit] = useState(null);
const [userManagementLoading, setUserManagementLoading] = useState(false);
const [userManagementError, setUserManagementError] = useState("");
const [userManagementMessage, setUserManagementMessage] = useState("");
const [deletingUser, setDeletingUser] = useState(null);
const [deleteUserBusy, setDeleteUserBusy] = useState(false);
const [deleteUserError, setDeleteUserError] = useState("");
const [newUserForm, setNewUserForm] = useState({
  name: "",
  password: "",
  role: "agent",
  dsrRole: "BDE",
  shiftType: "regular",
  disabled: false,
  zoomEmail: "",
  zoomExtension: "",
  team: "",
  roleReason: ""
});
const stopCelebrationSound = useCallback(() => {
  const sound = celebrationSoundRef.current;

  if (!sound) return;

  if (sound.stopTimer) {
    clearTimeout(sound.stopTimer);
  }

  if (sound.audioContext) {
    sound.audioContext.close().catch(() => {});
  }

  celebrationSoundRef.current = null;
}, []);

const playCelebrationSound = useCallback((type) => {
  if (celebrationMuted) return;

  const AudioContext =
    window.AudioContext || window.webkitAudioContext;

  if (!AudioContext) return;

  stopCelebrationSound();

  try {
    const audioContext = new AudioContext();
    const masterGain = audioContext.createGain();
    const now = audioContext.currentTime;
    const isTarget = type === "target";
    const pulseCount = isTarget ? 12 : 10;
    const baseFrequency = isTarget ? 210 : 165;

    masterGain.gain.setValueAtTime(0.0001, now);
    masterGain.gain.linearRampToValueAtTime(0.04, now + 0.08);
    masterGain.gain.exponentialRampToValueAtTime(
      0.0001,
      now + pulseCount * 0.32 + 0.8
    );
    masterGain.connect(audioContext.destination);

    if (audioContext.state === "suspended") {
      audioContext.resume().catch(() => {});
    }

    for (let index = 0; index < pulseCount; index += 1) {
      const start = now + index * 0.32;
      const duration = 0.18;
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const frequency =
        baseFrequency + (index % 4) * (isTarget ? 52 : 36);

      oscillator.type = isTarget ? "triangle" : "sawtooth";
      oscillator.frequency.setValueAtTime(frequency, start);
      oscillator.frequency.exponentialRampToValueAtTime(
        frequency * 1.22,
        start + duration
      );

      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.linearRampToValueAtTime(0.08, start + 0.035);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

      oscillator.connect(gain);
      gain.connect(masterGain);
      oscillator.start(start);
      oscillator.stop(start + duration + 0.05);
    }

    const stopTimer = setTimeout(() => {
      stopCelebrationSound();
    }, 6000);

    celebrationSoundRef.current = {
      audioContext,
      stopTimer
    };
  } catch (err) {
    console.log("Celebration sound blocked:", err);
  }
}, [celebrationMuted, stopCelebrationSound]);

const fetchBreakSettings = useCallback(async () => {
  try {
    const { data } = await apiRequest(
      `${API_URL}/break-settings?ts=${Date.now()}`,
      {
        headers: getAuthHeaders()
      },
      { retries: 2, timeoutMs: 10000 }
    );

    const breakLimitMs = Number(data.breakLimitMs || DEFAULT_BREAK_LIMIT);
    const breakLimitMinutes = Number(data.breakLimitMinutes || Math.round(breakLimitMs / 60000));

    setBreakSettings({
      breakLimitMs,
      breakLimitMinutes,
      teams: data.teams || [],
      minBreakLimitMinutes: Number(data.minBreakLimitMinutes || 5),
      maxBreakLimitMinutes: Number(data.maxBreakLimitMinutes || 240)
    });
    localStorage.setItem(BREAK_LIMIT_STORAGE_KEY, String(breakLimitMinutes));
  } catch (err) {
    if (err.status === 401 || err.status === 403) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.reload();
      return;
    }

    console.log("Break settings fetch error:", err);
    const cachedMinutes = Number(localStorage.getItem(BREAK_LIMIT_STORAGE_KEY) || 0);
    if (Number.isFinite(cachedMinutes) && cachedMinutes > 0) {
      setBreakSettings((current) => ({
        ...current,
        breakLimitMs: cachedMinutes * 60000,
        breakLimitMinutes: cachedMinutes
      }));
    }
  }
}, []);

const saveBreakLimit = async (payload) => {
  if (!canUseAdminControls) return;

  const minutes = Number(payload.breakLimitMinutes);
  const min = Number(breakSettings.minBreakLimitMinutes || 5);
  const max = Number(breakSettings.maxBreakLimitMinutes || 240);

  if (!Number.isFinite(minutes) || minutes < min || minutes > max) {
    setBreakLimitFeedback(`Enter a break limit from ${min} to ${max} minutes.`);
    throw new Error('Invalid break limit');
  }

  try {
    setBreakLimitSaving(true);
    setBreakLimitFeedback("");

    const { data } = await apiRequest(
      `${API_URL}/break-settings`,
      {
        method: "PUT",
        headers: getAuthHeaders({
          "Content-Type": "application/json"
        }),
        body: JSON.stringify(payload)
      },
      { retries: 2, timeoutMs: 12000 }
    );

    const breakLimitMs = Number(data.breakLimitMs || minutes * 60000);
    const breakLimitMinutes = Number(data.breakLimitMinutes || minutes);

    setBreakSettings({
      breakLimitMs,
      breakLimitMinutes,
      teams: data.teams || [],
      minBreakLimitMinutes: Number(data.minBreakLimitMinutes || min),
      maxBreakLimitMinutes: Number(data.maxBreakLimitMinutes || max)
    });
    localStorage.setItem(BREAK_LIMIT_STORAGE_KEY, String(breakLimitMinutes));
    setBreakLimitFeedback('Break settings saved.');
    await fetchAgents();
    if (salesAnalyticsTab === "breakReports") {
      fetchMonthlyBreakReport();
    }
  } catch (err) {
    console.log("Break limit save error:", err);
    setBreakLimitFeedback(formatApiError(err, "Failed to save break limit"));
    throw err;
  } finally {
    setBreakLimitSaving(false);
  }
};

const fetchAgents = useCallback(async () => {
  try {
    const { data: agentsData } = await apiRequest(
      `${API_URL}/agents?ts=${Date.now()}`,
      {
        headers: getAuthHeaders()
      },
      { retries: 2, timeoutMs: 12000 }
    );

    if (!Array.isArray(agentsData)) {
      console.log("Agents fetch returned an invalid payload");
      return;
    }

    cacheAgentsSnapshot(agentsData);

    setAgents((currentAgents) => {
      if (agentsData.length === 0 && currentAgents.length > 0) {
        return currentAgents;
      }

      return agentsData;
    });
  } catch (err) {
    if (err.status === 401 || err.status === 403) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.reload();
      return;
    }

    console.log("Agents fetch error:", err);
  }
}, []);

const fetchAgentsVersion = useCallback(async () => {
  if (agentsVersionInFlightRef.current) return;

  try {
    agentsVersionInFlightRef.current = true;
    const { data } = await apiRequest(
      `${API_URL}/agents-version?ts=${Date.now()}`,
      {
        headers: getAuthHeaders()
      },
      { retries: 1, timeoutMs: 6000 }
    );
    const nextVersion = String(data?.version || "");
    if (!nextVersion) return;

    if (!agentsVersionRef.current) {
      agentsVersionRef.current = nextVersion;
      return;
    }

    if (agentsVersionRef.current !== nextVersion) {
      agentsVersionRef.current = nextVersion;
      await Promise.all([fetchAgents(), fetchBreakSettings()]);
    }
  } catch (err) {
    if (err.status === 401 || err.status === 403) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.reload();
      return;
    }

    console.log("Agents version fetch error:", err);
  } finally {
    agentsVersionInFlightRef.current = false;
  }
}, [fetchAgents, fetchBreakSettings]);

const adminUpdateAgentStatus = useCallback(
  async (agent, status, breakType = "") => {
    if (!canUseAdminControls) return;
    if (!agent?.name) return;

    const loadingKey = `${agent._id || agent.name}-${status}-${breakType}`;

    try {
      setAdminStatusLoadingId(loadingKey);
      setAdminStatusError("");
      setAdminStatusMessage("");

      const { data } = await apiRequest(
        `${API_URL}/update-status`,
        {
          method: "POST",
          headers: getAuthHeaders({
            "Content-Type": "application/json"
          }),
          body: JSON.stringify({
            agentId: agent._id,
            name: agent.name,
            status,
            breakType
          })
        },
        { retries: 3, timeoutMs: 15000 }
      );

      if (Array.isArray(data.agents)) {
        cacheAgentsSnapshot(data.agents);
        setAgents(data.agents);
      } else {
        await fetchAgents();
      }

      broadcastAgentStatusRefresh();

      setAdminStatusMessage(
        status === "Break"
          ? `${agent.name} marked on ${breakType || "Break"}`
          : `${agent.name} marked ${status}`
      );
    } catch (err) {
      console.log("Admin status update error:", err);
      setAdminStatusError(formatApiError(err, "Failed to update agent status"));
    } finally {
      setAdminStatusLoadingId("");
    }
  },
  [canUseAdminControls, fetchAgents]
);

const fetchManagedUsers = useCallback(async () => {
  if (!canUseAdminControls) return;

  try {
    setUserManagementLoading(true);
    setUserManagementError("");

    const res = await fetch(`${API_URL}/users`, {
      headers: getAuthHeaders()
    });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to fetch users");
    }

    const users = Array.isArray(data) ? data : [];
    setManagedUsers(users);
    setUserDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };

      users.forEach((user) => {
        nextDrafts[user._id] = {
          name: nextDrafts[user._id]?.name ?? user.name ?? "",
          role: nextDrafts[user._id]?.role ?? user.role ?? "agent",
          roleVersion: user.roleVersion ?? 0,
          roleReason: nextDrafts[user._id]?.roleReason ?? "",
          dsrRole: nextDrafts[user._id]?.dsrRole ?? user.dsrRole ?? "BDE",
          password: nextDrafts[user._id]?.password ?? "",
          shiftType:
            nextDrafts[user._id]?.shiftType ?? user.shiftType ?? "regular",
          disabled:
            nextDrafts[user._id]?.disabled ?? Boolean(user.disabled),
          team: nextDrafts[user._id]?.team ?? user.team ?? "",
          zoomEmail: nextDrafts[user._id]?.zoomEmail ?? user.zoomEmail ?? "",
          zoomExtension:
            nextDrafts[user._id]?.zoomExtension ?? user.zoomExtension ?? ""
        };
      });

      return nextDrafts;
    });
  } catch (err) {
    console.log("Users fetch error:", err);
    setUserManagementError(err.message || "Failed to fetch users");
  } finally {
    setUserManagementLoading(false);
  }
}, [canUseAdminControls]);

const fetchRoleAudit = async (user) => {
  setRoleAudit({ userId: user._id, rows: [], loading: true, error: "" });
  try {
    const response = await fetch(`${API_URL}/users/${user._id}/role-audit`, { headers: getAuthHeaders() });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Role history is unavailable");
    setRoleAudit({ userId: user._id, rows: data.rows || [], loading: false, error: "" });
  } catch (error) {
    setRoleAudit({ userId: user._id, rows: [], loading: false, error: error.message || "Role history is unavailable" });
  }
};

const openUserManagement = () => {
  if (!canUseAdminControls) return;

  setShowAgentManagement(true);
  setUserManagementMessage("");
  fetchManagedUsers();
};

const updateUserDraft = (userId, field, value) => {
  setUserDrafts((currentDrafts) => ({
    ...currentDrafts,
    [userId]: {
      ...currentDrafts[userId],
      [field]: value
    }
  }));
};

const createManagedUser = async () => {
  if (!canUseAdminControls) return;
  if (["am", "qa", "sm"].includes(newUserForm.role) && !newUserForm.roleReason.trim()) {
    setUserManagementError("Enter a reason for assigning this staff role.");
    return;
  }

  try {
    setUserManagementLoading(true);
    setUserManagementError("");
    setUserManagementMessage("");

    const res = await fetch(`${API_URL}/register`, {
      method: "POST",
      headers: getAuthHeaders({
        "Content-Type": "application/json"
      }),
      body: JSON.stringify(newUserForm)
    });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to create user");
    }

    setNewUserForm({
      name: "",
      password: "",
      role: "agent",
      dsrRole: "BDE",
      shiftType: "regular",
      disabled: false,
      zoomEmail: "",
      zoomExtension: "",
      team: "",
      roleReason: ""
    });
    setUserManagementMessage("User created successfully");
    await fetchManagedUsers();
    await fetchAgents();
  } catch (err) {
    console.log("User create error:", err);
    setUserManagementError(err.message || "Failed to create user");
  } finally {
    setUserManagementLoading(false);
  }
};

const saveManagedUser = async (userId, overrideDraft = null) => {
  if (!canUseAdminControls) return;

  try {
    setUserManagementLoading(true);
    setUserManagementError("");
    setUserManagementMessage("");

    const draft = overrideDraft || userDrafts[userId] || {};
    const existing = managedUsers.find((entry) => entry._id === userId);
    const roleChanged = Boolean(existing && draft.role && draft.role !== existing.role);
    if (roleChanged && !String(draft.roleReason || "").trim()) {
      setUserManagementError("Enter a reason for changing this account's role.");
      return;
    }
    const removingAdmin = roleChanged && existing.role === "admin" && ["am", "qa", "sm"].includes(draft.role);
    if (removingAdmin && !window.confirm("This will remove Admin access. To retain Admin access and add Senior Manager KPI authority, keep the Admin role and grant Senior Manager in KPI Permissions. Continue with Admin removal?")) return;
    const payload = roleChanged ? { ...draft, roleVersion: existing.roleVersion ?? draft.roleVersion, confirmAdminRemoval: removingAdmin } : draft;
    const res = await fetch(`${API_URL}/users/${userId}`, {
      method: "PUT",
      headers: getAuthHeaders({
        "Content-Type": "application/json"
      }),
      body: JSON.stringify(payload)
    });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to update user");
    }

    setUserDrafts((currentDrafts) => ({
      ...currentDrafts,
      [userId]: {
        ...currentDrafts[userId],
        password: "",
        roleReason: ""
      }
    }));
    setUserManagementMessage("User updated successfully");
    await fetchManagedUsers();
    await fetchAgents();
  } catch (err) {
    console.log("User update error:", err);
    setUserManagementError(err.message || "Failed to update user");
  } finally {
    setUserManagementLoading(false);
  }
};

const deleteManagedUser = async () => {
  if (!canUseAdminControls || !deletingUser || deleteUserBusy) return;
  setDeleteUserBusy(true);
  setDeleteUserError("");
  try {
    const response = await fetch(`${API_URL}/users/${deletingUser._id}`, {
      method: "DELETE", headers: getAuthHeaders()
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || "Failed to delete user");
    setDeletingUser(null);
    setUserManagementMessage(data.message);
    await fetchManagedUsers();
    await fetchAgents();
  } catch (error) {
    setDeleteUserError(error.message || "Failed to delete user");
  } finally {
    setDeleteUserBusy(false);
  }
};

const fetchPasswordResetRequests = useCallback(async () => {
  if (!canUseAdminControls) {
    setPasswordResetRequests([]);
    setPasswordResetError("");
    return;
  }

  try {
    setPasswordResetError("");
    const res = await fetch(`${API_URL}/password-reset-requests`, {
      headers: getAuthHeaders()
    });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to fetch password requests");
    }

    setPasswordResetRequests(Array.isArray(data) ? data : []);
  } catch (err) {
    console.log("Password reset requests fetch error:", err);
    setPasswordResetError(err.message || "Failed to fetch password requests");
  }
}, [canUseAdminControls]);

const reviewPasswordResetRequest = useCallback(
  async (requestId, action) => {
    if (!canUseAdminControls) return;

    try {
      setPasswordResetLoading(true);
      setPasswordResetError("");

      const res = await fetch(
        `${API_URL}/password-reset-requests/${requestId}/${action}`,
        {
          method: "POST",
          headers: getAuthHeaders()
        }
      );
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to update password request");
      }

      await fetchPasswordResetRequests();
    } catch (err) {
      console.log("Password reset review error:", err);
      setPasswordResetError(err.message || "Failed to update password request");
    } finally {
      setPasswordResetLoading(false);
    }
  },
  [canUseAdminControls, fetchPasswordResetRequests]
);

const fetchArrData = useCallback(async (monthValue, yearValue) => {
  try {
    setArrLoading(true);
    setArrError(false);

    const params = new URLSearchParams();

    if (monthValue) {
      params.append("month", monthValue);
    }

    if (yearValue) {
      params.append("year", yearValue);
    }

    params.append("_t", Date.now());

    const arrUrl = `${API_URL}/arr-leaderboard?${params.toString()}`;

    const arrRes = await fetch(arrUrl, {
      cache: "no-store",
      headers: getAuthHeaders()
    });

    const arrJson = await arrRes.json();

    if (!arrRes.ok) {
      if (handleAuthExpired(arrRes)) return;
      throw new Error(arrJson?.message || "Failed to fetch ARR data");
    }

    if (arrJson?.celebrations?.length > 0) {
      const newCelebration = arrJson.celebrations.find((item) => {
        const popupKey = [
          "celebration",
          item.type,
          item.name,
          item.arr,
          item.target || "",
          item.dealType || "",
          item.outboundARR || 0,
          item.inboundARR || 0
        ].join("-");

        if (localStorage.getItem(popupKey)) {
          return false;
        }

        localStorage.setItem(popupKey, "shown");
        return true;
      });

      if (newCelebration) {
        setCelebrationPopup(newCelebration);

        setTimeout(() => {
          setCelebrationPopup(null);
        }, 60000);
      }
    }

    setArrData({
      summary: arrJson?.summary || {
        totalARR: 0,
        inboundARR: 0,
        outboundARR: 0,
        topOutboundPerformer: null,
        topInboundPerformer: null,
        topSrBdeOutboundPerformer: null,
        topSrBdeInboundPerformer: null
      },
      bdeOutbound: arrJson?.bdeOutbound || [],
      bdeInbound: arrJson?.bdeInbound || [],
      srBdeOutbound: arrJson?.srBdeOutbound || [],
      srBdeInbound: arrJson?.srBdeInbound || [],
      salesFeed: arrJson?.salesFeed || []
    });
    setArrLastSyncedAt(Date.now());
  } catch (err) {
    console.log("ARR fetch error:", err);
    setArrError(true);
  } finally {
    setArrLoading(false);
  }
}, []);

const handleMonthChange = (e) => {
  const newMonth = e.target.value;
  setSelectedMonth(newMonth);
};

const handleYearChange = (e) => {
  const newYear = e.target.value;
  setSelectedYear(newYear);
};

useEffect(() => {
  localStorage.setItem("selectedMonth", selectedMonth);
  localStorage.setItem("selectedYear", selectedYear);
}, [selectedMonth, selectedYear]);

useEffect(() => {
  localStorage.setItem("ytdThroughMonth", ytdThroughMonth);
  localStorage.setItem("ytdThroughYear", ytdThroughYear);
}, [ytdThroughMonth, ytdThroughYear]);


const downloadBreakReport = () => {
  const headers = ["Agent Name", "Current Status", "Total Break Today"];

  const rows = agents.map((agent) => [
    agent.name,
    agent.status,
    formatMilliseconds(getLiveDailyBreak(agent))
  ]);

  downloadCsv(
    `daily_break_report_${new Date().toISOString().split("T")[0]}.csv`,
    buildCsvContent(headers, rows)
  );
};

const getHistoricalBreakCsvContent = () => {
  const headers = [
    "Agent Name",
    "Report Date",
    "Shift Type",
    "Total Break Time",
    "Breaks",
    "Exceeded Breaks",
    "Status", "Team", "Allowed Break", "Exceeded By", "Limit Source"
  ];

  const rows = historicalReports.map((report) => [
    report.agentName,
    report.reportDate,
    report.shiftType,
    formatMilliseconds(report.totalBreakTime),
    report.breakCount || 0,
    report.exceededBreaks || 0,
    report.status, report.team || 'No Team', formatMilliseconds(report.breakLimitMs),
    report.dailyLimitExceeded ? formatMilliseconds(report.overageMs) : '', report.limitSource
  ]);

  return buildCsvContent(headers, rows);
};

const getHistoricalReportRange = () => {
  const start = selectedReportStartDate || selectedReportDate || getTodayDateInput();
  const end = selectedReportEndDate || start;
  return start > end ? { start: end, end: start } : { start, end };
};

const downloadHistoricalReport = () => {
  const { start, end } = getHistoricalReportRange();
  const rangeLabel = start === end ? start : `${start}_to_${end}`;
  downloadCsv(
    `historical_break_report_${rangeLabel || "all"}.csv`,
    getHistoricalBreakCsvContent()
  );
};

const openHistoricalReportInGoogleSheets = async () => {
  try {
    const csvContent = getHistoricalBreakCsvContent();
    const sheetWindow = window.open(
      "https://docs.google.com/spreadsheets/u/0/create",
      "_blank",
      "noopener,noreferrer"
    );

    await navigator.clipboard.writeText(csvContent);

    if (!sheetWindow) {
      throw new Error("Popup was blocked");
    }

    setAdminStatusError("");
    setAdminStatusMessage(
      "Historical break report copied. A blank Google Sheet opened; click A1 and paste."
    );
  } catch (err) {
    console.log("Google Sheets open helper error:", err);
    setAdminStatusError(
      "Could not copy report automatically. Download CSV and import it into Google Sheets."
    );
  }
};

const downloadMonthlyBreakReport = () => {
  const headers = [
    "Agent Name",
    "Date",
    "Break Number",
    "Break Type",
    "Start",
    "End",
    "Duration",
    "Exceeded", "Team", "Allowed Break", "Limit Source"
  ];
  const rows = monthlyBreakData.agents.flatMap((agent) =>
    (agent.days || []).flatMap((day) =>
      (day.sessions || []).map((session) => [
        agent.agentName,
        day.date,
        session.sequence,
        session.breakType,
        session.startLabel,
        session.endLabel,
        formatMilliseconds(session.durationMs),
        session.exceeded ? "Yes" : "No", session.team || 'No Team', formatMilliseconds(session.breakLimitMs), session.limitSource
      ])
    )
  );
  downloadCsv(
    `monthly_break_report_${breakReportYear}_${breakReportMonth}.csv`,
    buildCsvContent(headers, rows)
  );
};
const fetchHistoricalReports = async (startDate, endDate) => {
  try {
    setReportsLoading(true);

    const start = startDate || selectedReportStartDate || selectedReportDate || getTodayDateInput();
    const end = endDate || selectedReportEndDate || start;
    const range = start > end ? { start: end, end: start } : { start, end };
    const params = new URLSearchParams();
    if (range.start === range.end) {
      params.append("date", range.start);
    } else {
      params.append("startDate", range.start);
      params.append("endDate", range.end);
    }
    params.append("ts", String(Date.now()));

    const res = await fetch(
      `${API_URL}/daily-break-reports?${params.toString()}`,
      {
        cache: "no-store",
        headers: getAuthHeaders()
      }
    );

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to fetch historical reports");
    }

    setHistoricalReports(Array.isArray(data) ? data : []);
    setSelectedReportDate(range.start);
    setSelectedReportStartDate(range.start);
    setSelectedReportEndDate(range.end);
  } catch (err) {
    console.log(err);
    const message = err?.message === "Failed to fetch"
      ? "Could not reach the report API. Refresh once and try again; if it repeats, the Worker/CORS deploy is not being reached by this browser."
      : err.message || "Failed to fetch historical reports";
    alert(message);
  } finally {
    setReportsLoading(false);
  }
};
const fetchDsrReports = async (overrideFilters = dsrFilters) => {
  try {
    setDsrLoading(true);
    setDsrError("");

    const from = overrideFilters.from || getTodayDateInput();
    const to = overrideFilters.to || from;
    const params = new URLSearchParams({
      from,
      to,
      ts: String(Date.now())
    });

    if (overrideFilters.role && overrideFilters.role !== "all") {
      params.append("role", overrideFilters.role);
    }

    if (overrideFilters.team && overrideFilters.team !== "all") {
      params.append("team", overrideFilters.team);
    }

    if (overrideFilters.agent) {
      params.append("agent", overrideFilters.agent);
    }

    const res = await fetch(`${API_URL}/dsr-reports?${params.toString()}`, {
      cache: "no-store",
      headers: getAuthHeaders()
    });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to fetch DSR reports");
    }

    setDsrReports(Array.isArray(data.reports) ? data.reports : []);
    setDsrSummary(data.summary || {});
    setDsrFilters((current) => ({
      ...current,
      from: data.from || from,
      to: data.to || to
    }));
    fetchDsrApprovals({
      ...overrideFilters,
      from: data.from || from,
      to: data.to || to
    });
  } catch (err) {
    console.log("DSR report fetch error:", err);
    setDsrError(err.message || "Failed to fetch DSR reports");
  } finally {
    setDsrLoading(false);
  }
};

useEffect(() => {
  if (initialOpenDsr) fetchDsrReports();
  // This runs once when the Admin DSR workspace is opened from Home.
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

const fetchDsrApprovals = async (overrideFilters = dsrFilters) => {
  try {
    setDsrApprovalsLoading(true);
    setDsrApprovalsError("");

    const from = overrideFilters.from || getTodayDateInput();
    const to = overrideFilters.to || from;
    const params = new URLSearchParams({
      from,
      to,
      status: "all",
      ts: String(Date.now())
    });

    if (overrideFilters.role && overrideFilters.role !== "all") {
      params.append("role", overrideFilters.role);
    }

    if (overrideFilters.team && overrideFilters.team !== "all") {
      params.append("team", overrideFilters.team);
    }

    if (overrideFilters.agent) {
      params.append("agent", overrideFilters.agent);
    }

    const res = await fetch(`${API_URL}/dsr-approval-requests?${params.toString()}`, {
      cache: "no-store",
      headers: getAuthHeaders()
    });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to fetch pending DSR approvals");
    }

    setDsrApprovals(Array.isArray(data.requests) ? data.requests : []);
  } catch (err) {
    console.log("DSR approval fetch error:", err);
    setDsrApprovalsError(err.message || "Failed to fetch pending DSR approvals");
  } finally {
    setDsrApprovalsLoading(false);
  }
};

const reviewDsrApprovalRequest = async (requestId, action, note = "") => {
  if (!requestId || reviewingDsrApprovalId) return;

  try {
    setReviewingDsrApprovalId(requestId);
    setDsrApprovalsError("");

    const res = await fetch(`${API_URL}/dsr-approval-requests/${requestId}/${action}`, {
      method: "POST",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders()
      },
      body: JSON.stringify({ note })
    });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || `Failed to ${action} DSR approval`);
    }

    await Promise.all([
      fetchDsrApprovals(),
      fetchDsrReports(),
      missingDsrResult ? fetchMissingDsrAgents() : Promise.resolve()
    ]);
  } catch (err) {
    console.log("DSR approval review error:", err);
    setDsrApprovalsError(err.message || `Failed to ${action} DSR approval`);
  } finally {
    setReviewingDsrApprovalId("");
  }
};

const deleteDsrReport = async (report) => {
  if (!report?.id || deletingDsrReportId) return;

  const confirmed = window.confirm(
    `Delete ${report.agentName}'s DSR for ${report.businessDate}? They will be able to submit it again.`
  );
  if (!confirmed) return;

  try {
    setDeletingDsrReportId(report.id);
    setDsrError("");

    const res = await fetch(`${API_URL}/dsr-reports/${report.id}`, {
      method: "DELETE",
      cache: "no-store",
      headers: getAuthHeaders()
    });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to delete DSR report");
    }

    setExpandedDsrReportId((current) => (current === report.id ? "" : current));
    await Promise.all([
      fetchDsrReports(),
      fetchDsrApprovals(),
      missingDsrResult ? fetchMissingDsrAgents() : Promise.resolve()
    ]);
  } catch (err) {
    console.log("DSR delete error:", err);
    setDsrError(err.message || "Failed to delete DSR report");
  } finally {
    setDeletingDsrReportId("");
  }
};

const saveDsrManagerReview = async (report, reviewFields) => {
  if (!report?.id || savingDsrReviewId) return null;

  try {
    setSavingDsrReviewId(report.id);
    setDsrError("");

    const res = await fetch(`${API_URL}/dsr-reports/${report.id}/manager-review`, {
      method: "PUT",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders()
      },
      body: JSON.stringify(reviewFields || {})
    });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to save manager review");
    }

    const savedReview = data.review || null;
    setDsrReports((current) =>
      current.map((item) =>
        item.id === report.id
          ? {
              ...item,
              managerReview: savedReview,
              managerReviewStatus: "reviewed",
              managerReviewFollowUpDate: savedReview?.followUpDate || ""
            }
          : item
      )
    );

    return savedReview;
  } catch (err) {
    console.log("DSR manager review save error:", err);
    setDsrError(err.message || "Failed to save manager review");
    throw err;
  } finally {
    setSavingDsrReviewId("");
  }
};

const fetchMissingDsrAgents = async (override = {}) => {
  try {
    setMissingDsrLoading(true);
    setMissingDsrError("");

    const selected = { ...dsrFilters, ...override };
    const role = selected.role || "all";
    const team = selected.team || "all";
    const params = new URLSearchParams({
      from: selected.from,
      to: selected.to,
      ts: String(Date.now())
    });
    if (selected.agent) params.append("agent", selected.agent);

    if (role && role !== "all") {
      params.append("role", role);
    }

    if (team && team !== "all") {
      params.append("team", team);
    }

    const res = await fetch(`${API_URL}/dsr-missing?${params.toString()}`, {
      cache: "no-store",
      headers: getAuthHeaders()
    });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to fetch missing DSR agents");
    }

    setMissingDsrResult(data);
  } catch (err) {
    console.log("Missing DSR fetch error:", err);
    setMissingDsrError(err.message || "Failed to fetch missing DSR agents");
  } finally {
    setMissingDsrLoading(false);
  }
};

const downloadMissingDsrCsv = () => {
  const rows = (missingDsrResult?.missingAgents || []).map((agent) => [
    agent.businessDate,
    agent.agentName,
    agent.agentTeam || "",
    formatDsrRoleLabel(agent.dsrRole),
  ]);

  downloadCsv(
    `missing_dsr_${missingDsrResult?.from || dsrFilters.from}_${missingDsrResult?.to || dsrFilters.to}.csv`,
    buildCsvContent(["Business Date", "Agent", "Team", "DSR Role"], rows)
  );
};

const downloadDsrCsv = () => {
  const headers = [
    "Business Date",
    "Agent",
    "Team",
    "Role",
    "Dials / Total Calls",
    "Connects",
    "New Leads",
    "Lead Lost",
    "MTD Leads",
    "YTD Leads",
    "ARR Closed",
    "Deals Closed",
    "Deal Lost",
    "Total CP",
    "ARR from CP",
    "Submitted At",
    "Follow-ups Due",
    "Follow-ups Completed",
    "Cold Today",
    "Warm Today",
    "Hot Today",
    "Cold MTD",
    "Warm MTD",
    "Hot MTD",
    "Cold to Warm",
    "Warm to Hot",
    "Hot to Closed",
    "Opportunity Notes",
    "Proposals Sent",
    "Negotiations",
    "Demos Completed Today",
    "Demos Scheduled Future",
    "CP Prospects",
    "CP Meetings",
    "Total Calls",
    "Inbound Calls",
    "Call Back",
    "Live Transfer",
    "Long Followup",
    "LGS Follow-up Cold",
    "LGS Follow-up Warm",
    "LGS Follow-up Hot",
    "Transferred Leads Closed",
    "Transferred ARR",
    "SR. LGS Own Deals",
    "SR. LGS Own ARR",
    "Inbound Sales / Deals",
    "Inbound ARR",
    "WhatsApp",
    "Referral",
    "Other",
    "Conversation Summary",
    "Additional Notes",
    "Review Status",
    "Reviewed By",
    "Reviewer Team",
    "Reviewed At",
    "Low-Dial Approval Status",
    "Low-Dial Request Reason",
    "Low-Dial Decision Reason",
    "Low-Dial Reviewed By",
    "Low-Dial Reviewed At",
    "Key Achievement Today",
    "Biggest Opportunity",
    "Blocker",
    "Next Action",
    "Follow-up Date",
    "Planned Leaves",
    "UPL",
    "CP Assigned User IDs",
    "CP Assign To",
    "Connected Calls",
    "Notes",
    "Deal Lost Reason"
  ];
  const rows = dsrReports.map((report) => {
    const transferred = Array.isArray(report.fields?.transferredClosedLeads)
      ? report.fields.transferredClosedLeads
      : [];
    const transferredArr = transferred.reduce((sum, entry) => sum + Number(entry.arrClosed || 0), 0);
    const review = report.managerReview || {};
    const approval = report.approval || {};

    return [
      report.businessDate,
      report.agentName,
      report.agentTeam || "",
      formatDsrRoleLabel(report.dsrRole),
      report.dials,
      report.connects,
      report.newLeads,
      report.fields?.leadLost || 0,
      report.fields?.newLeadsMtd || 0,
      report.fields?.newLeadsYtd || 0,
      report.arrClosed,
      report.dealsClosed,
      report.fields?.dealLost || 0,
      report.fields?.totalCp || 0,
      report.fields?.arrFromCp || 0,
      report.submittedAt,
      report.fields?.followUpsDue || 0,
      report.fields?.followUpsCompleted || 0,
      report.fields?.coldLeadsToday || 0,
      report.fields?.warmLeadsToday || 0,
      report.fields?.hotLeadsToday || 0,
      report.fields?.coldLeadsMtd || 0,
      report.fields?.warmLeadsMtd || 0,
      report.fields?.hotLeadsMtd || 0,
      report.fields?.coldToWarm || 0,
      report.fields?.warmToHot || 0,
      report.fields?.hotToClosed || 0,
      report.fields?.opportunityNotes || "",
      report.fields?.proposalsSent || 0,
      report.fields?.negotiations || 0,
      report.fields?.demosCompletedToday || 0,
      report.fields?.demosScheduledFuture || 0,
      report.fields?.cpProspects || 0,
      report.fields?.cpMeetings || 0,
      report.fields?.outboundCalls || 0,
      report.fields?.inboundCalls || 0,
      report.fields?.callBacks || 0,
      report.fields?.liveTransfers || 0,
      report.fields?.longFollowups || 0,
      report.fields?.lgsFollowupCold || 0,
      report.fields?.lgsFollowupWarm || 0,
      report.fields?.lgsFollowupHot || 0,
      transferred.map((entry) => `${entry.leadCloserName || "Closer"}: ${entry.arrClosed || 0}`).join("; "),
      transferredArr,
      report.fields?.srLgsDealsClosed || 0,
      report.fields?.srLgsArrClosed || 0,
      report.fields?.inboundDealsClosed || 0,
      report.fields?.inboundArrClosed || 0,
      report.fields?.whatsApp || 0,
      report.fields?.referral || 0,
      report.fields?.other || 0,
      report.fields?.conversationSummary || "",
      report.fields?.additionalNotes || "",
      report.managerReviewStatus || "",
      review.updatedBy || review.createdBy || "",
      review.reviewerTeam || "",
      review.updatedAt || review.createdAt || "",
      approval.status || "",
      approval.requestReason || "",
      approval.reviewNote || "",
      approval.reviewedBy || "",
      approval.reviewedAt || "",
      review.keyAchievement || "",
      review.biggestOpportunity || "",
      review.blocker || "",
      review.nextAction || "",
      review.followUpDate || "",
      review.plannedLeaves || "",
      review.upl || "",
      report.dsrRole === "cpa" ? (report.fields?.cpAssignments || []).map((entry) => entry.assignedToId).join("; ") : "",
      report.dsrRole === "cpa" ? (report.fields?.cpAssignments || []).map((entry) => entry.assignedToName || entry.assignedToId).join("; ") : "",
      report.dsrRole === "lgs" ? report.fields?.connectedCalls ?? "" : "",
      ["lgs", "cpa"].includes(report.dsrRole) ? report.fields?.notes || "" : "",
      report.fields?.dealLostReason || ""
    ];
  });
  downloadCsv(
    `dsr_reports_${dsrFilters.from}_to_${dsrFilters.to}.csv`,
    buildCsvContent(headers, rows)
  );
};

const resetDsrFilters = () => {
  const today = getTodayDateInput();
  const nextFilters = {
    from: today,
    to: today,
    role: "all",
    team: "all",
    agent: ""
  };

  setDsrFilters(nextFilters);
  setExpandedDsrReportId("");
  fetchDsrReports(nextFilters);
};
const updateShiftType = async (agentId, shiftType) => {
  try {
    await fetch(`${API_URL}/update-shift`, {
      method: "POST",
      headers: getAuthHeaders({
        "Content-Type": "application/json"
      }),
      body: JSON.stringify({
        agentId,
        shiftType
      })
    });

    fetchAgents();
  } catch (err) {
    console.log(err);
    alert("Failed to update shift");
  }
};

const fetchArrComparisonData = useCallback(async () => {
  try {
    const res = await fetch(`${API_URL}/arr-year-comparison`, {
      headers: getAuthHeaders()
    });

    const data = await res.json();

    setArrComparisonData(Array.isArray(data) ? data : []);
    setArrComparisonLastSyncedAt(Date.now());
  } catch (err) {
    console.log(err);
    setArrComparisonData([]);
  }
}, []);

const fetchDevicesBySource = useCallback(async () => {
  try {
    const res = await fetch(
      `${API_URL}/devices-by-source?month=${devicesMonth}&year=${devicesYear}`,
      {
        headers: getAuthHeaders()
      }
    );

    const data = await res.json();

    setDevicesBySource(data);
    setDevicesLastSyncedAt(Date.now());
  } catch (err) {
    console.log(err);
  }
}, [devicesMonth, devicesYear]);

const fetchLgsConversion = useCallback(async () => {
  try {
    const res = await fetch(
      `${API_URL}/lgs-conversion?month=${lgsMonth}&year=${lgsYear}`,
      {
        headers: getAuthHeaders()
      }
    );

    const data = await res.json();
    setLgsData(normalizeLgsPayload(data));
    setLgsLastSyncedAt(Date.now());
  } catch (err) {
    console.log("LGS conversion fetch error:", err);
    setLgsData({
      totalTransferred: 0,
      totalConverted: 0,
      conversionRate: 0,
      topLGS: null,
      leaderboard: []
    });
  }
}, [lgsMonth, lgsYear]);

const fetchHubspotFollowups = useCallback(async () => {
  try {
    setHubspotLoading(true);
    setHubspotError("");

    const params = new URLSearchParams({
      status: hubspotStatus,
      ownerId: hubspotOwnerId
    });
    const res = await fetch(`${API_URL}/hubspot/followups?${params.toString()}`, {
      headers: getAuthHeaders()
    });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(
        data.details?.message ||
          data.message ||
          "Failed to fetch HubSpot lead status"
      );
    }

    setHubspotFollowups({
      summary: {
        total: data.summary?.total || 0,
        followUp: data.summary?.followUp || 0,
        pipeline: data.summary?.pipeline || 0,
        inProcess: data.summary?.inProcess || 0,
        topOwner: data.summary?.topOwner || null
      },
      owners: Array.isArray(data.owners) ? data.owners : [],
      leaderboard: Array.isArray(data.leaderboard) ? data.leaderboard : [],
      statuses: Array.isArray(data.statuses) ? data.statuses : [],
      statusProperty: data.statusProperty || null,
      truncated: Boolean(data.truncated),
      countsExact: data.countsExact !== false
    });
  } catch (err) {
    console.log("HubSpot follow-ups fetch error:", err);
    const rawMessage = err.message || "Failed to fetch HubSpot lead status";
    const friendlyMessage =
      rawMessage.toLowerCase().includes("secondly") ||
      rawMessage.toLowerCase().includes("rate limit") ||
      rawMessage.includes("429")
        ? "HubSpot is temporarily rate limiting requests. Please wait a few seconds and refresh."
        : rawMessage;

    setHubspotError(friendlyMessage);
    setHubspotFollowups((current) => ({
      ...current,
      summary: {
        total: 0,
        followUp: 0,
        pipeline: 0,
        inProcess: 0,
        topOwner: null
      },
      leaderboard: [],
      statuses: [],
      countsExact: true
    }));
  } finally {
    setHubspotLoading(false);
  }
}, [hubspotOwnerId, hubspotStatus]);

const fetchZoomCallSummary = useCallback(async (forceRefresh = false) => {
  if (zoomCallSummaryInFlightRef.current) return;

  try {
    zoomCallSummaryInFlightRef.current = true;
    setZoomLoading(true);
    setZoomError("");

    const params = new URLSearchParams({
      date: zoomDate
    });

    if (forceRefresh === true) {
      params.append("refresh", "1");
    }

    const res = await fetch(`${API_URL}/zoom/call-summary?${params.toString()}`, {
      cache: "no-store",
      headers: getAuthHeaders()
    });
    const data = await res.json();

    if (!res.ok) {
      const message = data.message || "Failed to fetch Zoom call summary";

      if (res.status === 429 || /rate limit/i.test(message)) {
        setZoomCallSummary((current) => ({
          ...current,
          summary: {
            ...current.summary,
            warning:
              "Zoom is rate limiting call-history refresh. Showing the last synced dial count.",
            stale: true,
            nextRetryAt: data.retryAfter || current.summary.nextRetryAt || null
          }
        }));
        setZoomError("");
        return;
      }

      throw new Error(data.message || "Failed to fetch Zoom call summary");
    }

    setZoomCallSummary({
      configured: Boolean(data.configured),
      summary: {
        date: data.summary?.date || zoomDate,
        siteName: data.summary?.siteName || "All Sites",
        totalOutboundCalls: data.summary?.totalOutboundCalls || 0,
        totalInboundCalls: data.summary?.totalInboundCalls || 0,
        connectedCalls: data.summary?.connectedCalls || 0,
        totalTalkSeconds: data.summary?.totalTalkSeconds || 0,
        recordsTotal: data.summary?.recordsTotal || 0,
        recordsScanned: data.summary?.recordsScanned || 0,
        recordsCounted: data.summary?.recordsCounted || 0,
        source: data.summary?.source || "member_availability",
        pagesScanned: data.summary?.pagesScanned || 0,
        warning: data.summary?.warning || "",
        stale: Boolean(data.summary?.stale),
        nextRetryAt: data.summary?.nextRetryAt || null
      },
      agents: Array.isArray(data.agents) ? data.agents : [],
      dashboardAgents: Array.isArray(data.dashboardAgents)
        ? data.dashboardAgents
        : []
    });
    setZoomLastSyncedAt(Date.now());
  } catch (err) {
    console.log("Zoom call summary fetch error:", err);
    const message = err.message || "Failed to fetch Zoom call summary";
    setZoomError(
      /fetch failed/i.test(message)
        ? "Zoom API fetch failed. Please retry after a few seconds."
        : message
    );
  } finally {
    zoomCallSummaryInFlightRef.current = false;
    setZoomLoading(false);
  }
}, [zoomDate]);

const fetchZoomAvailability = useCallback(async () => {
  if (zoomAvailabilityInFlightRef.current) return;

  try {
    zoomAvailabilityInFlightRef.current = true;
    setZoomAvailabilityError("");

    const params = new URLSearchParams({
      _t: Date.now()
    });

    const res = await fetch(`${API_URL}/zoom/member-availability?${params.toString()}`, {
      cache: "no-store",
      headers: getAuthHeaders()
    });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to fetch Zoom availability");
    }

    setZoomAvailability({
      configured: Boolean(data.configured),
      summary: {
        available: data.summary?.available || 0,
        onCall: data.summary?.onCall || 0,
        break: data.summary?.break || 0,
        unavailable: data.summary?.unavailable || 0
      },
      agents: Array.isArray(data.agents) ? data.agents : [],
      dashboardAgents: Array.isArray(data.dashboardAgents)
        ? data.dashboardAgents
        : []
    });
    setZoomAvailabilityLastSyncedAt(Date.now());
  } catch (err) {
    console.log("Zoom availability fetch error:", err);
    const message = err.message || "Failed to fetch Zoom availability";
    setZoomAvailabilityError(
      /fetch failed/i.test(message)
        ? "Zoom live status fetch failed. Please retry after a few seconds."
        : message
    );
  } finally {
    zoomAvailabilityInFlightRef.current = false;
  }
}, []);

const fetchTeamArrData = useCallback(async (monthValue, yearValue) => {
  try {
    setTeamArrLoading(true);
    setTeamArrError("");

    const params = new URLSearchParams();

    if (monthValue) {
      params.append("month", monthValue);
    }

    if (yearValue) {
      params.append("year", yearValue);
    }

    params.append("_t", Date.now());

    const res = await fetch(`${API_URL}/arr-team-analytics?${params.toString()}`, {
      cache: "no-store",
      headers: getAuthHeaders()
    });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to fetch team ARR");
    }

    const teams = Array.isArray(data.teams) ? data.teams : [];

    setTeamArrData({
      summary: data.summary || {
        totalARR: 0,
        previousTotalARR: 0,
        growth: null,
        topTeam: null,
        teamCount: 0
      },
      teams
    });
    setTeamArrLastSyncedAt(Date.now());
    setSelectedTeamArrName((currentTeamName) => {
      if (teams.some((team) => team.team === currentTeamName)) {
        return currentTeamName;
      }

      return teams[0]?.team || "";
    });
  } catch (err) {
    console.log("Team ARR fetch error:", err);
    setTeamArrError(err.message || "Failed to fetch team ARR");
    setTeamArrData({
      summary: {
        totalARR: 0,
        previousTotalARR: 0,
        growth: null,
        topTeam: null,
        teamCount: 0
      },
      teams: []
    });
    setSelectedTeamArrName("");
  } finally {
    setTeamArrLoading(false);
  }
}, []);

const fetchMonthlyBreakReport = useCallback(async () => {
  try {
    setBreakReportLoading(true);
    setBreakReportError("");

    const params = new URLSearchParams({
      month: breakReportMonth,
      year: breakReportYear
    });

    if (breakReportAgent) {
      params.append("agent", breakReportAgent);
    }

    const res = await fetch(`${API_URL}/break-monthly-reports?${params.toString()}`, {
      headers: getAuthHeaders()
    });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to fetch monthly break report");
    }

    const nextAgents = Array.isArray(data.agents) ? data.agents : [];

    setMonthlyBreakData({
      summary: data.summary || {
        agentCount: 0,
        breakCount: 0,
        exceededBreaks: 0,
        daysWithBreaks: 0
      },
      agents: nextAgents,
      sessions: Array.isArray(data.sessions) ? data.sessions : []
    });
    setBreakReportLastSyncedAt(Date.now());
    setSelectedBreakReportAgent((current) =>
      nextAgents.some((agent) => agent.agentName === current) ? current : ""
    );
  } catch (err) {
    console.log("Monthly break report fetch error:", err);
    setBreakReportError(err.message || "Failed to fetch monthly break report");
  } finally {
    setBreakReportLoading(false);
  }
}, [breakReportMonth, breakReportYear, breakReportAgent]);

const fetchZoomTrends = useCallback(async () => {
  try {
    setZoomTrendLoading(true);
    setZoomTrendError("");

    const params = new URLSearchParams({
      from: zoomTrendFrom,
      to: zoomTrendTo
    });

    if (zoomTrendAgentId) {
      params.append("agentId", zoomTrendAgentId);
    }

    const res = await fetch(`${API_URL}/zoom/trends?${params.toString()}`, {
      headers: getAuthHeaders()
    });
    const data = await res.json();

    if (!res.ok) {
      const message = data.message || "Failed to fetch Zoom trends";

      if (res.status === 429 || /rate limit/i.test(message)) {
        setZoomTrendData((current) => ({
          ...current,
          summary: {
            ...current.summary,
            warning:
              "Zoom is rate limiting call-history refresh. Showing the last synced trend data.",
            stale: true,
            nextRetryAt: data.retryAfter || current.summary.nextRetryAt || null
          }
        }));
        setZoomTrendError("");
        return;
      }

      throw new Error(data.message || "Failed to fetch Zoom trends");
    }

    setZoomTrendData({
      summary: data.summary || {
        totalOutboundCalls: 0,
        totalInboundCalls: 0,
        totalCalls: 0,
        activeAgents: 0,
        days: 0,
        warning: "",
        stale: false,
        nextRetryAt: null
      },
      daily: Array.isArray(data.daily) ? data.daily : [],
      agents: Array.isArray(data.agents) ? data.agents : []
    });
    setZoomTrendLastSyncedAt(Date.now());
  } catch (err) {
    console.log("Zoom trends fetch error:", err);
    setZoomTrendError(err.message || "Failed to fetch Zoom trends");
  } finally {
    setZoomTrendLoading(false);
  }
}, [zoomTrendFrom, zoomTrendTo, zoomTrendAgentId]);

const loadMonthlyPerformanceData = async () => {
  const reportMonth =
    selectedMonth || String(new Date().getMonth() + 1).padStart(2, "0");
  const reportYear = selectedYear || String(new Date().getFullYear());
  const reportRange = getMonthDateRange(reportMonth, reportYear);
  const query = `month=${reportMonth}&year=${reportYear}`;
  const fetchJson = async (path) => {
    const res = await fetch(`${API_URL}${path}`, {
      cache: "no-store",
      headers: getAuthHeaders()
    });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || `Failed to fetch ${path}`);
    }

    return data;
  };

  const [
    arrJson,
    teamJson,
    breakJson,
    devicesJson,
    lgsJson
  ] = await Promise.all([
    fetchJson(`/arr-leaderboard?${query}&_t=${Date.now()}`),
    fetchJson(`/arr-team-analytics?${query}&_t=${Date.now()}`),
    fetchJson(`/break-monthly-reports?${query}`),
    fetchJson(`/devices-by-source?${query}`),
    fetchJson(`/lgs-conversion?${query}`)
  ]);

  return {
    reportMonth,
    reportYear,
    reportRange,
    arrJson,
    teamJson,
    breakJson,
    zoomJson: EMPTY_ZOOM_TRENDS,
    devicesJson,
    lgsJson
  };
};

const applyMonthlyPerformanceData = (data) => {
  const now = Date.now();
  const lgsPayload = normalizeLgsPayload(data.lgsJson);
  const teams = Array.isArray(data.teamJson.teams) ? data.teamJson.teams : [];

  setArrData({
    summary: data.arrJson?.summary || {
      totalARR: 0,
      inboundARR: 0,
      outboundARR: 0,
      topOutboundPerformer: null,
      topInboundPerformer: null,
      topSrBdeOutboundPerformer: null,
      topSrBdeInboundPerformer: null
    },
    bdeOutbound: data.arrJson?.bdeOutbound || [],
    bdeInbound: data.arrJson?.bdeInbound || [],
    srBdeOutbound: data.arrJson?.srBdeOutbound || [],
    srBdeInbound: data.arrJson?.srBdeInbound || [],
    salesFeed: data.arrJson?.salesFeed || []
  });
  setTeamArrData({
    summary: data.teamJson.summary || {
      totalARR: 0,
      previousTotalARR: 0,
      growth: null,
      topTeam: null,
      teamCount: 0
    },
    teams
  });
  setSelectedTeamArrName((currentTeamName) => {
    if (teams.some((team) => team.team === currentTeamName)) {
      return currentTeamName;
    }

    return teams[0]?.team || "";
  });
  setMonthlyBreakData({
    summary: data.breakJson.summary || {
      agentCount: 0,
      breakCount: 0,
      exceededBreaks: 0,
      daysWithBreaks: 0
    },
    agents: Array.isArray(data.breakJson.agents) ? data.breakJson.agents : [],
    sessions: Array.isArray(data.breakJson.sessions) ? data.breakJson.sessions : []
  });
  setZoomTrendData({
    summary: data.zoomJson.summary || {
      totalOutboundCalls: 0,
      totalInboundCalls: 0,
      totalCalls: 0,
      activeAgents: 0,
      days: 0
    },
    daily: Array.isArray(data.zoomJson.daily) ? data.zoomJson.daily : [],
    agents: Array.isArray(data.zoomJson.agents) ? data.zoomJson.agents : []
  });
  setDevicesBySource(data.devicesJson || {
    totalDevices: 0,
    topSource: null,
    sources: []
  });
  setLgsData(lgsPayload);
  setBreakReportMonth(data.reportMonth);
  setBreakReportYear(data.reportYear);
  setDevicesMonth(data.reportMonth);
  setDevicesYear(data.reportYear);
  setLgsMonth(data.reportMonth);
  setLgsYear(data.reportYear);
  setZoomTrendFrom(data.reportRange.start);
  setZoomTrendTo(data.reportRange.end);
  setArrLastSyncedAt(now);
  setTeamArrLastSyncedAt(now);
  setBreakReportLastSyncedAt(now);
  setZoomTrendLastSyncedAt(now);
  setDevicesLastSyncedAt(now);
  setLgsLastSyncedAt(now);
};

const refreshMonthlyPerformancePreview = async () => {
  try {
    setMonthlyReportLoading(true);
    setMonthlyReportError("");

    const data = await loadMonthlyPerformanceData();

    applyMonthlyPerformanceData(data);
  } catch (err) {
    console.log("Monthly performance refresh error:", err);
    setMonthlyReportError(err.message || "Failed to refresh monthly report");
  } finally {
    setMonthlyReportLoading(false);
  }
};

const downloadMonthlyPerformanceReport = async () => {
  try {
    setMonthlyReportLoading(true);
    setMonthlyReportError("");

    const data = await loadMonthlyPerformanceData();
    const lgsPayload = normalizeLgsPayload(data.lgsJson);
    const teams = Array.isArray(data.teamJson.teams) ? data.teamJson.teams : [];
    const breakAgents = Array.isArray(data.breakJson.agents)
      ? data.breakJson.agents
      : [];
    const zoomAgents = Array.isArray(data.zoomJson.agents)
      ? data.zoomJson.agents
      : [];
    const deviceSources = Array.isArray(data.devicesJson.sources)
      ? data.devicesJson.sources
      : [];
    const targetRows = teams.flatMap((team) =>
      (Array.isArray(team.agents) ? team.agents : [])
        .filter((agent) => Number(agent.target || 0) > 0)
        .map((agent) => ({
          team: team.team,
          name: agent.name,
          outboundARR: Number(agent.outboundARR || 0),
          target: Number(agent.target || 0),
          progress: Number(agent.targetProgress || 0),
          remaining: Math.max(
            Number(agent.target || 0) - Number(agent.outboundARR || 0),
            0
          )
        }))
    );
    const rows = [
      ["Section", "Name", "Metric", "Value", "Extra"],
      [
        "Summary",
        `${getMonthLabel(data.reportMonth)} ${data.reportYear}`,
        "Total ARR",
        formatARR(data.arrJson.summary?.totalARR || 0),
        ""
      ],
      [
        "Summary",
        `${getMonthLabel(data.reportMonth)} ${data.reportYear}`,
        "Outbound ARR",
        formatARR(data.arrJson.summary?.outboundARR || 0),
        ""
      ],
      [
        "Summary",
        `${getMonthLabel(data.reportMonth)} ${data.reportYear}`,
        "Inbound ARR",
        formatARR(data.arrJson.summary?.inboundARR || 0),
        ""
      ],
      [
        "Summary",
        "Zoom",
        "Outbound calls",
        data.zoomJson.summary?.totalOutboundCalls || 0,
        `${data.zoomJson.summary?.activeAgents || 0} active agents`
      ],
      [
        "Summary",
        "Breaks",
        "Exceeded",
        data.breakJson.summary?.exceededBreaks || 0,
        `${data.breakJson.summary?.breakCount || 0} total breaks`
      ],
      [
        "Summary",
        "LGS",
        "Conversion",
        `${lgsPayload.conversionRate}%`,
        `${lgsPayload.totalConverted}/${lgsPayload.totalTransferred}`
      ],
      [],
      ["Team ARR", "Team", "Outbound ARR", "Inbound ARR", "Target Progress"],
      ...teams.map((team) => [
        "Team ARR",
        team.team,
        formatARR(team.outboundARR),
        formatARR(team.inboundARR),
        team.targetProgress == null ? "N/A" : formatPercentage(team.targetProgress)
      ]),
      [],
      ["Target Pace", "Agent", "Outbound ARR", "Target", "Remaining"],
      ...targetRows.map((agent) => [
        "Target Pace",
        agent.name,
        formatARR(agent.outboundARR),
        formatARR(agent.target),
        formatARR(agent.remaining)
      ]),
      [],
      ["Break Report", "Agent", "Breaks", "Exceeded", "Total Break Time"],
      ...breakAgents.map((agent) => [
        "Break Report",
        agent.agentName,
        agent.breakCount || 0,
        agent.exceededBreaks || 0,
        formatMilliseconds(agent.totalBreakTime)
      ]),
      [],
      ["Zoom", "Agent", "Outbound", "Inbound", "Total Calls"],
      ...zoomAgents.map((agent) => [
        "Zoom",
        agent.name,
        agent.outboundCalls || 0,
        agent.inboundCalls || 0,
        agent.totalCalls || 0
      ]),
      [],
      ["Devices", "Source", "Devices", "Share", ""],
      ...deviceSources.map((source) => [
        "Devices",
        source.source,
        source.count,
        data.devicesJson.totalDevices > 0
          ? formatPercentage((source.count / data.devicesJson.totalDevices) * 100)
          : "0%",
        ""
      ]),
      [],
      ["LGS", "Name", "Transferred", "Converted", "Conversion Rate"],
      ...lgsPayload.leaderboard.map((lgs) => [
        "LGS",
        lgs.name,
        lgs.transferred,
        lgs.converted,
        `${lgs.conversionRate}%`
      ])
    ];
    const csvContent = rows
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;"
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    applyMonthlyPerformanceData(data);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `monthly_performance_${data.reportYear}_${data.reportMonth}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (err) {
    console.log("Monthly performance report error:", err);
    setMonthlyReportError(err.message || "Failed to download monthly report");
  } finally {
    setMonthlyReportLoading(false);
  }
};

const downloadDailySnapshotReport = () => {
  const today = getTodayDateInput();
  const rows = [
    ["Section", "Metric", "Value", "Extra"],
    ["Live", "Available", availableAgents.length, ""],
    ["Live", "On Call", onCallAgents.length, ""],
    ["Live", "Meeting", meetingAgents.length, ""],
    ["Live", "On Break", breakAgents.length, ""],
    ["Live", "Logged Out", loggedOutAgents.length, ""],
    ["Live", "Break Exceeded", allBreakExceededAgents.length, ""],
    ["ARR", "Total ARR", formatARR(totalARR), selectedArrPeriod],
    ["ARR", "Outbound ARR", formatARR(outboundARR), selectedArrPeriod],
    ["ARR", "Inbound ARR", formatARR(inboundARR), selectedArrPeriod],
    [
      "ARR",
      "Top Outbound",
      arrData.summary.topOutboundPerformer?.name || "N/A",
      arrData.summary.topOutboundPerformer?.outboundARR
        ? formatARR(arrData.summary.topOutboundPerformer.outboundARR)
        : ""
    ],
    [
      "Zoom",
      "Outbound Calls",
      zoomCallSummary.summary.totalOutboundCalls || 0,
      zoomDate
    ],
    [
      "Zoom",
      "Live On Call",
      zoomAvailability.summary.onCall || onCallAgents.length,
      formatSyncTime(zoomAvailabilityLastSyncedAt)
    ],
    [
      "Targets",
      "Hit",
      targetPaceSummary.hit,
      `${targetPaceSummary.needsPush} need push`
    ],
    [
      "LGS",
      "Conversion",
      `${lgsData.conversionRate || 0}%`,
      `${lgsData.totalConverted || 0}/${lgsData.totalTransferred || 0}`
    ],
    [
      "Devices",
      "Top Source",
      devicesBySource.topSource?.source || "N/A",
      devicesBySource.topSource?.count || 0
    ],
    [],
    ["Latest Sales", "Agent", "ARR", "Source"],
    ...(arrData.salesFeed || []).slice(0, 8).map((sale) => [
      sale.date || "",
      sale.representative,
      formatARR(sale.totalARR),
      sale.source || sale.team || ""
    ])
  ];

  downloadCsv(`daily_snapshot_${today}.csv`, buildCsvContent(rows[0], rows.slice(1)));
};

useEffect(() => {
  const earlyAgents = agents.filter(
    (agent) => agent.shiftType === "early"
  );

  setEarlyShiftAgents(earlyAgents);
}, [agents]);

useEffect(() => {
  fetchAgents();
  fetchArrComparisonData();
  fetchPasswordResetRequests();

  const agentsInterval = setInterval(() => {
    runWhenVisible(fetchAgents);
  }, AGENTS_REFRESH_MS);

  return () => {
    clearInterval(agentsInterval);
  };
}, [fetchAgents, fetchArrComparisonData, fetchPasswordResetRequests]);

useEffect(() => {
  fetchAgentsVersion();
  const versionInterval = setInterval(() => {
    runWhenVisible(fetchAgentsVersion);
  }, AGENTS_VERSION_REFRESH_MS);

  return () => {
    clearInterval(versionInterval);
  };
}, [fetchAgentsVersion]);

useEffect(() => {
  const refreshAgentsNow = () => runWhenVisible(fetchAgents);
  const handleStorageRefresh = (event) => {
    if (event.key === AGENT_STATUS_EVENT_KEY) refreshAgentsNow();
  };
  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") fetchAgents();
  };

  window.addEventListener(AGENT_STATUS_EVENT_KEY, refreshAgentsNow);
  window.addEventListener("storage", handleStorageRefresh);
  window.addEventListener("focus", refreshAgentsNow);
  document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    window.removeEventListener(AGENT_STATUS_EVENT_KEY, refreshAgentsNow);
    window.removeEventListener("storage", handleStorageRefresh);
    window.removeEventListener("focus", refreshAgentsNow);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
}, [fetchAgents]);

useEffect(() => {
  // Zoom polling is intentionally disabled to reduce Cloudflare request usage.
  return undefined;
}, [fetchZoomCallSummary]);

useEffect(() => {
  const dateRolloverInterval = setInterval(() => {
    const nextToday = getTodayDateInput();

    if (nextToday === zoomAutoDateRef.current) return;

    setZoomDate((currentDate) => {
      const shouldFollowToday = currentDate === zoomAutoDateRef.current;
      zoomAutoDateRef.current = nextToday;

      return shouldFollowToday ? nextToday : currentDate;
    });
  }, 60000);

  return () => {
    clearInterval(dateRolloverInterval);
  };
}, []);

useEffect(() => {
  fetchBreakSettings();
}, [fetchBreakSettings]);

useEffect(() => {
  // Zoom polling is intentionally disabled to reduce Cloudflare request usage.
  return undefined;
}, [fetchZoomAvailability]);

useEffect(() => {
  if (!canUseAdminControls) return undefined;

  const passwordRequestsInterval = setInterval(() => {
    runWhenVisible(fetchPasswordResetRequests);
  }, PASSWORD_REQUESTS_REFRESH_MS);

  return () => {
    clearInterval(passwordRequestsInterval);
  };
}, [canUseAdminControls, fetchPasswordResetRequests]);

useEffect(() => {
  fetchDevicesBySource();
}, [fetchDevicesBySource]);


useEffect(() => {
  fetchArrData(selectedMonth, selectedYear);

  const dashboardInterval = setInterval(() => {
    runWhenVisible(() => fetchArrData(selectedMonth, selectedYear));
  }, DASHBOARD_REFRESH_MS);

  return () => {
    clearInterval(dashboardInterval);
  };
}, [selectedMonth, selectedYear, fetchArrData]);

useEffect(() => {
  const needsOpsTeamData =
    salesAnalyticsTab === "opsCenter" &&
    ["monthlyReport", "agentProfile"].includes(opsCenterView);

  if (
    ["teamArr", "targetPace"].includes(salesAnalyticsTab) ||
    needsOpsTeamData
  ) {
    fetchTeamArrData(selectedMonth, selectedYear);
  }
}, [
  salesAnalyticsTab,
  opsCenterView,
  selectedMonth,
  selectedYear,
  fetchTeamArrData
]);

useEffect(() => {
  const needsOpsBreakData =
    salesAnalyticsTab === "opsCenter" &&
    ["monthlyReport", "agentProfile"].includes(opsCenterView);

  if (salesAnalyticsTab === "breakReports" || needsOpsBreakData) {
    fetchMonthlyBreakReport();
  }
}, [salesAnalyticsTab, opsCenterView, fetchMonthlyBreakReport]);

useEffect(() => {
  return undefined;
}, [salesAnalyticsTab, opsCenterView, fetchZoomTrends]);

useEffect(() => {
  if (agents.length === 0) return;

  setManagerActionAgentId((current) =>
    agents.some((agent) => agent._id === current) ? current : agents[0]._id
  );
  setAgentProfileId((current) =>
    agents.some((agent) => agent._id === current) ? current : agents[0]._id
  );
}, [agents]);

useEffect(() => {
  const interval = setInterval(() => {
    setTick((t) => t + 1);
    setCurrentTime(new Date());
  }, 1000);

  return () => {
    clearInterval(interval);
  };
}, []);

const IDLE_LIMIT = 5 * 60 * 1000;
function isAgentWithinShift(agent) {
  const now = new Date();

  const pstTime = new Date(
    now.toLocaleString("en-US", {
      timeZone: "America/Los_Angeles"
    })
  );

  const currentHour = pstTime.getHours();
  const currentMinute = pstTime.getMinutes();

  const currentTime = currentHour * 60 + currentMinute;

  // Early Shift
  if (agent.shiftType === "early") {
    const start = 6 * 60;
    const end = 15 * 60;

    return currentTime >= start && currentTime <= end;
  }

  // Regular Shift
  const start = 8 * 60 + 30;
  const end = 17 * 60 + 30;

  return currentTime >= start && currentTime <= end;
}
useEffect(() => {
  localStorage.setItem("lgsMonth", lgsMonth);
  localStorage.setItem("lgsYear", lgsYear);
}, [lgsMonth, lgsYear]);

useEffect(() => {
  fetchLgsConversion();
}, [fetchLgsConversion]);

useEffect(() => {
  const updateViewportMode = () => {
    setIsMobileViewport(window.innerWidth <= 700);
  };

  updateViewportMode();
  window.addEventListener("resize", updateViewportMode);

  return () => window.removeEventListener("resize", updateViewportMode);
}, []);

useEffect(() => {
  if (!isReadOnlyViewer) return;

  setIsTvMode(true);

  if (!TV_ANALYTICS_TAB_KEYS.has(salesAnalyticsTab)) {
    setSalesAnalyticsTab("arr");
  }
}, [isReadOnlyViewer, salesAnalyticsTab]);

useEffect(() => {
  if (salesAnalyticsTab === "hubspot") {
    setSalesAnalyticsTab("arr");
  }
}, [salesAnalyticsTab]);

useEffect(() => {
  localStorage.setItem(
    "celebrationSoundMuted",
    String(celebrationMuted)
  );

  if (celebrationMuted) {
    stopCelebrationSound();
  }
}, [celebrationMuted, stopCelebrationSound]);

useEffect(() => {
  if (!celebrationPopup) {
    stopCelebrationSound();
    return undefined;
  }

  playCelebrationSound(celebrationPopup.type);

  return () => {
    stopCelebrationSound();
  };
}, [
  celebrationPopup,
  playCelebrationSound,
  stopCelebrationSound
]);
const toggleTvMode = () => {
  if (isReadOnlyViewer) {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }

    setIsTvMode(true);
    return;
  }

  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen();
    setIsTvMode(true);
  } else {
    document.exitFullscreen();
    setIsTvMode(false);
  }
};

const zoomStatsByAgentId = useMemo(() => {
  return new Map(
    zoomCallSummary.dashboardAgents.map((agentStats) => [
      agentStats.agentId,
      agentStats
    ])
  );
}, [zoomCallSummary.dashboardAgents]);

const zoomAvailabilityByAgentId = useMemo(() => {
  return new Map(
    zoomAvailability.dashboardAgents
      .filter((agentStatus) => agentStatus.status)
      .map((agentStatus) => [agentStatus.agentId, agentStatus])
  );
}, [zoomAvailability.dashboardAgents]);

const zoomUnmatchedAgents = useMemo(() => {
  return zoomCallSummary.agents.filter((agentStats) => !agentStats.dashboardAgentId);
}, [zoomCallSummary.agents]);

const zoomMappedAgentCount = useMemo(() => {
  return zoomCallSummary.dashboardAgents.filter(
    (agentStats) =>
      Number(agentStats.totalCalls || 0) > 0 ||
      Number(agentStats.inboundCalls || 0) > 0
  ).length;
}, [zoomCallSummary.dashboardAgents]);

const displayAgents = useMemo(() => {
  const now = Date.now();
  const visibleAgentIds = new Set();

  const nextAgents = agents.map((agent) => {
    const zoomStatus = zoomAvailabilityByAgentId.get(agent._id);
    const zoomStats = zoomStatsByAgentId.get(agent._id);
    const zoomStatsStatusStartedAt = Number(zoomStats?.lastStatusStartedAt || 0);
    const zoomStatsOnCallIsFresh =
      zoomStats?.lastStatus !== "On Call" ||
      (zoomStatsStatusStartedAt > 0 &&
        now - zoomStatsStatusStartedAt <= ZOOM_ON_CALL_STALE_LIMIT);
    const zoomLiveStatus =
      zoomStatus ||
      (zoomStats?.lastStatus && zoomStatsOnCallIsFresh
        ? {
            status: zoomStats.lastStatus,
            statusLabel: zoomStats.statusLabel || zoomStats.lastStatus,
            statusStartTime: zoomStats.lastStatusStartedAt || null
          }
        : null);
    const zoomStatusStartTime =
      Math.max(
        Number(zoomLiveStatus?.statusStartTime || 0),
        Number(zoomStats?.lastStatusStartedAt || 0)
      ) || agent.statusStartTime;
    const manualStatus = agent.status;
    const previousClock = displayStatusClockRef.current.get(agent._id);
    let displayAgent = {
      ...agent,
      manualStatus,
      zoomAvailability: zoomLiveStatus || null
    };

    visibleAgentIds.add(agent._id);

    if (
      zoomLiveStatus &&
      manualStatus !== "Break" &&
      manualStatus !== "Meeting" &&
      manualStatus !== "Logged Out" &&
      zoomLiveStatus.status === "On Call"
    ) {
      displayAgent = {
        ...agent,
        manualStatus,
        status: zoomLiveStatus.status,
        statusLabel: zoomLiveStatus.statusLabel || zoomLiveStatus.status,
        statusStartTime: zoomStatusStartTime,
        zoomAvailability: zoomLiveStatus
      };
    } else if (
      zoomLiveStatus &&
      manualStatus !== "Break" &&
      manualStatus !== "Meeting" &&
      manualStatus !== "Logged Out" &&
      zoomLiveStatus.status === "Available"
    ) {
      displayAgent = {
        ...agent,
        manualStatus,
        status: "Available",
        statusLabel: zoomLiveStatus.statusLabel || "Available",
        statusStartTime: zoomStatusStartTime,
        zoomAvailability: zoomLiveStatus
      };
    }

    const rawStartTime = Number(displayAgent.statusStartTime || 0);
    const currentCallCount =
      Number(zoomStats?.totalCalls || 0) + Number(zoomStats?.inboundCalls || 0);
    const statusClockKey = [
      displayAgent.status || "",
      displayAgent.breakType || "",
      displayAgent.statusLabel || ""
    ].join(":");
    let statusStartTime = rawStartTime || now;

    if (!previousClock) {
      statusStartTime = rawStartTime || now;
    } else if (previousClock.statusClockKey !== statusClockKey) {
      statusStartTime = rawStartTime || now;
    } else if (
      displayAgent.status === "Available" &&
      currentCallCount > Number(previousClock.callCount || 0)
    ) {
      statusStartTime = now;
    } else {
      statusStartTime = previousClock.statusStartTime;
    }

    displayStatusClockRef.current.set(agent._id, {
      statusClockKey,
      callCount: currentCallCount,
      statusStartTime
    });

    return {
      ...displayAgent,
      statusStartTime
    };
  });

  Array.from(displayStatusClockRef.current.keys()).forEach((agentId) => {
    if (!visibleAgentIds.has(agentId)) {
      displayStatusClockRef.current.delete(agentId);
    }
  });

  return nextAgents;
}, [agents, zoomAvailabilityByAgentId, zoomStatsByAgentId]);

const agentSearchTerm = agentSearch.trim().toLowerCase();
const filteredDisplayAgents = useMemo(() => {
  if (isTvMode) return displayAgents;
  if (!agentSearchTerm) return displayAgents;

  return displayAgents.filter((agent) => {
    const zoomStats = zoomStatsByAgentId.get(agent._id);
    const haystack = [
      agent.name,
      agent.status,
      agent.statusLabel,
      agent.breakType,
      agent.zoomEmail,
      agent.zoomExtension,
      zoomStats?.extension,
      zoomStats?.zoomEmails?.join(" "),
      zoomStats?.zoomNames?.join(" ")
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(agentSearchTerm);
  });
}, [agentSearchTerm, displayAgents, isTvMode, zoomStatsByAgentId]);

const dialCountDisplayAgents = useMemo(() => {
  return [...filteredDisplayAgents].sort((a, b) => {
    const aStats = zoomStatsByAgentId.get(a._id);
    const bStats = zoomStatsByAgentId.get(b._id);
    const aDials = Number(aStats?.totalCalls ?? a.dialCount ?? 0);
    const bDials = Number(bStats?.totalCalls ?? b.dialCount ?? 0);

    if (bDials !== aDials) return bDials - aDials;

    return String(a.name || "").localeCompare(String(b.name || ""));
  });
}, [filteredDisplayAgents, zoomStatsByAgentId]);

const idleDetectedAgents = useMemo(() => {
  return displayAgents.filter((agent) => {
    if (!isAgentWithinShift(agent)) {
      return false;
    }

    if (agent.status !== "Available" || agent.status === "Logged Out") {
      return false;
    }

    if (!agent.statusStartTime) return false;

    const idleTime = Date.now() - Number(agent.statusStartTime);

    return idleTime >= IDLE_LIMIT;
  });
}, [displayAgents, IDLE_LIMIT]);

useEffect(() => {
  setIdleAgents(idleDetectedAgents);
}, [idleDetectedAgents]);

const availableAgents = filteredDisplayAgents.filter(
  (a) => a.status === "Available"
);

const loggedOutAgents = filteredDisplayAgents.filter(
  (a) => a.status === "Logged Out"
);
const sortedLoggedOutAgents = [...loggedOutAgents].sort((left, right) =>
  (left.name || "").localeCompare(right.name || "")
);

const onCallAgents = filteredDisplayAgents.filter((a) => a.status === "On Call");
const meetingAgents = filteredDisplayAgents.filter((a) => a.status === "Meeting");

const dailyBreakLimitMs = Number(breakSettings.breakLimitMs || DEFAULT_BREAK_LIMIT);
const breakAgents = filteredDisplayAgents.filter((a) => a.status === "Break");
const allBreakAgents = displayAgents.filter((a) => a.status === "Break");
const allBreakExceededAgents = allBreakAgents.filter((agent) =>
  isBreakExceeded(agent, dailyBreakLimitMs)
);

const breakExceededAgents = breakAgents.filter((agent) =>
  isBreakExceeded(agent, dailyBreakLimitMs)
);
const monthlyBreakTotalMs = monthlyBreakData.agents.reduce(
  (sum, agent) => sum + Number(agent.totalBreakTime || 0),
  0
);
const monthlyAverageBreakMs =
  monthlyBreakData.summary.breakCount > 0
    ? monthlyBreakTotalMs / monthlyBreakData.summary.breakCount
    : 0;

const currentArrConfig =
  ARR_LEADERBOARD_TABS[arrTab] || ARR_LEADERBOARD_TABS.outbound;
const currentArrList = arrData[currentArrConfig.dataKey] || [];
const currentArrValueKey = currentArrConfig.valueKey;

const leaderboardTeamOptions = Array.from(
  new Set(currentArrList.map((agent) => agent.team).filter(Boolean))
).sort((a, b) => a.localeCompare(b));
const leaderboardRoleOptions = Array.from(
  new Set(currentArrList.map((agent) => agent.role).filter(Boolean))
).sort((a, b) => a.localeCompare(b));
const filteredArrList = currentArrList
  .filter((agent) =>
    leaderboardTeamFilter === "all" ? true : agent.team === leaderboardTeamFilter
  )
  .filter((agent) =>
    leaderboardRoleFilter === "all" ? true : agent.role === leaderboardRoleFilter
  )
  .map((agent, index) => ({
    ...agent,
    displayRank: index + 1
  }));

  const currentArrLabel = currentArrConfig.label;
  const ytdCurrentYear = ytdThroughYear || String(new Date().getFullYear());
  const ytdPreviousYear = String(Number(ytdCurrentYear) - 1);
  const ytdCurrentMonthIndex = Math.max(
    0,
    Math.min(11, Number(ytdThroughMonth || "12") - 1)
  );
  const ytdThroughMonthLabel = getMonthLabel(ytdThroughMonth);
  const ytdThroughLabel = `${ytdThroughMonthLabel} ${ytdCurrentYear}`;
  const ytdYearOptions = useMemo(() => {
    const today = new Date();
    const years = new Set([
      String(today.getFullYear()),
      String(today.getFullYear() - 1),
      String(today.getFullYear() + 1),
      ytdCurrentYear,
      selectedYear
    ]);

    arrComparisonData.forEach((item) => {
      Object.keys(item || {}).forEach((key) => {
        const match = key.match(/^(arr|outbound|inbound)(\d{4})$/);
        if (match) {
          years.add(match[2]);
        }
      });
    });

    return Array.from(years)
      .filter(Boolean)
      .sort((a, b) => Number(b) - Number(a));
  }, [arrComparisonData, selectedYear, ytdCurrentYear]);
  const arrYtdTotals = useMemo(() => {
    const ytdRows = arrComparisonData.filter((item) => {
      const monthIndex = MONTH_INDEX[item.month];
      return monthIndex !== undefined && monthIndex <= ytdCurrentMonthIndex;
    });

    const currentKey = `arr${ytdCurrentYear}`;
    const previousKey = `arr${ytdPreviousYear}`;
    const currentOutboundKey = `outbound${ytdCurrentYear}`;
    const previousOutboundKey = `outbound${ytdPreviousYear}`;
    const currentInboundKey = `inbound${ytdCurrentYear}`;
    const previousInboundKey = `inbound${ytdPreviousYear}`;
    const currentYtd = ytdRows.reduce(
      (sum, item) => sum + Number(item[currentKey] || 0),
      0
    );
    const previousYtd = ytdRows.reduce(
      (sum, item) => sum + Number(item[previousKey] || 0),
      0
    );
    const currentOutboundYtd = ytdRows.reduce(
      (sum, item) => sum + Number(item[currentOutboundKey] || 0),
      0
    );
    const previousOutboundYtd = ytdRows.reduce(
      (sum, item) => sum + Number(item[previousOutboundKey] || 0),
      0
    );
    const currentInboundYtd = ytdRows.reduce(
      (sum, item) => sum + Number(item[currentInboundKey] || 0),
      0
    );
    const previousInboundYtd = ytdRows.reduce(
      (sum, item) => sum + Number(item[previousInboundKey] || 0),
      0
    );
    const growth =
      previousYtd > 0
        ? ((currentYtd - previousYtd) / previousYtd) * 100
        : null;
    const outboundGrowth =
      previousOutboundYtd > 0
        ? ((currentOutboundYtd - previousOutboundYtd) / previousOutboundYtd) *
          100
        : null;

    return {
      currentYtd,
      previousYtd,
      currentOutboundYtd,
      previousOutboundYtd,
      currentInboundYtd,
      previousInboundYtd,
      growth,
      outboundGrowth
    };
  }, [
    arrComparisonData,
    ytdCurrentMonthIndex,
    ytdCurrentYear,
    ytdPreviousYear
  ]);
  const currentYearTotalKey = `arr${ytdCurrentYear}`;
  const previousYearTotalKey = `arr${ytdPreviousYear}`;
  const currentYearOutboundKey = `outbound${ytdCurrentYear}`;
  const previousYearOutboundKey = `outbound${ytdPreviousYear}`;
  const currentYearInboundKey = `inbound${ytdCurrentYear}`;
  const previousYearInboundKey = `inbound${ytdPreviousYear}`;
  const arrComparisonMetricOptions = [
    {
      key: "total",
      label: "Total ARR",
      currentKey: currentYearTotalKey,
      previousKey: previousYearTotalKey
    },
    {
      key: "outbound",
      label: "Outbound ARR",
      currentKey: currentYearOutboundKey,
      previousKey: previousYearOutboundKey
    },
    {
      key: "inbound",
      label: "Inbound ARR",
      currentKey: currentYearInboundKey,
      previousKey: previousYearInboundKey
    }
  ];
  const selectedArrComparisonMetric =
    arrComparisonMetricOptions.find(
      (option) => option.key === arrComparisonMetric
    ) || arrComparisonMetricOptions[0];
  const currentMonthLabel =
    Object.entries(MONTH_LABELS).find(
      ([, label]) => MONTH_INDEX[label] === ytdCurrentMonthIndex
    )?.[1]?.slice(0, 3) || "";
  const selectedArrDrilldown =
    arrComparisonData.find((item) => item.month === arrDrilldownMonth) ||
    arrComparisonData.find((item) => item.month === currentMonthLabel) ||
    arrComparisonData[0] ||
    null;
  const drilldownCurrentTotal = Number(
    selectedArrDrilldown?.[currentYearTotalKey] || 0
  );
  const drilldownPreviousTotal = Number(
    selectedArrDrilldown?.[previousYearTotalKey] || 0
  );
  const drilldownGrowth =
    drilldownPreviousTotal > 0
      ? ((drilldownCurrentTotal - drilldownPreviousTotal) /
          drilldownPreviousTotal) *
        100
      : null;
  const arrSummary = arrData.summary || {};
  const totalARR = Number(arrSummary.totalARR || 0);
  const outboundARR = Number(arrSummary.outboundARR || 0);
  const inboundARR = Number(arrSummary.inboundARR || 0);
  const outboundShare =
    totalARR > 0 ? (outboundARR / totalARR) * 100 : 0;
  const inboundShare =
    totalARR > 0 ? (inboundARR / totalARR) * 100 : 0;
  const topOutbound = arrSummary.topOutboundPerformer;
  const topArrPerformer = topOutbound
    ? {
        name: topOutbound.name,
        value: topOutbound.outboundARR,
        type: "Outbound"
      }
    : null;
  const selectedArrPeriod = `${getMonthLabel(selectedMonth)} ${
    selectedYear || "All Years"
  }`;
  const teamChartData = teamArrData.teams.slice(0, 8).map((team) => ({
    team: team.team,
    totalARR: Number(team.totalARR || 0),
    outboundARR: Number(team.outboundARR || 0),
    inboundARR: Number(team.inboundARR || 0),
    previousARR: Number(team.previousARR || 0)
  }));
  const selectedTeamArr =
    teamArrData.teams.find((team) => team.team === selectedTeamArrName) ||
    teamArrData.teams[0] ||
    null;
  const selectedTeamAgents = Array.isArray(selectedTeamArr?.agents)
    ? selectedTeamArr.agents.map((agent, index) =>
        typeof agent === "string"
          ? {
              rank: index + 1,
              name: agent,
              totalARR: 0,
              outboundARR: 0,
              inboundARR: 0,
              target: 0,
              targetProgress: null
            }
          : agent
      )
    : [];
  const targetPaceDaysRemaining = getRemainingDaysInMonth(
    selectedMonth,
    selectedYear
  );
  const targetPaceAgents = teamArrData.teams
    .flatMap((team) =>
      (Array.isArray(team.agents) ? team.agents : [])
        .filter((agent) => typeof agent !== "string" && Number(agent.target || 0) > 0)
        .map((agent) => {
          const target = Number(agent.target || 0);
          const outbound = Number(agent.outboundARR || 0);
          const remaining = Math.max(target - outbound, 0);
          const progress = target > 0 ? (outbound / target) * 100 : 0;

          return {
            ...agent,
            team: team.team,
            target,
            outboundARR: outbound,
            remaining,
            progress,
            dailyNeeded:
              remaining > 0
                ? remaining / Math.max(targetPaceDaysRemaining, 1)
                : 0,
            status:
              remaining <= 0
                ? "Hit"
                : progress >= 80
                  ? "Close"
                  : progress >= 50
                    ? "On Watch"
                    : "Needs Push"
          };
        })
    )
    .sort((a, b) => {
      if (a.remaining === 0 && b.remaining > 0) return 1;
      if (b.remaining === 0 && a.remaining > 0) return -1;
      return b.dailyNeeded - a.dailyNeeded;
    });
  const targetPaceSummary = targetPaceAgents.reduce(
    (summary, agent) => {
      summary.target += Number(agent.target || 0);
      summary.outbound += Number(agent.outboundARR || 0);
      summary.remaining += Number(agent.remaining || 0);

      if (Number(agent.remaining || 0) <= 0) {
        summary.hit += 1;
      } else if (Number(agent.progress || 0) < 70) {
        summary.needsPush += 1;
      }

      return summary;
    },
    {
      target: 0,
      outbound: 0,
      remaining: 0,
      hit: 0,
      needsPush: 0
    }
  );
  const teamTargetLeaders = [...teamArrData.teams]
    .filter((team) => Number(team.target || 0) > 0)
    .sort(
      (a, b) =>
        Number(b.targetProgress || 0) - Number(a.targetProgress || 0)
    )
    .slice(0, 5);
  const closeToTargetAgents = teamArrData.teams
    .flatMap((team) =>
      (Array.isArray(team.agents) ? team.agents : [])
        .filter(
          (agent) =>
            typeof agent !== "string" &&
            Number(agent.target || 0) > 0 &&
            Number(agent.targetProgress || 0) >= 70 &&
            Number(agent.targetProgress || 0) < 100
        )
        .map((agent) => ({
          ...agent,
          team: team.team
        }))
    )
    .sort(
      (a, b) =>
        Number(b.targetProgress || 0) - Number(a.targetProgress || 0)
    )
    .slice(0, 6);
  const selectedDevicesPeriod = `${getMonthLabel(devicesMonth)} ${
    devicesYear || "All Years"
  }`;
  const selectedLgsPeriod = `${getMonthLabel(lgsMonth)} ${
    lgsYear || "All Years"
  }`;
  const selectedManagerAgent =
    displayAgents.find((agent) => agent._id === managerActionAgentId) ||
    displayAgents[0] ||
    null;
  const selectedProfileAgent =
    displayAgents.find((agent) => agent._id === agentProfileId) ||
    displayAgents[0] ||
    null;
  const selectedProfileZoomStats = selectedProfileAgent
    ? zoomStatsByAgentId.get(selectedProfileAgent._id)
    : null;
  const selectedProfileBreakReport = selectedProfileAgent
    ? monthlyBreakData.agents.find(
        (agent) =>
          normalizeLocalName(agent.agentName) ===
          normalizeLocalName(selectedProfileAgent.name)
      )
    : null;
  const selectedProfileArr = selectedProfileAgent
    ? teamArrData.teams
        .flatMap((team) =>
          (Array.isArray(team.agents) ? team.agents : [])
            .filter((agent) => typeof agent !== "string")
            .map((agent) => ({
              ...agent,
              team: team.team
            }))
        )
        .find(
          (agent) =>
            normalizeLocalName(agent.name) ===
            normalizeLocalName(selectedProfileAgent.name)
        )
    : null;
  const bestArrMonth = arrComparisonData
    .map((item) => ({
      month: item.month,
      value: Number(item[currentYearTotalKey] || 0)
    }))
    .sort((a, b) => b.value - a.value)[0];
  const topSourceShare =
    devicesBySource.totalDevices > 0 && devicesBySource.topSource?.count
      ? (devicesBySource.topSource.count / devicesBySource.totalDevices) * 100
      : 0;
  const lgsConversionGap =
    Number(lgsData.totalTransferred || 0) -
    Number(lgsData.totalConverted || 0);
  const insightRows = [
    {
      label: "ARR Mix",
      title:
        outboundARR >= inboundARR
          ? "Outbound is carrying more ARR"
          : "Inbound is carrying more ARR",
      detail: `${formatInsightPercent(outboundShare)} outbound / ${formatInsightPercent(
        inboundShare
      )} inbound for ${selectedArrPeriod}.`,
      value: formatARR(totalARR)
    },
    {
      label: "YTD Movement",
      title:
        (arrYtdTotals.outboundGrowth || 0) >= 0
          ? `Outbound ARR is ahead of ${ytdPreviousYear}`
          : `Outbound ARR is behind ${ytdPreviousYear}`,
      detail:
        arrYtdTotals.previousOutboundYtd > 0
          ? `${formatInsightPercent(Math.abs(arrYtdTotals.outboundGrowth || 0))} ${
              (arrYtdTotals.outboundGrowth || 0) >= 0 ? "above" : "below"
            } ${ytdPreviousYear} outbound YTD through ${ytdThroughMonthLabel}.`
          : "Previous year comparison is not available yet.",
      value: formatARR(arrYtdTotals.currentOutboundYtd)
    },
    {
      label: "Devices",
      title: devicesBySource.topSource?.source
        ? `${devicesBySource.topSource.source} is the top source`
        : "No source data yet",
      detail: `${formatInsightPercent(topSourceShare)} of ${devicesBySource.totalDevices || 0} devices.`,
      value: String(devicesBySource.topSource?.count || 0)
    },
    {
      label: "LGS",
      title: lgsData.topLGS?.name
        ? `${lgsData.topLGS.name} is leading LGS`
        : "No LGS leader yet",
      detail: `${lgsData.totalConverted || 0} converted from ${
        lgsData.totalTransferred || 0
      } transferred leads.`,
      value: `${lgsData.conversionRate || 0}%`
    }
  ];
  const notificationItems = [
    passwordResetRequests.length > 0 && {
      type: "Password",
      title: `${passwordResetRequests.length} password request${
        passwordResetRequests.length === 1 ? "" : "s"
      } pending`,
      detail: "Review requests from the admin controls.",
      tone: "info"
    },
    allBreakExceededAgents.length > 0 && {
      type: "Break",
      title: `${allBreakExceededAgents.length} break limit alert${
        allBreakExceededAgents.length === 1 ? "" : "s"
      }`,
      detail: allBreakExceededAgents
        .slice(0, 3)
        .map((agent) => agent.name)
        .join(", "),
      tone: "danger"
    },
    idleDetectedAgents.length > 0 && {
      type: "Idle",
      title: `${idleDetectedAgents.length} idle agent${
        idleDetectedAgents.length === 1 ? "" : "s"
      }`,
      detail: "Available agents past the idle threshold.",
      tone: "warning"
    },
    zoomError && {
      type: "Zoom",
      title: "Dial count sync issue",
      detail: zoomError,
      tone: "danger"
    },
    zoomAvailabilityError && {
      type: "Zoom",
      title: "Availability sync issue",
      detail: zoomAvailabilityError,
      tone: "danger"
    },
    celebrationPopup && {
      type: "Sales",
      title:
        celebrationPopup.type === "target"
          ? `${celebrationPopup.name} hit target`
          : `${celebrationPopup.name} closed a big deal`,
      detail: formatARR(celebrationPopup.arr || 0),
      tone: "success"
    }
  ].filter(Boolean);
  const recentSalesFeed = Array.isArray(arrData.salesFeed)
    ? arrData.salesFeed.slice(0, 6)
    : [];
  const visibleAnalyticsTabs = isReadOnlyViewer
    ? ANALYTICS_TABS.filter((tab) => TV_ANALYTICS_TAB_KEYS.has(tab.key))
    : ANALYTICS_TABS;

  return (
   <div
    className={`${isTvMode ? "dashboard tv-mode-active" : "dashboard"}${
      isReadOnlyViewer ? " tv-viewer-mode" : ""
    }`}
  >
    {isTvMode && canUseAdminControls && (
  <div
    className="tv-mode-controls"
    style={{
      position: "fixed",
      top: "86px",
      right: "14px",
      zIndex: 999999,
      display: "flex",
      gap: "10px",
      maxWidth: "min(980px, calc(100vw - 28px))",
      flexWrap: "wrap",
      justifyContent: "flex-end",
      background: "rgba(2,6,23,0.85)",
      padding: "10px",
      borderRadius: "14px",
      border: "1px solid rgba(148,163,184,0.25)",
      boxShadow: "0 12px 30px rgba(0,0,0,0.35)"
    }}
  >

<button className="logout-btn" onClick={downloadBreakReport}>
  Download Report
</button>

<button
  className="logout-btn"
  onClick={openUserManagement}
>
  User Management
</button>

<button
  className="logout-btn"
  onClick={() => setShowShiftManagement(true)}
>
  Shift Management
</button>

<button
  className="logout-btn"
  onClick={() => setShowIdlePopup(!showIdlePopup)}
>
  {showIdlePopup ? "Disable Idle Popup" : "Enable Idle Popup"}
</button>

<button
  className="logout-btn"
  onClick={() => {
    setShowHistoricalReports(true);
    fetchHistoricalReports(selectedReportStartDate, selectedReportEndDate);
  }}
>
  Historical Reports
</button>

<button
  className="logout-btn"
  onClick={() => {
    setShowPasswordRequests(!showPasswordRequests);
    fetchPasswordResetRequests();
  }}
>
  Password Requests
  {passwordResetRequests.length > 0 ? ` (${passwordResetRequests.length})` : ""}
</button>

<button
  className="logout-btn"
  onClick={() => {
    setShowArrLeaderboard(true);
    fetchArrData(selectedMonth, selectedYear);
  }}
>
  ARR Leaderboard
</button>
    <button className="logout-btn" onClick={toggleTvMode}>
      Exit TV Mode
    </button>

<button
  className="logout-btn"
  onClick={() => {
    if (logout) {
      logout();
      return;
    }

    localStorage.clear();
    window.location.reload();
  }}
>
  Logout
</button>
  </div>
)}
    {isTvMode && isReadOnlyViewer && (
  <div
    className="tv-mode-controls tv-viewer-controls"
    style={{
      position: "fixed",
      top: "86px",
      right: "14px",
      zIndex: 999999,
      display: "flex",
      gap: "10px",
      maxWidth: "min(620px, calc(100vw - 28px))",
      flexWrap: "wrap",
      justifyContent: "flex-end",
      background: "rgba(2,6,23,0.85)",
      padding: "10px",
      borderRadius: "14px",
      border: "1px solid rgba(148,163,184,0.25)",
      boxShadow: "0 12px 30px rgba(0,0,0,0.35)"
    }}
  >
    <button
      className="logout-btn"
      onClick={() => setShowIdlePopup(!showIdlePopup)}
    >
      {showIdlePopup ? "Disable Idle Popup" : "Enable Idle Popup"}
    </button>

    <button
      className="logout-btn"
      onClick={() => {
        setShowArrLeaderboard(true);
        fetchArrData(selectedMonth, selectedYear);
      }}
    >
      ARR Leaderboard
    </button>

    <button className="logout-btn" onClick={toggleTvMode}>
      TV Mode
    </button>

    <button
      className="logout-btn"
      onClick={() => {
        if (logout) {
          logout();
          return;
        }

        localStorage.clear();
        window.location.reload();
      }}
    >
      Logout
    </button>
  </div>
)}
 {showIdlePopup && idleAgents.length > 0 && (
  <div
    style={{
      position: "fixed",
      top: isMobileViewport ? "12px" : "30px",
      right: isMobileViewport ? "10px" : "16px",
      left: isMobileViewport ? "10px" : "auto",
      width: isMobileViewport ? "auto" : "min(420px, calc(100vw - 32px))",
      background: "linear-gradient(135deg, #991b1b, #7f1d1d)",
      border: "2px solid #ef4444",
      borderRadius: isMobileViewport ? "18px" : "24px",
      padding: isMobileViewport ? "16px" : "24px",
      zIndex: 999999,
      maxHeight: isMobileViewport ? "72vh" : "78vh",
      overflowY: "auto",
      boxShadow: "0 0 40px rgba(239,68,68,0.55)",
      animation: "pulse 1.5s infinite"
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "18px"
      }}
    >
      <h2
        style={{
          color: "#fff",
          margin: 0,
          fontSize: isMobileViewport ? "20px" : "28px"
        }}
      >
        ⚠ Idle Agents
      </h2>

      <button
        onClick={() => setShowIdlePopup(false)}
        style={{
          background: "transparent",
          border: "none",
          color: "#fff",
          fontSize: isMobileViewport ? "22px" : "28px",
          cursor: "pointer",
          fontWeight: "bold"
        }}
      >
        ×
      </button>
    </div>

    {idleAgents.map((agent) => {
      const idleMinutes = Math.floor(
        (Date.now() - Number(agent.statusStartTime)) / 60000
      );

      return (
        <div
          key={agent._id}
          style={{
            background: "rgba(255,255,255,0.08)",
            padding: "14px",
            borderRadius: "14px",
            marginBottom: "12px",
            border: "1px solid rgba(255,255,255,0.12)"
          }}
        >
          <div
            style={{
              color: "#fff",
              fontSize: isMobileViewport ? "18px" : "22px",
              fontWeight: "bold"
            }}
          >
            {agent.name}
          </div>

          <div
            style={{
              color: "#fecaca",
              marginTop: "6px",
              fontWeight: "bold"
            }}
          >
            Idle from last {idleMinutes} mins
          </div>
        </div>
      );
    })}
  </div>
)}
    {celebrationPopup && (
      <CelebrationPopup
        popup={celebrationPopup}
        period={selectedArrPeriod}
        onClose={() => setCelebrationPopup(null)}
      />
    )}
    <ArrTicker
      summary={arrLastSyncedAt ? arrData.summary : null}
      loading={arrLoading}
      error={arrError}
    />
      {showArrLeaderboard && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background:
              "radial-gradient(circle at top, rgba(37,99,235,0.25), rgba(0,0,0,0.92))",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999
          }}
        >
          <div
            style={{
              background: "linear-gradient(180deg, #101a33, #08111f)",
              padding: "30px",
              borderRadius: "28px",
              width: "94%",
              height: "90vh",
              overflowY: "auto",
              boxShadow: "0 0 50px rgba(0,0,0,0.65)",
              border: "1px solid rgba(148,163,184,0.22)",
              animation: "leaderboardPop 0.35s ease-out"
            }}
          >
            <style>
              {`
                @keyframes leaderboardPop {
                  from {
                    opacity: 0;
                    transform: scale(0.96) translateY(20px);
                  }
                  to {
                    opacity: 1;
                    transform: scale(1) translateY(0);
                  }
                }

                @keyframes rankSlideIn {
                  from {
                    opacity: 0;
                    transform: translateX(-20px);
                  }
                  to {
                    opacity: 1;
                    transform: translateX(0);
                  }
                }

                @keyframes softGlow {
                  0% {
                    box-shadow: 0 0 16px rgba(245,158,11,0.25);
                  }
                  50% {
                    box-shadow: 0 0 34px rgba(245,158,11,0.55);
                  }
                  100% {
                    box-shadow: 0 0 16px rgba(245,158,11,0.25);
                  }
                }

                .leaderboard-card:hover {
                  transform: translateY(-3px) scale(1.01);
                  border-color: rgba(56,189,248,0.75) !important;
                }
              `}
            </style>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "24px",
                gap: "20px"
              }}
            >
              <div>
                <h2 style={{ color: "#fff", fontSize: "36px", margin: 0 }}>
                  🏆 ARR Leaderboard
                </h2>
                <p style={{ color: "#9ca3af", marginTop: "8px" }}>
                  BDE and Sr BDE ARR are shown in separate outbound and inbound rankings.
                </p>
                <p style={{ color: "#38bdf8", marginTop: "8px" }}>
                  Showing: {selectedMonth || "All Months"} /{" "}
                  {selectedYear || "All Years"} · {currentArrLabel}
                </p>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                  flexWrap: "wrap",
                  justifyContent: "flex-end"
                }}
              >
                <select
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  style={{
                    padding: "12px",
                    borderRadius: "12px",
                    background: "#0f3460",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.12)",
                    fontWeight: "bold"
                  }}
                >
                  <option value="">All Months</option>
                  <option value="01">January</option>
                  <option value="02">February</option>
                  <option value="03">March</option>
                  <option value="04">April</option>
                  <option value="05">May</option>
                  <option value="06">June</option>
                  <option value="07">July</option>
                  <option value="08">August</option>
                  <option value="09">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>

                <select
                  value={selectedYear}
                  onChange={handleYearChange}
                  style={{
                    padding: "12px",
                    borderRadius: "12px",
                    background: "#0f3460",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.12)",
                    fontWeight: "bold"
                  }}
                >
                  <option value="">All Years</option>
                  <option value="2025">2025</option>
                  <option value="2026">2026</option>
                  <option value="2027">2027</option>
                </select>

                <select
                  value={leaderboardTeamFilter}
                  onChange={(e) => setLeaderboardTeamFilter(e.target.value)}
                  style={{
                    padding: "12px",
                    borderRadius: "12px",
                    background: "#0f3460",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.12)",
                    fontWeight: "bold"
                  }}
                >
                  <option value="all">All Teams</option>
                  {leaderboardTeamOptions.map((team) => (
                    <option key={team} value={team}>
                      {team}
                    </option>
                  ))}
                </select>

                <select
                  value={leaderboardRoleFilter}
                  onChange={(e) => setLeaderboardRoleFilter(e.target.value)}
                  style={{
                    padding: "12px",
                    borderRadius: "12px",
                    background: "#0f3460",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.12)",
                    fontWeight: "bold"
                  }}
                >
                  <option value="all">All Roles</option>
                  {leaderboardRoleOptions.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>

                <button
                  onClick={() => fetchArrData(selectedMonth, selectedYear)}
                  style={{
                    background: "#2563eb",
                    color: "#fff",
                    border: "none",
                    padding: "12px 18px",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontWeight: "bold"
                  }}
                >
                  Refresh
                </button>

                <button
                  onClick={() => setShowArrLeaderboard(false)}
                  style={{
                    background: "#ff4d4f",
                    color: "#fff",
                    border: "none",
                    padding: "12px 18px",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontWeight: "bold"
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            {arrLoading && (
              <p style={{ color: "#fff", fontWeight: "bold" }}>
                Loading ARR data...
              </p>
            )}

            <div className="analytics-summary-grid">
              <div className="summary-card">
                <h3>Total ARR</h3>
                <p>{formatARR(arrData.summary.totalARR)}</p>
              </div>

              <div className="summary-card">
                <h3>Outbound ARR</h3>
                <p>{formatARR(arrData.summary.outboundARR)}</p>
              </div>

              <div className="summary-card">
                <h3>Inbound ARR</h3>
                <p>{formatARR(arrData.summary.inboundARR)}</p>
              </div>

              <div className="summary-card">
                <h3>Top Outbound</h3>
                <p style={{ fontSize: "24px" }}>
                  {arrData.summary.topOutboundPerformer?.name || "N/A"}
                </p>
              </div>

              <div className="summary-card">
                <h3>Top Inbound</h3>
                <p style={{ fontSize: "24px" }}>
                  {arrData.summary.topInboundPerformer?.name || "N/A"}
                </p>
              </div>

              <div className="summary-card">
                <h3>Top Sr BDE Outbound</h3>
                <p style={{ fontSize: "24px" }}>
                  {arrData.summary.topSrBdeOutboundPerformer?.name || "N/A"}
                </p>
              </div>

              <div className="summary-card">
                <h3>Top Sr BDE Inbound</h3>
                <p style={{ fontSize: "24px" }}>
                  {arrData.summary.topSrBdeInboundPerformer?.name || "N/A"}
                </p>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                marginBottom: "24px",
                flexWrap: "wrap"
              }}
            >
              {Object.entries(ARR_LEADERBOARD_TABS).map(([tabKey, tab]) => (
                <button
                  key={tabKey}
                  onClick={() => setArrTab(tabKey)}
                  style={{
                    background: arrTab === tabKey ? "#2563eb" : "#0f3460",
                    color: "#fff",
                    border:
                      arrTab === tabKey
                        ? "1px solid #60a5fa"
                        : "1px solid rgba(255,255,255,0.1)",
                    padding: "12px 22px",
                    borderRadius: "999px",
                    cursor: "pointer",
                    fontWeight: "bold"
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {filteredArrList.length === 0 ? (
              <p style={{ color: "#fff" }}>No ARR data found</p>
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      filteredArrList.length >= 3
                        ? "1.2fr 1fr 1fr"
                        : "repeat(3, 1fr)",
                    gap: "16px",
                    marginBottom: "24px"
                  }}
                >
                  {filteredArrList.slice(0, 3).map((agent) => {
                    const arrValue = agent[currentArrValueKey] || 0;

                    return (
                      <div
                          key={`top-${arrTab}-${agent.displayRank}-${agent.name}`}
                        style={{
                          background:
                              agent.displayRank === 1
                              ? "linear-gradient(135deg, rgba(245,158,11,0.35), rgba(15,23,42,0.95))"
                              : "linear-gradient(135deg, rgba(56,189,248,0.18), rgba(15,23,42,0.95))",
                          border: `1px solid ${getRankColor(agent.displayRank)}`,
                          borderRadius: "22px",
                          padding: "22px",
                          minHeight: agent.displayRank === 1 ? "190px" : "160px",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          animation:
                            agent.displayRank === 1
                              ? "softGlow 2s infinite"
                              : "rankSlideIn 0.45s ease-out",
                          transition: "0.25s ease"
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: agent.displayRank === 1 ? "46px" : "36px",
                              marginBottom: "10px"
                            }}
                          >
                            {getRankIcon(agent.displayRank)}
                          </div>

                          <h3
                            style={{
                              color: "#fff",
                              margin: 0,
                              fontSize: agent.displayRank === 1 ? "28px" : "22px"
                            }}
                          >
                            {agent.name}
                          </h3>

                          <p
                            style={{
                              color: "#94a3b8",
                              marginTop: "8px",
                              fontWeight: "bold"
                            }}
                          >
                            Rank #{agent.displayRank}
                            {agent.team ? ` - ${agent.team}` : ""}
                          </p>
                        </div>

                        <div
                          style={{
                            color: getRankColor(agent.displayRank),
                            fontSize: agent.displayRank === 1 ? "42px" : "32px",
                            fontWeight: "bold"
                          }}
                        >
                          {formatARR(arrValue)}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "14px"
                  }}
                >
                  {filteredArrList.slice(3).map((agent, index) => {
                    const arrValue = agent[currentArrValueKey] || 0;

                    return (
                      <div
                        className="leaderboard-card"
                        key={`${arrTab}-${agent.displayRank}-${agent.name}`}
                        style={{
                          background: "rgba(15,23,42,0.92)",
                          border: "1px solid rgba(148,163,184,0.24)",
                          borderRadius: "18px",
                          padding: "18px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          animation: `rankSlideIn 0.35s ease-out ${
                            index * 0.04
                          }s both`,
                          transition: "0.2s ease"
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "16px"
                          }}
                        >
                          <div
                            style={{
                              width: "54px",
                              height: "54px",
                              borderRadius: "16px",
                              background: "rgba(37,99,235,0.25)",
                              color: "#38bdf8",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              fontSize: "18px",
                              border: "1px solid rgba(56,189,248,0.4)"
                            }}
                          >
                            #{agent.displayRank}
                          </div>

                          <div>
                            <h3
                              style={{
                                color: "#fff",
                                margin: 0,
                                fontSize: "20px"
                              }}
                            >
                              {agent.name}
                            </h3>
                            <p
                              style={{
                                color: "#94a3b8",
                                margin: "6px 0 0"
                              }}
                            >
                              {currentArrLabel}
                              {agent.team ? ` - ${agent.team}` : ""}
                            </p>
                          </div>
                        </div>

                        <strong
                          style={{
                            color: "#fff",
                            fontSize: "26px"
                          }}
                        >
                          {formatARR(arrValue)}
                        </strong>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
{canViewOperationalReports && showHistoricalReports && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.75)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 99999
    }}
  >
    <div
      style={{
        width: "90%",
        maxWidth: "1100px",
        maxHeight: "90vh",
        overflowY: "auto",
        background: "#0f172a",
        borderRadius: "24px",
        padding: "28px",
        border: "1px solid rgba(255,255,255,0.1)"
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px"
        }}
      >
        <div>
          <h2
            style={{
              color: "#fff",
              margin: 0
            }}
          >
            📊 Historical Break Reports
          </h2>

          <p
            style={{
              color: "#94a3b8",
              marginTop: "8px"
            }}
          >
            View and download past break reports for one day or a selected date range
          </p>
        </div>

        <button
          onClick={() => setShowHistoricalReports(false)}
          style={{
            background: "#ef4444",
            border: "none",
            color: "#fff",
            padding: "10px 14px",
            borderRadius: "10px",
            cursor: "pointer",
            fontWeight: "bold"
          }}
        >
          Close
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: "12px",
          marginBottom: "20px",
          flexWrap: "wrap"
        }}
      >
        <label
          style={{
            display: "grid",
            gap: "6px",
            color: "#cbd5e1",
            fontSize: "12px",
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.04em"
          }}
        >
          Start Date
          <input
            type="date"
            value={selectedReportStartDate}
            onChange={(e) => {
              setSelectedReportStartDate(e.target.value);
              setSelectedReportDate(e.target.value);
            }}
            style={{
              padding: "12px",
              borderRadius: "10px",
              border: "none",
              background: "#1e293b",
              color: "#fff"
            }}
          />
        </label>

        <label
          style={{
            display: "grid",
            gap: "6px",
            color: "#cbd5e1",
            fontSize: "12px",
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.04em"
          }}
        >
          End Date
          <input
            type="date"
            value={selectedReportEndDate}
            onChange={(e) => setSelectedReportEndDate(e.target.value)}
            style={{
              padding: "12px",
              borderRadius: "10px",
              border: "none",
              background: "#1e293b",
              color: "#fff"
            }}
          />
        </label>

        <button
          onClick={() =>
            fetchHistoricalReports(selectedReportStartDate, selectedReportEndDate)
          }
          style={{
            background: "#2563eb",
            color: "#fff",
            border: "none",
            padding: "12px 18px",
            borderRadius: "10px",
            cursor: "pointer",
            fontWeight: "bold"
          }}
        >
          Fetch Reports
        </button>

        <button
          onClick={downloadHistoricalReport}
          style={{
            background: "#16a34a",
            color: "#fff",
            border: "none",
            padding: "12px 18px",
            borderRadius: "10px",
            cursor: "pointer",
            fontWeight: "bold"
          }}
        >
          Download CSV
        </button>

        <button
          onClick={openHistoricalReportInGoogleSheets}
          disabled={historicalReports.length === 0}
          style={{
            background: "#0ea5e9",
            color: "#fff",
            border: "none",
            padding: "12px 18px",
            borderRadius: "10px",
            cursor: historicalReports.length === 0 ? "not-allowed" : "pointer",
            fontWeight: "bold",
            opacity: historicalReports.length === 0 ? 0.6 : 1
          }}
        >
          Open Google Sheet
        </button>
      </div>

      {reportsLoading ? (
        <p style={{ color: "#fff" }}>
          Loading reports...
        </p>
      ) : historicalReports.length === 0 ? (
        <p style={{ color: "#94a3b8" }}>
          No reports found
        </p>
      ) : (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse"
          }}
        >
          <thead>
            <tr
              style={{
                background: "#1e293b"
              }}
            >
              <th style={tableHeaderStyle}>Agent</th>
              <th style={tableHeaderStyle}>Date</th>
              <th style={tableHeaderStyle}>Shift</th>
              <th style={tableHeaderStyle}>Break Time</th>
              <th style={tableHeaderStyle}>Team / Allowed</th>
              <th style={tableHeaderStyle}>Breaks</th>
              <th style={tableHeaderStyle}>Exceeded</th>
              <th style={tableHeaderStyle}>Status</th>
            </tr>
          </thead>

          <tbody>
            {historicalReports.map((report) => (
              <tr
                key={report._id}
                style={{
                  borderBottom:
                    "1px solid rgba(255,255,255,0.08)"
                }}
              >
                <td style={tableCellStyle}>
                  {report.agentName}
                </td>

                <td style={tableCellStyle}>
                  {report.reportDate}
                </td>

                <td style={tableCellStyle}>
                  {report.shiftType}
                </td>

                <td style={tableCellStyle}>
                  {formatMilliseconds(
                    report.totalBreakTime
                  )}
                </td>

                <td style={tableCellStyle}>
                  {report.team || 'No Team'} / {formatMilliseconds(report.breakLimitMs)}
                  {report.limitSource === 'legacy-default' && <small> (legacy fallback)</small>}
                </td>

                <td style={tableCellStyle}>
                  {report.breakCount || 0}
                </td>

                <td style={tableCellStyle}>
                  {report.exceededBreaks || 0}
                </td>

                <td style={tableCellStyle}>
                  {report.status}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
)}
{ENABLE_DSR && canReviewDsr && showDsrReports && (
  <DsrReportsWorkspace
    reports={dsrReports}
    summary={dsrSummary}
    filters={dsrFilters}
    setFilters={setDsrFilters}
    loading={dsrLoading}
    error={dsrError}
    onClose={() => initialOpenDsr && onHome ? onHome() : setShowDsrReports(false)}
    closeLabel={initialOpenDsr ? "Back to Home" : "Back to Dashboard"}
    onFetchReports={fetchDsrReports}
    onResetFilters={resetDsrFilters}
    onDownloadCsv={downloadDsrCsv}
    approvals={dsrApprovals}
    approvalsLoading={dsrApprovalsLoading}
    approvalsError={dsrApprovalsError}
    reviewingApprovalId={reviewingDsrApprovalId}
    onReviewApproval={reviewDsrApprovalRequest}
    missingResult={missingDsrResult}
    missingLoading={missingDsrLoading}
    missingError={missingDsrError}
    onFetchMissing={fetchMissingDsrAgents}
    onDownloadMissingCsv={downloadMissingDsrCsv}
    selectedReportId={expandedDsrReportId}
    setSelectedReportId={setExpandedDsrReportId}
    deletingReportId={deletingDsrReportId}
    onDeleteReport={deleteDsrReport}
    canDeleteReports={canUseAdminControls}
    onSaveManagerReview={saveDsrManagerReview}
    savingManagerReviewId={savingDsrReviewId}
  />
)}
{false && ENABLE_DSR && canUseAdminControls && showDsrReports && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.78)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 99999
    }}
  >
    <div
      style={{
        width: "92%",
        maxWidth: "1180px",
        maxHeight: "90vh",
        overflowY: "auto",
        background: "linear-gradient(180deg, #0f172a, #111827)",
        borderRadius: "24px",
        padding: "28px",
        border: "1px solid rgba(56,189,248,0.25)",
        boxShadow: "0 28px 80px rgba(0,0,0,0.45)"
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
          marginBottom: "20px"
        }}
      >
        <div>
          <h2 style={{ color: "#fff", margin: 0 }}>Daily Sales Reports</h2>
          <p style={{ color: "#94a3b8", marginTop: "8px" }}>
            Review BDE, LGS, and CPA submissions by business date, role, or agent.
          </p>
        </div>

        <button
          onClick={() => setShowDsrReports(false)}
          style={{
            background: "#ef4444",
            border: "none",
            color: "#fff",
            padding: "10px 14px",
            borderRadius: "10px",
            cursor: "pointer",
            fontWeight: "bold"
          }}
        >
          Close
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "12px",
          marginBottom: "18px"
        }}
      >
        {[
          ["Reports", dsrSummary.totalReports || 0],
          ["BDE", dsrSummary.bdeReports || 0],
          ["LGS", dsrSummary.lgsReports || 0],
          ["CPA", dsrSummary.cpaReports || 0],
          ["Dials / Calls", dsrSummary.totalDials || 0],
          ["Connects", dsrSummary.totalConnects || 0],
          ["New Leads", dsrSummary.totalNewLeads || 0],
          ["ARR Closed", formatARR(dsrSummary.totalArrClosed || 0)],
          ["Deals", dsrSummary.totalDealsClosed || 0],
          ["Total CP", dsrSummary.totalCp || 0],
          ["ARR from CP", formatARR(dsrSummary.totalArrFromCp || 0)]
        ].map(([label, value]) => (
          <div
            key={label}
            style={{
              background: "rgba(30,41,59,0.9)",
              border: "1px solid rgba(148,163,184,0.18)",
              borderRadius: "16px",
              padding: "14px"
            }}
          >
            <span style={{ color: "#94a3b8", fontSize: "12px", fontWeight: 800 }}>
              {label}
            </span>
            <strong style={{ color: "#fff", display: "block", fontSize: "22px", marginTop: "6px" }}>
              {value}
            </strong>
          </div>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          gap: "12px",
          flexWrap: "wrap",
          marginBottom: "18px",
          alignItems: "end"
        }}
      >
        <label style={{ display: "grid", gap: "6px", color: "#cbd5e1", fontWeight: 800 }}>
          From
          <input
            type="date"
            value={dsrFilters.from}
            onChange={(event) =>
              setDsrFilters((current) => ({ ...current, from: event.target.value }))
            }
            style={{
              padding: "12px",
              borderRadius: "10px",
              border: "1px solid rgba(148,163,184,0.2)",
              background: "#1e293b",
              color: "#fff"
            }}
          />
        </label>

        <label style={{ display: "grid", gap: "6px", color: "#cbd5e1", fontWeight: 800 }}>
          To
          <input
            type="date"
            value={dsrFilters.to}
            onChange={(event) =>
              setDsrFilters((current) => ({ ...current, to: event.target.value }))
            }
            style={{
              padding: "12px",
              borderRadius: "10px",
              border: "1px solid rgba(148,163,184,0.2)",
              background: "#1e293b",
              color: "#fff"
            }}
          />
        </label>

        <label style={{ display: "grid", gap: "6px", color: "#cbd5e1", fontWeight: 800 }}>
          Role
          <select
            value={dsrFilters.role}
            onChange={(event) =>
              setDsrFilters((current) => ({ ...current, role: event.target.value }))
            }
            style={{
              padding: "12px",
              borderRadius: "10px",
              border: "1px solid rgba(148,163,184,0.2)",
              background: "#1e293b",
              color: "#fff"
            }}
          >
            <option value="all">All Roles</option>
            <option value="bde">BDE</option>
            <option value="lgs">LGS</option>
            <option value="sr_lgs">SR. LGS</option>
            <option value="sr_bde">SR. BDE</option>
            <option value="cpa">CPA</option>
            <option value="fs">FS</option>
          </select>
        </label>

        <label style={{ display: "grid", gap: "6px", color: "#cbd5e1", fontWeight: 800, minWidth: "220px" }}>
          Agent
          <input
            type="search"
            value={dsrFilters.agent}
            onChange={(event) =>
              setDsrFilters((current) => ({ ...current, agent: event.target.value }))
            }
            placeholder="Search agent..."
            style={{
              padding: "12px",
              borderRadius: "10px",
              border: "1px solid rgba(148,163,184,0.2)",
              background: "#1e293b",
              color: "#fff"
            }}
          />
        </label>

        <button
          onClick={() => fetchDsrReports()}
          disabled={dsrLoading}
          style={{
            background: "#2563eb",
            color: "#fff",
            border: "none",
            padding: "12px 18px",
            borderRadius: "10px",
            cursor: dsrLoading ? "wait" : "pointer",
            fontWeight: "bold"
          }}
        >
          {dsrLoading ? "Loading..." : "Fetch DSR"}
        </button>

        <button
          onClick={downloadDsrCsv}
          disabled={dsrReports.length === 0}
          style={{
            background: "#16a34a",
            color: "#fff",
            border: "none",
            padding: "12px 18px",
            borderRadius: "10px",
            cursor: dsrReports.length === 0 ? "not-allowed" : "pointer",
            fontWeight: "bold",
            opacity: dsrReports.length === 0 ? 0.55 : 1
          }}
        >
          Download CSV
        </button>
      </div>

      <div
        style={{
          background: "linear-gradient(135deg, rgba(245,158,11,0.13), rgba(15,23,42,0.78))",
          border: "1px solid rgba(245,158,11,0.3)",
          borderRadius: "18px",
          padding: "18px",
          marginBottom: "18px"
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "16px",
            flexWrap: "wrap",
            marginBottom: "14px"
          }}
        >
          <div>
            <h3 style={{ color: "#fff", margin: 0 }}>Pending DSR Approvals</h3>
            <p style={{ color: "#fcd34d", margin: "6px 0 0" }}>
              BDE DSRs below the dial target come here unless the agent closed a sale.
            </p>
          </div>
          <strong style={{ color: "#fde68a", alignSelf: "center" }}>
            {dsrApprovals.length} pending
          </strong>
        </div>

        {dsrApprovalsError && <p style={{ color: "#fca5a5", fontWeight: 800 }}>{dsrApprovalsError}</p>}

        {dsrApprovalsLoading ? (
          <p style={{ color: "#fff", margin: 0 }}>Loading pending approvals...</p>
        ) : dsrApprovals.length === 0 ? (
          <p style={{ color: "#94a3b8", margin: 0 }}>No DSR approvals pending for this filter.</p>
        ) : (
          <div className="dsr-admin-table-wrap">
            <table className="dsr-admin-table dsr-approval-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Agent</th>
                  <th>Role</th>
                  <th>Dials</th>
                  <th>Deals</th>
                  <th>ARR</th>
                  <th>Reason</th>
                  <th>Requested</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {dsrApprovals.map((request) => {
                  const isReviewing = reviewingDsrApprovalId === request.id;
                  return (
                    <tr key={request.id}>
                      <td>{request.businessDate}</td>
                      <td><strong>{request.agentName}</strong></td>
                      <td><span className={`dsr-role-pill ${request.dsrRole}`}>{formatDsrRoleLabel(request.dsrRole)}</span></td>
                      <td>{request.dials || 0}</td>
                      <td>{request.dealsClosed || 0}</td>
                      <td>{formatARR(request.arrClosed || 0)}</td>
                      <td>{request.requestReason || "Below DSR threshold"}</td>
                      <td>{request.requestedAt ? new Date(request.requestedAt).toLocaleString() : "-"}</td>
                      <td>
                        <div className="dsr-approval-actions">
                          <button
                            type="button"
                            className="dsr-approval-approve"
                            disabled={isReviewing}
                            onClick={() => {
                              const note = window.prompt("Approval reason is required before accepting this low-dial DSR.");
                              if (!note || !note.trim()) return;
                              reviewDsrApprovalRequest(request.id, "approve", note.trim());
                            }}
                          >
                            {isReviewing ? "Saving..." : "Approve"}
                          </button>
                          <button
                            type="button"
                            className="dsr-approval-reject"
                            disabled={isReviewing}
                            onClick={() => {
                              const note = window.prompt("Rejection reason (optional):") || "";
                              reviewDsrApprovalRequest(request.id, "reject", note.trim());
                            }}
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {dsrError && <p style={{ color: "#fca5a5", fontWeight: 800 }}>{dsrError}</p>}

      {dsrLoading ? (
        <p style={{ color: "#fff" }}>Loading DSR reports...</p>
      ) : dsrReports.length === 0 ? (
        <p style={{ color: "#94a3b8" }}>No DSR reports found for this filter.</p>
      ) : (
        <div className="dsr-admin-table-wrap">
          <table className="dsr-admin-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Agent</th>
                <th>Team</th>
                <th>Role</th>
                <th>Dials / Calls</th>
                <th>Connects / Inbound</th>
                <th>New Leads</th>
                <th>Deals / Callbacks</th>
                <th>CP ARR / Transfers</th>
                <th>ARR / Long Followup</th>
                <th>Review</th>
                <th>Submitted</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {dsrReports.map((report) => {
                const isLgsReport = report.dsrRole === "lgs" || report.dsrRole === "sr_lgs";
                const lgsInboundCalls = report.fields?.inboundCalls ?? report.connects ?? 0;
                const lgsCallBacks = report.fields?.callBacks ?? 0;
                const lgsLiveTransfers = report.fields?.liveTransfers ?? 0;
                const lgsLongFollowups = report.fields?.longFollowups ?? 0;

                return (
                  <tr key={report.id}>
                    <td>{report.businessDate}</td>
                    <td><strong>{report.agentName}</strong></td>
                    <td>{report.agentTeam || "-"}</td>
                    <td><span className={`dsr-role-pill ${report.dsrRole}`}>{formatDsrRoleLabel(report.dsrRole)}</span></td>
                    <td>{report.dials || 0}</td>
                    <td>{isLgsReport ? lgsInboundCalls : report.connects || 0}</td>
                    <td>{report.newLeads || 0}</td>
                    <td>{isLgsReport ? lgsCallBacks : report.dealsClosed || 0}</td>
                    <td>{isLgsReport ? lgsLiveTransfers : formatARR(report.fields?.arrFromCp || 0)}</td>
                    <td>{isLgsReport ? lgsLongFollowups : formatARR(report.arrClosed || 0)}</td>
                    <td>
                      {report.dsrRole === "bde" ? (
                        <span className={`dsr-review-status-badge ${report.managerReviewStatus === "reviewed" ? "reviewed" : "pending"}`}>
                          {report.managerReviewStatus === "reviewed" ? "Reviewed" : "Pending"}
                        </span>
                      ) : (
                        <span style={{ color: "#64748b" }}>-</span>
                      )}
                    </td>
                    <td>{report.submittedAt ? new Date(report.submittedAt).toLocaleString() : ""}</td>
                    <td>
                      <div className="dsr-action-buttons">
                        <button
                          type="button"
                          className="dsr-view-button"
                          onClick={() => setExpandedDsrReportId(report.id)}
                        >
                          View DSR
                        </button>
                        <button
                          type="button"
                          className="dsr-delete-button"
                          disabled={deletingDsrReportId === report.id}
                          onClick={() => deleteDsrReport(report)}
                        >
                          {deletingDsrReportId === report.id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <DsrReportDetail
        report={dsrReports.find((report) => report.id === expandedDsrReportId) || null}
        onClose={() => setExpandedDsrReportId("")}
        canManageManagerReview
        onSaveManagerReview={saveDsrManagerReview}
        savingManagerReview={savingDsrReviewId === expandedDsrReportId}
      />
    </div>
  </div>
)}
      {canUseAdminControls && deletingUser && (
        <DeleteUserDialog user={deletingUser} busy={deleteUserBusy} error={deleteUserError}
          onCancel={() => setDeletingUser(null)} onConfirm={deleteManagedUser} />
      )}
      {canUseAdminControls && showAgentManagement && (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(0,0,0,0.75)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999,
      backdropFilter: "blur(6px)"
    }}
  >
    <div
      style={{
        background: "linear-gradient(180deg, #16213e, #0f172a)",
        padding: "28px",
        borderRadius: "24px",
        width: "min(940px, calc(100vw - 32px))",
        maxHeight: "85vh",
        overflowY: "auto",
        border: "1px solid rgba(148,163,184,0.22)",
        boxShadow: "0 0 40px rgba(0,0,0,0.45)"
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px"
        }}
      >
        <div>
          <h2
            style={{
              color: "#fff",
              margin: 0,
              fontSize: "30px"
            }}
          >
            User Management
          </h2>

          <p
            style={{
              color: "#94a3b8",
              marginTop: "8px"
            }}
          >
            Add users, update access, and keep Zoom mapping in one place
          </p>
        </div>

        <button
          onClick={() => setShowAgentManagement(false)}
          style={{
            background: "#ef4444",
            color: "#fff",
            border: "none",
            padding: "10px 14px",
            borderRadius: "10px",
            cursor: "pointer",
            fontWeight: "bold"
          }}
        >
          Close
        </button>
      </div>

      {userManagementError && (
        <p className="zoom-error">{userManagementError}</p>
      )}

      {userManagementMessage && (
        <p
          style={{
            color: "#bbf7d0",
            background: "rgba(34,197,94,0.14)",
            border: "1px solid rgba(34,197,94,0.35)",
            borderRadius: "12px",
            padding: "10px 12px",
            margin: "0 0 12px"
          }}
        >
          {userManagementMessage}
        </p>
      )}

      <div
        style={{
          background: "rgba(15,23,42,0.78)",
          border: "1px solid rgba(96,165,250,0.22)",
          borderRadius: "18px",
          padding: "16px",
          marginBottom: "18px"
        }}
      >
        <h3 style={{ color: "#fff", marginTop: 0 }}>Add User</h3>

        <div className="zoom-email-row">
          <input
            type="text"
            placeholder="Name"
            value={newUserForm.name}
            onChange={(e) =>
              setNewUserForm((current) => ({
                ...current,
                name: e.target.value
              }))
            }
          />

          <input
            type="password"
            placeholder="Password"
            value={newUserForm.password}
            onChange={(e) =>
              setNewUserForm((current) => ({
                ...current,
                password: e.target.value
              }))
            }
          />

          <select
            value={newUserForm.role}
            onChange={(e) =>
              setNewUserForm((current) => ({
                ...current,
                role: e.target.value
              }))
            }
          >
            <option value="agent">Agent</option>
            <option value="admin">Admin</option>
            <option value="tv">TV Viewer</option>
            <option value="am">Assistant Manager (AM)</option>
            <option value="qa">Quality Auditor (QA)</option>
            <option value="sm">Senior Manager (SM)</option>
          </select>

          {["am", "qa", "sm"].includes(newUserForm.role) && (
            <input
              type="text"
              aria-label="Role assignment reason"
              placeholder="Reason for staff role assignment"
              value={newUserForm.roleReason}
              onChange={(e) => setNewUserForm((current) => ({ ...current, roleReason: e.target.value }))}
            />
          )}

          <select
            value={newUserForm.dsrRole}
            onChange={(e) =>
              setNewUserForm((current) => ({
                ...current,
                dsrRole: e.target.value
              }))
            }
            disabled={newUserForm.role !== "agent"}
            title="DSR / sales role"
          >
            {DSR_ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={newUserForm.team}
            onChange={(e) =>
              setNewUserForm((current) => ({
                ...current,
                team: e.target.value
              }))
            }
            disabled={newUserForm.role !== "agent"}
            title="Team"
          >
            <option value="">Select Team</option>
            {USER_TEAM_OPTIONS.map((team) => (
              <option key={team} value={team}>
                {team}
              </option>
            ))}
          </select>

          <select
            value={newUserForm.shiftType}
            onChange={(e) =>
              setNewUserForm((current) => ({
                ...current,
                shiftType: e.target.value
              }))
            }
          >
            <option value="regular">Regular Shift</option>
            <option value="early">Early Shift</option>
          </select>

          <select
            value={newUserForm.disabled ? "disabled" : "active"}
            onChange={(e) =>
              setNewUserForm((current) => ({
                ...current,
                disabled: e.target.value === "disabled"
              }))
            }
          >
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </select>

          <input
            type="email"
            placeholder="Zoom email"
            value={newUserForm.zoomEmail}
            onChange={(e) =>
              setNewUserForm((current) => ({
                ...current,
                zoomEmail: e.target.value
              }))
            }
          />

          <input
            type="text"
            placeholder="Extension"
            value={newUserForm.zoomExtension}
            onChange={(e) =>
              setNewUserForm((current) => ({
                ...current,
                zoomExtension: e.target.value
              }))
            }
          />

          <button
            onClick={createManagedUser}
            disabled={userManagementLoading}
          >
            Add User
          </button>
        </div>
      </div>

      {managedUsers.length === 0 ? (
        <div
          style={{
            background: "#0f172a",
            padding: "30px",
            borderRadius: "18px",
            textAlign: "center",
            color: "#94a3b8",
            border: "1px solid rgba(255,255,255,0.08)"
          }}
        >
          {userManagementLoading ? "Loading users..." : "No users found"}
        </div>
      ) : (
        managedUsers.map((agent, index) => {
          const isDisabled = Boolean(
            userDrafts[agent._id]?.disabled ?? agent.disabled
          );
          const statusLabel = isDisabled ? "Disabled" : agent.status;

          return (
          <div
            key={agent._id}
            style={{
              background:
                isDisabled
                  ? "rgba(100,116,139,0.16)"
                  : agent.status === "Break"
                  ? "rgba(239,68,68,0.12)"
                  : agent.status === "On Call"
                  ? "rgba(59,130,246,0.12)"
                  : "rgba(34,197,94,0.12)",
              padding: "18px",
              borderRadius: "18px",
              marginBottom: "14px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              border:
                isDisabled
                  ? "1px solid rgba(148,163,184,0.35)"
                  : agent.status === "Break"
                  ? "1px solid rgba(239,68,68,0.45)"
                  : agent.status === "On Call"
                  ? "1px solid rgba(59,130,246,0.45)"
                  : "1px solid rgba(34,197,94,0.45)",
              animation: `rankSlideIn 0.3s ease ${index * 0.04}s both`
            }}
          >
            <div>
              <h3
                style={{
                  color: "#fff",
                  margin: 0,
                  fontSize: "22px"
                }}
              >
                {agent.name}
              </h3>

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  marginTop: "10px",
                  flexWrap: "wrap"
                }}
              >
                <span
                  style={{
                    background:
                      isDisabled
                        ? "#64748b"
                        : agent.status === "Break"
                        ? "#ef4444"
                        : agent.status === "On Call"
                        ? "#2563eb"
                        : "#16a34a",
                    color: "#fff",
                    padding: "6px 12px",
                    borderRadius: "999px",
                    fontSize: "12px",
                    fontWeight: "bold"
                  }}
                >
                  {statusLabel}
                </span>

                <span
                  style={{
                    background: "#334155",
                    color: "#fff",
                    padding: "6px 12px",
                    borderRadius: "999px",
                    fontSize: "12px",
                    fontWeight: "bold"
                  }}
                >
                  {ACCOUNT_ROLE_LABELS[agent.role] || agent.role || "Agent"}
                </span>

                {agent.role === "agent" && (
                  <span
                    style={{
                      background: "rgba(14,165,233,0.22)",
                      color: "#bae6fd",
                      padding: "6px 12px",
                      borderRadius: "999px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      border: "1px solid rgba(56,189,248,0.35)"
                    }}
                  >
                    {formatDsrRoleLabel(agent.dsrRole)}
                  </span>
                )}

                {agent.role === "agent" && agent.team && (
                  <span
                    style={{
                      background: "rgba(34,197,94,0.18)",
                      color: "#bbf7d0",
                      padding: "6px 12px",
                      borderRadius: "999px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      border: "1px solid rgba(34,197,94,0.35)"
                    }}
                  >
                    {agent.team}
                  </span>
                )}
              </div>

              <div className="zoom-email-row">
                <input
                  type="text"
                  placeholder="Name"
                  value={userDrafts[agent._id]?.name ?? agent.name ?? ""}
                  onChange={(e) =>
                    updateUserDraft(agent._id, "name", e.target.value)
                  }
                />

                <select
                  value={userDrafts[agent._id]?.role ?? agent.role ?? "agent"}
                  onChange={(e) =>
                    updateUserDraft(agent._id, "role", e.target.value)
                  }
                >
                  <option value="agent">Agent</option>
                  <option value="admin">Admin</option>
                  <option value="tv">TV Viewer</option>
                  <option value="am">Assistant Manager (AM)</option>
                  <option value="qa">Quality Auditor (QA)</option>
                  <option value="sm">Senior Manager (SM)</option>
                </select>

                {userDrafts[agent._id]?.role !== agent.role && (
                  <input
                    type="text"
                    aria-label={`Role change reason for ${agent.name}`}
                    placeholder="Reason for role change"
                    value={userDrafts[agent._id]?.roleReason || ""}
                    onChange={(e) => updateUserDraft(agent._id, "roleReason", e.target.value)}
                  />
                )}

                <select
                  value={userDrafts[agent._id]?.dsrRole ?? agent.dsrRole ?? "BDE"}
                  onChange={(e) =>
                    updateUserDraft(agent._id, "dsrRole", e.target.value)
                  }
                  disabled={(userDrafts[agent._id]?.role ?? agent.role ?? "agent") !== "agent"}
                  title="DSR / sales role"
                >
                  {DSR_ROLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <select
                  value={userDrafts[agent._id]?.team ?? agent.team ?? ""}
                  onChange={(e) =>
                    updateUserDraft(agent._id, "team", e.target.value)
                  }
                  disabled={(userDrafts[agent._id]?.role ?? agent.role ?? "agent") !== "agent"}
                  title="Team"
                >
                  <option value="">No Team</option>
                  {USER_TEAM_OPTIONS.map((team) => (
                    <option key={team} value={team}>
                      {team}
                    </option>
                  ))}
                </select>

                <input
                  type="password"
                  placeholder="New password"
                  value={userDrafts[agent._id]?.password || ""}
                  onChange={(e) =>
                    updateUserDraft(agent._id, "password", e.target.value)
                  }
                />

                <select
                  value={
                    userDrafts[agent._id]?.shiftType ??
                    agent.shiftType ??
                    "regular"
                  }
                  onChange={(e) =>
                    updateUserDraft(agent._id, "shiftType", e.target.value)
                  }
                >
                  <option value="regular">Regular Shift</option>
                  <option value="early">Early Shift</option>
                </select>

                <select
                  value={isDisabled ? "disabled" : "active"}
                  onChange={(e) =>
                    updateUserDraft(
                      agent._id,
                      "disabled",
                      e.target.value === "disabled"
                    )
                  }
                >
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>

              <div className="zoom-email-row">
                <input
                  type="email"
                  placeholder="Zoom email"
                  value={
                    userDrafts[agent._id]?.zoomEmail ??
                    agent.zoomEmail ??
                    ""
                  }
                  onChange={(e) =>
                    updateUserDraft(agent._id, "zoomEmail", e.target.value)
                  }
                />

                <input
                  type="text"
                  placeholder="Extension"
                  value={
                    userDrafts[agent._id]?.zoomExtension ??
                    agent.zoomExtension ??
                    ""
                  }
                  onChange={(e) =>
                    updateUserDraft(agent._id, "zoomExtension", e.target.value)
                  }
                />

                <button
                  onClick={() => saveManagedUser(agent._id)}
                  disabled={userManagementLoading}
                >
                  Save User
                </button>
                <button type="button" onClick={() => fetchRoleAudit(agent)} disabled={userManagementLoading}>
                  Role History
                </button>
              </div>
              {roleAudit?.userId === agent._id && (
                <section className="quarter-detail" aria-label={`Role history for ${agent.name}`}>
                  {roleAudit.loading ? <p role="status">Loading role history...</p> : roleAudit.error ? <p role="alert">{roleAudit.error}</p> : roleAudit.rows.length ? (
                    <ol>{roleAudit.rows.map((entry) => (
                      <li key={entry.id}>
                        <strong>{entry.old_role || "New account"} to {entry.new_role}</strong>
                        <span>{entry.actor_name} / {entry.changed_at}</span>
                        <p>{entry.reason}</p>
                      </li>
                    ))}</ol>
                  ) : <p>No staff role changes recorded.</p>}
                </section>
              )}
            </div>

            <button
              onClick={() =>
                saveManagedUser(agent._id, {
                  ...(userDrafts[agent._id] || {}),
                  disabled: !isDisabled
                })
              }
              style={{
                background: isDisabled ? "#16a34a" : "#ef4444",
                color: "#fff",
                border: "none",
                padding: "10px 16px",
                borderRadius: "12px",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "14px"
              }}
            >
              {isDisabled ? "Enable User" : "Disable User"}
            </button>
            <details className="user-extra-actions">
              <summary>Actions</summary>
              <button type="button" className="user-delete-action" disabled={userManagementLoading || deleteUserBusy}
                onClick={() => { setDeleteUserError(""); setDeletingUser(agent); }}>
                Delete User
              </button>
            </details>
          </div>
          );
        })
      )}
    </div>
  </div>
)}
{canUseAdminControls && showShiftManagement && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.75)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 99999
    }}
  >
    <div
      style={{
        width: "700px",
        maxHeight: "85vh",
        overflowY: "auto",
        background: "#0f172a",
        borderRadius: "24px",
        padding: "28px",
        border: "1px solid rgba(255,255,255,0.1)"
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px"
        }}
      >
        <div>
          <h2
            style={{
              color: "#fff",
              margin: 0
            }}
          >
            🌅 Early Shift Management
          </h2>

          <p
            style={{
              color: "#94a3b8",
              marginTop: "8px"
            }}
          >
            Manage agents working in early PST shift
          </p>
        </div>

        <button
          onClick={() => setShowShiftManagement(false)}
          style={{
            background: "#ef4444",
            border: "none",
            color: "#fff",
            padding: "10px 14px",
            borderRadius: "10px",
            cursor: "pointer",
            fontWeight: "bold"
          }}
        >
          Close
        </button>
      </div>

      <div
        style={{
          background: "rgba(56,189,248,0.08)",
          padding: "16px",
          borderRadius: "16px",
          marginBottom: "20px",
          border: "1px solid rgba(56,189,248,0.25)"
        }}
      >
        <h3
          style={{
            color: "#38bdf8",
            marginTop: 0
          }}
        >
          Current Early Shift Agents
        </h3>

        {earlyShiftAgents.length === 0 ? (
          <p style={{ color: "#fff" }}>
            No agents assigned
          </p>
        ) : (
          earlyShiftAgents.map((agent) => (
            <div
              key={agent._id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#111827",
                padding: "12px",
                borderRadius: "12px",
                marginBottom: "10px"
              }}
            >
              <span
                style={{
                  color: "#fff",
                  fontWeight: "bold"
                }}
              >
                {agent.name}
              </span>

              <button
                onClick={() =>
                  updateShiftType(agent._id, "regular")
                }
                style={{
                  background: "#ef4444",
                  border: "none",
                  color: "#fff",
                  padding: "8px 12px",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                Remove
              </button>
            </div>
          ))
        )}
      </div>

      <div>
        <h3 style={{ color: "#fff" }}>
          Add Agents To Early Shift
        </h3>

        {agents
          .filter((agent) => agent.shiftType !== "early")
          .map((agent) => (
            <div
              key={agent._id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#111827",
                padding: "14px",
                borderRadius: "14px",
                marginBottom: "12px",
                border: "1px solid rgba(255,255,255,0.08)"
              }}
            >
              <div>
                <h4
                  style={{
                    color: "#fff",
                    margin: 0
                  }}
                >
                  {agent.name}
                </h4>

                <p
                  style={{
                    color: "#94a3b8",
                    marginTop: "6px"
                  }}
                >
                  {agent.status}
                </p>
              </div>

              <button
                onClick={() =>
                  updateShiftType(agent._id, "early")
                }
                style={{
                  background: "#2563eb",
                  border: "none",
                  color: "#fff",
                  padding: "10px 14px",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                Add
              </button>
            </div>
          ))}
      </div>
    </div>
  </div>
)}
      <div className="header sales-page-header">
        <div className="dashboard-page-identity">
          {onHome && !initialOpenDsr && (
            <nav className="dashboard-home-navigation" aria-label="Workspace navigation">
              <button className="dashboard-home-nav-button" type="button" onClick={onHome}>
                <ArrowLeft size={18} aria-hidden="true" /> Home
              </button>
            </nav>
          )}
          <h1>
            {isReadOnlyViewer
              ? "Sales Floor TV Dashboard"
              : "Sales Floor Live Dashboard"}
          </h1>
          <p className="subtitle">
            {isReadOnlyViewer
              ? "Read-only floor display"
              : "Real-time agent status monitoring"}
          </p>
        </div>

        <div className="live-clock">
          <strong>
            {currentTime.toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric"
            })}
          </strong>

          <span>
            {currentTime.toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit"
            })}
          </span>
        </div>

        <div className="dashboard-header-actions">
          {operationalManagerMode && !isTvMode && (
            <>
              <button className="logout-btn" onClick={downloadBreakReport}>Download Report</button>
              <button className="logout-btn" onClick={() => { setShowHistoricalReports(true); fetchHistoricalReports(selectedReportStartDate, selectedReportEndDate); }}>Sales History</button>
              {logout && <button className="logout-btn" onClick={logout}>Logout</button>}
            </>
          )}
          {isReadOnlyViewer && (
            <>
              <span className="tv-viewer-badge">TV Viewer</span>
              <button
                className="logout-btn"
                onClick={() => {
                  if (logout) {
                    logout();
                    return;
                  }

                  localStorage.clear();
                  window.location.reload();
                }}
              >
                Logout
              </button>
            </>
          )}

          {!isTvMode && canUseAdminControls && (
            <>
              <button className="logout-btn" onClick={downloadBreakReport}>
                Download Report
              </button>

              <button
                className="logout-btn"
                onClick={openUserManagement}
              >
                User Management
              </button>

              <button
                className="logout-btn"
                onClick={() => setShowShiftManagement(true)}
              >
                Shift Management
              </button>

              <button
                className="logout-btn"
                onClick={() => setShowIdlePopup(!showIdlePopup)}
              >
                {showIdlePopup ? "Disable Idle Popup" : "Enable Idle Popup"}
              </button>

              <button
                className="logout-btn"
                onClick={() => {
                  setShowHistoricalReports(true);
                  fetchHistoricalReports(selectedReportStartDate, selectedReportEndDate);
                }}
              >
                Historical Reports
              </button>

              <button
                className="logout-btn"
                onClick={() => {
                  setShowArrLeaderboard(true);
                  fetchArrData(selectedMonth, selectedYear);
                }}
              >
                ARR Leaderboard
              </button>

              <button className="logout-btn" onClick={toggleTvMode}>
                TV Mode
              </button>

              <button
                className="logout-btn"
                onClick={() => {
                  if (logout) {
                    logout();
                    return;
                  }

                  localStorage.clear();
                  window.location.reload();
                }}
              >
                Logout
              </button>
            </>
          )}
        </div>
      </div>

      <div className={`dashboard-control-area ${canUseAdminControls && !isTvMode ? 'with-break-settings' : ''}`}>
        <DashboardControls search={agentSearch} onSearch={setAgentSearch}
          activeView={activeFloorView} onView={focusFloorView}
          counts={{ all: filteredDisplayAgents.length, available: availableAgents.length,
            break: breakAgents.length, meeting: meetingAgents.length,
            'logged-out': loggedOutAgents.length, exceeded: breakExceededAgents.length }}
          canManage={canUseAdminControls && !isTvMode}
          onViewAll={() => { setAgentSearch(''); focusFloorView('all'); }}
          onDownload={downloadBreakReport} onManageUsers={openUserManagement}
          onManageShifts={() => setShowShiftManagement(true)}
          notificationCount={notificationItems.length} notificationsOpen={showNotificationCenter}
          onNotifications={() => setShowNotificationCenter((current) => !current)} />
        {canUseAdminControls && !isTvMode && (
          <BreakSettingsEditor settings={breakSettings} onSave={saveBreakLimit}
            saving={breakLimitSaving} feedback={breakLimitFeedback} />
        )}
      </div>


      {isMobileViewport && !isTvMode && (
        <div className="mobile-admin-dock">
          <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            Top
          </button>
          <button
            type="button"
            onClick={() =>
              document
                .querySelector(".tv-grid")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
          >
            Live
          </button>
          <button
            type="button"
            onClick={() =>
              document
                .querySelector(".analytics-center-panel")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
          >
            Analytics
          </button>
          <button type="button" onClick={downloadDailySnapshotReport}>
            Snapshot
          </button>
        </div>
      )}

      {agentSearchTerm && (
        <p className="dashboard-filter-note">
          Showing {filteredDisplayAgents.length} of {displayAgents.length} agents
          matching "{agentSearch}".
        </p>
      )}

      {showNotificationCenter && (
        <div className="notification-center">
          <div className="notification-center-header">
            <div>
              <h2>Notification Center</h2>
              <p>Live admin alerts from the dashboard.</p>
            </div>
            <button
              type="button"
              className="secondary-action"
              onClick={() => setShowNotificationCenter(false)}
            >
              Close
            </button>
          </div>

          {notificationItems.length === 0 ? (
            <p className="empty">No active dashboard alerts.</p>
          ) : (
            <div className="notification-list">
              {notificationItems.map((item, index) => (
                <div
                  className={`notification-row ${item.tone}`}
                  key={`${item.type}-${index}`}
                >
                  <span>{item.type}</span>
                  <div>
                    <strong>{item.title}</strong>
                    <p>{item.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="summary-grid live-summary-grid">
        <div className="summary-card metric-card available-card">
          <h3>Available</h3>
          <p>{availableAgents.length}</p>
        </div>

        <div className="summary-card metric-card break-card">
          <h3>On Break</h3>
          <p>{breakAgents.length}</p>
        </div>

        <div className="summary-card metric-card meeting-card">
          <h3>Meeting</h3>
          <p>{meetingAgents.length}</p>
        </div>

        <button
          type="button"
          className={`summary-card metric-card muted-card clickable-summary-card ${
            showLoggedOutAgents ? "active" : ""
          }`}
          onClick={() => setShowLoggedOutAgents((current) => !current)}
          aria-expanded={showLoggedOutAgents}
        >
          <h3>Logged Out</h3>
          <p>{loggedOutAgents.length}</p>
        </button>

        <div
          className={
            breakExceededAgents.length > 0
              ? "summary-card metric-card danger flash-alert"
              : "summary-card metric-card danger"
          }
        >
          <h3>Break Exceeded</h3>
          <p>{breakExceededAgents.length}</p>
        </div>
      </div>

      {showLoggedOutAgents && (
        <div className="logged-out-detail-panel" id="floor-logged-out" tabIndex={-1}>
          <div className="logged-out-detail-header">
            <div>
              <h2>Logged Out Agents</h2>
              <p>
                {sortedLoggedOutAgents.length} portal
                {sortedLoggedOutAgents.length === 1 ? " is" : "s are"} logged out.
              </p>
            </div>
            <button
              type="button"
              className="secondary-action small"
              onClick={() => setShowLoggedOutAgents(false)}
            >
              Hide
            </button>
          </div>

          {sortedLoggedOutAgents.length === 0 ? (
            <p className="empty">No logged out agents right now.</p>
          ) : (
            <div className="logged-out-agent-grid">
              {sortedLoggedOutAgents.map((agent) => (
                <div
                  className="logged-out-agent-pill"
                  key={agent._id || agent.name}
                >
                  <strong>{agent.name}</strong>
                  <span>{agent.role || "Agent"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="summary-grid arr-top-summary">
        <div className="summary-card metric-card arr-card">
          <h3>Total ARR</h3>
          <p>{formatARR(arrData.summary.totalARR)}</p>
        </div>

        <div className="summary-card metric-card arr-card">
          <h3>Outbound ARR</h3>
          <p>{formatARR(arrData.summary.outboundARR)}</p>
        </div>

        <div className="summary-card metric-card arr-card">
          <h3>Top Outbound</h3>
          <p className="arr-person-name">
            {arrData.summary.topOutboundPerformer?.name || "N/A"}
          </p>
        </div>

        <div className="summary-card metric-card arr-card">
          <h3>Top Inbound</h3>
          <p className="arr-person-name">
            {arrData.summary.topInboundPerformer?.name || "N/A"}
          </p>
        </div>

        <div className="summary-card metric-card senior-card">
          <h3>Top Sr BDE Outbound</h3>
          <p className="arr-person-name">
            {arrData.summary.topSrBdeOutboundPerformer?.name || "N/A"}
          </p>
        </div>

        <div className="summary-card metric-card senior-card">
          <h3>Top Sr BDE Inbound</h3>
          <p className="arr-person-name">
            {arrData.summary.topSrBdeInboundPerformer?.name || "N/A"}
          </p>
        </div>
      </div>

      {canUseAdminControls &&
        (showPasswordRequests || passwordResetRequests.length > 0) && (
        <div className="panel" style={{ marginTop: "20px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              marginBottom: "12px"
            }}
          >
            <div>
              <h2>Password Requests</h2>
              <p className="empty" style={{ margin: "4px 0 0" }}>
                {passwordResetRequests.length} pending
              </p>
            </div>

            <button
              className="logout-btn"
              onClick={fetchPasswordResetRequests}
              disabled={passwordResetLoading}
            >
              Refresh
            </button>
          </div>

          {passwordResetError && (
            <p className="zoom-error">{passwordResetError}</p>
          )}

          {passwordResetRequests.length === 0 ? (
            <p className="empty">No pending password requests</p>
          ) : (
            passwordResetRequests.map((request) => (
              <div
                className="agent-card"
                key={request._id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "14px"
                }}
              >
                <div>
                  <h3>{request.agentName}</h3>
                  <p>
                    Requested{" "}
                    {new Date(request.createdAt).toLocaleString("en-IN")}
                  </p>
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    className="logout-btn"
                    onClick={() =>
                      reviewPasswordResetRequest(request._id, "approve")
                    }
                    disabled={passwordResetLoading}
                    style={{ background: "#16a34a" }}
                  >
                    Approve
                  </button>

                  <button
                    className="logout-btn"
                    onClick={() =>
                      reviewPasswordResetRequest(request._id, "reject")
                    }
                    disabled={passwordResetLoading}
                    style={{ background: "#dc2626" }}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {canUseAdminControls && (adminStatusError || adminStatusMessage) && (
        <div
          className={
            adminStatusError
              ? "admin-status-feedback error"
              : "admin-status-feedback"
          }
        >
          {adminStatusError || adminStatusMessage}
        </div>
      )}

      <div className="tv-grid" id="floor-all" tabIndex={-1}>
        <div className="panel dial-count-panel" style={{ display: "none" }}>
          <div className="zoom-dial-header">
            <div>
              <h2>Dial Count</h2>
              <p>
                Zoom outbound calls - {zoomCallSummary.summary.siteName}
              </p>
              <span className="panel-sync-note">
                {getZoomSourceLabel(zoomCallSummary.summary.source)} - Last sync{" "}
                {formatSyncTime(zoomLastSyncedAt)}
              </span>
            </div>

            <div className="zoom-dial-actions">
              <label>
                Call date
              </label>

              <input
                type="date"
                value={zoomDate}
                onChange={(e) => setZoomDate(e.target.value)}
              />

              <button onClick={() => fetchZoomCallSummary(true)} disabled={zoomLoading}>
                {zoomLoading ? "Syncing" : "Refresh"}
              </button>

              <button
                type="button"
                className="secondary-action"
                onClick={() => setShowZoomDiagnostics((current) => !current)}
              >
                Diagnostics
              </button>
            </div>
          </div>

          {zoomError && (
            <p className="zoom-error">{zoomError}</p>
          )}

          {!zoomError && zoomCallSummary.summary.warning && (
            <p className="zoom-warning">{zoomCallSummary.summary.warning}</p>
          )}

          {zoomAvailabilityError && (
            <p className="zoom-error">{zoomAvailabilityError}</p>
          )}

          {showZoomDiagnostics && (
            <div className="zoom-diagnostics">
              <div>
                <span>Total outbound</span>
                <strong>
                  {formatCompactNumber(
                    zoomCallSummary.summary.totalOutboundCalls
                  )}
                </strong>
              </div>
              <div>
                <span>Mapped agents</span>
                <strong>{zoomMappedAgentCount}</strong>
              </div>
              <div>
                <span>Total talk</span>
                <strong>
                  {formatSeconds(zoomCallSummary.summary.totalTalkSeconds)}
                </strong>
              </div>
              <div>
                <span>Unmatched Zoom rows</span>
                <strong>{zoomUnmatchedAgents.length}</strong>
              </div>
              <div>
                <span>Availability sync</span>
                <strong>{formatSyncTime(zoomAvailabilityLastSyncedAt)}</strong>
              </div>
            </div>
          )}

          {dialCountDisplayAgents.length === 0 ? (
            <p className="empty">No agents found</p>
          ) : (
            <div className="status-list compact">
              {dialCountDisplayAgents.map((agent) => {
              const zoomStats = zoomStatsByAgentId.get(agent._id);
              const dialCount = zoomStats?.totalCalls ?? agent.dialCount ?? 0;

              return (
      <div className={`agent-card ${getStatusClass(agent.status)}`} key={agent._id}>
  <div>
    <h3>
      {agent.name}

      {agent.status === "Meeting" && (
        <span
          className="warning-badge"
          style={{
            background: "#7c3aed",
            marginLeft: "8px"
          }}
        >
          MEETING
        </span>
      )}

      {agent.status === "Break" && isBreakExceeded(agent, dailyBreakLimitMs) && (
        <span
          className="warning-badge"
          style={{
            marginLeft: "8px"
          }}
        >
          EXCEEDED
        </span>
      )}
    </h3>

    <p>
      Total Dials

      <span
        style={{
          marginLeft: "8px",
          color:
            agent.status === "Meeting"
              ? "#a78bfa"
              : agent.status === "On Call"
              ? "#38bdf8"
              : agent.status === "Break"
              ? "#ef4444"
              : "#22c55e",
          fontWeight: "bold"
        }}
      >
        • {agent.statusLabel || agent.status}
      </span>
    </p>

  </div>

  <strong>{formatCompactNumber(dialCount)}</strong>
</div>
              );
            })}
            </div>
          )}
        </div>

        <div className="panel available status-panel" id="floor-available" tabIndex={-1}>
          <div className="panel-heading">
            <div>
              <h2>Available</h2>
              <span>Ready agents</span>
            </div>
            <strong>{availableAgents.length}</strong>
          </div>

          {availableAgents.length === 0 ? (
            <p className="empty">No available agents</p>
          ) : (
            <div className="status-list">
              {availableAgents.map((agent) => (
                <div className="agent-card status-available" key={agent._id}>
                  <div>
                    <h3>{agent.name}</h3>
                    <p>
                      {agent.statusLabel || "Available"} -{" "}
                      {formatTimer(agent.statusStartTime)}
                    </p>
                  </div>

                  {canUseAdminControls && (
                    <div className="admin-status-actions">
                      <button
                        type="button"
                        className="admin-status-btn bio"
                        disabled={Boolean(adminStatusLoadingId)}
                        onClick={() =>
                          adminUpdateAgentStatus(agent, "Break", "Bio Break")
                        }
                      >
                        Bio
                      </button>

                      <button
                        type="button"
                        className="admin-status-btn main"
                        disabled={Boolean(adminStatusLoadingId)}
                        onClick={() =>
                          adminUpdateAgentStatus(agent, "Break", "Main Break")
                        }
                      >
                        Main
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel break status-panel" id="floor-break" tabIndex={-1}>
          <div className="panel-heading">
            <div>
              <h2>On Break</h2>
              <span>{breakExceededAgents.length} exceeded</span>
            </div>
            <strong>{breakAgents.length}</strong>
          </div>

          {breakAgents.length === 0 ? (
            <p className="empty">No agents on break</p>
          ) : (
            <div className="status-list">
              {breakAgents.map((agent) => {
                const exceeded = isBreakExceeded(agent, dailyBreakLimitMs);
                const totalBreakToday = getLiveDailyBreak(agent);

                return (
                  <div
                    className={
                      exceeded
                        ? "agent-card danger-card flash-alert"
                        : "agent-card status-break"
                    }
                    key={agent._id}
                  >
                    <div>
                      <h3>
                        {agent.name}{" "}
                        {exceeded && (
                          <span className="warning-badge">EXCEEDED</span>
                        )}
                      </h3>

                      <p>
                        {exceeded ? "Total today" : agent.breakType || "Break"} -{" "}
                        {exceeded
                          ? formatMilliseconds(totalBreakToday)
                          : formatTodayTimer(agent.statusStartTime)}
                        {exceeded && (
                          <span className="break-card-detail">
                            {agent.breakType || "Break"} current -{" "}
                            {formatTodayTimer(agent.statusStartTime)}
                          </span>
                        )}
                      </p>
                      <p className="break-card-detail">{agent.team || 'No Team'} - Allowed {formatMilliseconds(getBreakLimit(agent, dailyBreakLimitMs))}
                        {exceeded && ` - Exceeded by ${formatMilliseconds(totalBreakToday - getBreakLimit(agent, dailyBreakLimitMs))}`}</p>
                    </div>

                    {canUseAdminControls && (
                      <div className="admin-status-actions">
                        <button
                          type="button"
                          className="admin-status-btn back"
                          disabled={Boolean(adminStatusLoadingId)}
                          onClick={() =>
                            adminUpdateAgentStatus(agent, "Available")
                          }
                        >
                          Back
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="panel meeting status-panel" id="floor-meeting" tabIndex={-1}>
          <div className="panel-heading">
            <div>
              <h2>Meeting</h2>
              <span>Agents currently in meeting</span>
            </div>
            <strong>{meetingAgents.length}</strong>
          </div>

          {meetingAgents.length === 0 ? (
            <p className="empty">No agents in meeting</p>
          ) : (
            <div className="status-list">
              {meetingAgents.map((agent) => (
                <div className="agent-card status-meeting" key={agent._id}>
                  <div>
                    <h3>{agent.name}</h3>
                    <p>
                      {agent.statusLabel || "Meeting"} -{" "}
                      {formatTimer(agent.statusStartTime)}
                    </p>
                  </div>

                  {canUseAdminControls && (
                    <div className="admin-status-actions">
                      <button
                        type="button"
                        className="admin-status-btn back"
                        disabled={Boolean(adminStatusLoadingId)}
                        onClick={() =>
                          adminUpdateAgentStatus(agent, "Available")
                        }
                      >
                        Back
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
<div className="history-section">
        {canUseAdminControls && (
          <button
            className="history-toggle"
            onClick={() => setShowBreakSummary(!showBreakSummary)}
          >
            {showBreakSummary
              ? "Hide Daily Break Summary"
              : "Show Daily Break Summary"}
          </button>
        )}
      <div className="panel exceeded-panel" id="floor-exceeded" tabIndex={-1}>
        <h2>Break Exceeded</h2>

        {breakExceededAgents.length === 0 ? (
          <p className="empty">No agents exceeded their daily break limit</p>
        ) : (
          <div className="status-list compact exceeded-list">
            {breakExceededAgents.map((agent) => {
              const totalBreakToday = getLiveDailyBreak(agent);

              return (
                <div className="agent-card danger-card flash-alert" key={agent._id}>
                  <div>
                    <h3>
                      {agent.name} <span className="warning-badge">ALERT</span>
                    </h3>

                    <p>
                      Total today - {formatMilliseconds(totalBreakToday)}
                      <span className="break-card-detail">{agent.team || 'No Team'} - Allowed {formatMilliseconds(getBreakLimit(agent, dailyBreakLimitMs))}
                        {` - Exceeded by ${formatMilliseconds(totalBreakToday - getBreakLimit(agent, dailyBreakLimitMs))}`}</span>
                      <span className="break-card-detail">
                        {agent.breakType || "Break"} current -{" "}
                        {formatTodayTimer(agent.statusStartTime)}
                      </span>
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>


        {canUseAdminControls && showBreakSummary && (
          <div className="panel">
            <h2>Daily Break Summary</h2>

            <table>
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Total Break Today</th>
                  <th>Current Status</th>
                </tr>
              </thead>

              <tbody>
                {agents.map((agent) => (
                  <tr key={agent._id}>
                    <td>{agent.name}</td>
                    <td>{formatMilliseconds(getLiveDailyBreak(agent))}</td>
                    <td>{agent.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

     <div className="panel analytics-center-panel">
  <div className="analytics-center-header">
    <div>
      <h2 style={{ marginBottom: "8px" }}>
        📊 Sales Analytics Center
      </h2>

      <p style={{ color: "#94a3b8", margin: 0 }}>
        Native sales insights replacing Looker dashboard
      </p>
    </div>

    <div className="analytics-tab-list">
      {visibleAnalyticsTabs.map((tab) => (
        <button
          key={tab.key}
          onClick={() => setSalesAnalyticsTab(tab.key)}
          className={`analytics-tab-button ${
            salesAnalyticsTab === tab.key ? "active" : ""
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  </div>

  {salesAnalyticsTab === "arr" && (
    <div>
      <div className="analytics-summary-grid">
        <div className="summary-card">
          <h3>Current Year ARR</h3>
          <p>{formatARR(arrData.summary.totalARR)}</p>
        </div>

        <div className="summary-card">
          <h3>Outbound ARR</h3>
          <p>{formatARR(arrData.summary.outboundARR)}</p>
        </div>

        <div className="summary-card">
          <h3>Inbound ARR</h3>
          <p>{formatARR(arrData.summary.inboundARR)}</p>
        </div>

        <div className="summary-card">
          <h3>Top Outbound</h3>
          <p style={{ fontSize: "24px" }}>
            {arrData.summary.topOutboundPerformer?.name || "N/A"}
          </p>
        </div>
      </div>

      <div className="arr-ytd-toolbar">
        <div>
          <h3>YTD Through</h3>
          <p>
            Outbound YTD is calculated from January through {ytdThroughLabel}.
          </p>
        </div>

        <div className="devices-filter-row arr-ytd-filter-row">
          <select
            value={ytdThroughMonth}
            aria-label="YTD through month"
            onChange={(event) => setYtdThroughMonth(event.target.value)}
          >
            {MONTH_OPTIONS.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>

          <select
            value={ytdThroughYear}
            aria-label="YTD through year"
            onChange={(event) => setYtdThroughYear(event.target.value)}
          >
            {ytdYearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="arr-ytd-grid">
        <div className="analytics-metric-card">
          <h3>{ytdCurrentYear} YTD Outbound ARR</h3>
          <p>{formatARR(arrYtdTotals.currentOutboundYtd)}</p>
          <small>
            Through {ytdThroughMonthLabel} - Total{" "}
            {formatARR(arrYtdTotals.currentYtd)} / Inbound{" "}
            {formatARR(arrYtdTotals.currentInboundYtd)}
          </small>
        </div>

        <div className="analytics-metric-card">
          <h3>{ytdPreviousYear} YTD Outbound ARR</h3>
          <p>{formatARR(arrYtdTotals.previousOutboundYtd)}</p>
          <small>
            Through {ytdThroughMonthLabel} - Total{" "}
            {formatARR(arrYtdTotals.previousYtd)} / Inbound{" "}
            {formatARR(arrYtdTotals.previousInboundYtd)}
          </small>
        </div>

        <div className="analytics-metric-card">
          <h3>YTD Outbound Growth</h3>
          <p
            style={{
              color:
                arrYtdTotals.outboundGrowth === null ||
                arrYtdTotals.outboundGrowth >= 0
                  ? "#22c55e"
                  : "#ef4444"
            }}
          >
            {formatPercentage(arrYtdTotals.outboundGrowth)}
          </p>
          <small>
            Through {ytdThroughMonthLabel}, compared with {ytdPreviousYear}
          </small>
        </div>
      </div>

      <div className="arr-comparison-panel">
        <div className="arr-comparison-header">
          <div>
            <h2>Year-over-Year ARR Comparison</h2>
            <p>
              {selectedArrComparisonMetric.label} by month, comparing{" "}
              {ytdCurrentYear} with {ytdPreviousYear}. Click any month for details.
            </p>
          </div>

          <div className="arr-metric-toggle">
            {arrComparisonMetricOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                className={arrComparisonMetric === option.key ? "active" : ""}
                onClick={() => setArrComparisonMetric(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="arr-comparison-chart-card">
          <ResponsiveContainer width="100%" height={260}>
  <BarChart
    data={arrComparisonData}
    margin={{ top: 22, right: 24, left: 6, bottom: 8 }}
    barCategoryGap="24%"
    barGap={8}
    onClick={(chartData) => {
      if (chartData?.activeLabel) {
        setArrDrilldownMonth(chartData.activeLabel);
      }
    }}
  >
    <defs>
      <linearGradient id="previousArrBar" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#7dd3fc" stopOpacity={0.98} />
        <stop offset="100%" stopColor="#2563eb" stopOpacity={0.72} />
      </linearGradient>
      <linearGradient id="currentArrBar" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#38bdf8" stopOpacity={1} />
        <stop offset="48%" stopColor="#2563eb" stopOpacity={0.98} />
        <stop offset="100%" stopColor="#1d4ed8" stopOpacity={0.92} />
      </linearGradient>
    </defs>

    <CartesianGrid
      strokeDasharray="4 8"
      stroke="rgba(148,163,184,0.16)"
      vertical={false}
    />

    <XAxis
      dataKey="month"
      stroke="#b7d7ff"
      tickLine={false}
      axisLine={{ stroke: "rgba(148,163,184,0.24)" }}
      tick={{ fill: "#c7ddff", fontSize: 12, fontWeight: 800 }}
    />

    <YAxis
      stroke="#93c5fd"
      tickLine={false}
      axisLine={false}
      tick={{ fill: "#9fb8d9", fontSize: 12, fontWeight: 700 }}
      tickFormatter={(value) => `$${Math.round(Number(value || 0) / 1000)}k`}
    />

    <Tooltip
      cursor={{ fill: "rgba(59,130,246,0.1)", radius: 16 }}
      content={({ active, payload, label }) => {
        if (!active || !payload?.length) return null;

        return (
          <div className="arr-chart-tooltip">
            <strong>{label}</strong>
            {payload.map((item) => (
              <div key={item.dataKey} className="arr-chart-tooltip-row">
                <span
                  style={{
                    background: item.color || item.fill
                  }}
                />
                <p>{item.name}</p>
                <b>{formatARR(item.value)}</b>
              </div>
            ))}
          </div>
        );
      }}
    />

    <Legend
      wrapperStyle={{
        paddingTop: "12px",
        color: "#c7ddff",
        fontWeight: 800
      }}
    />

    <Bar
      dataKey={selectedArrComparisonMetric.previousKey}
      fill="url(#previousArrBar)"
      name={`${ytdPreviousYear} ${selectedArrComparisonMetric.label}`}
      radius={[12, 12, 4, 4]}
      maxBarSize={42}
    />

    <Bar
      dataKey={selectedArrComparisonMetric.currentKey}
      fill="url(#currentArrBar)"
      name={`${ytdCurrentYear} ${selectedArrComparisonMetric.label}`}
      radius={[12, 12, 4, 4]}
      maxBarSize={42}
    />
  </BarChart>
</ResponsiveContainer>
        </div>

        {selectedArrDrilldown && (
          <div className="arr-drilldown-strip">
            <div>
              <span>Selected Month</span>
              <strong>{selectedArrDrilldown.month}</strong>
            </div>
            <div>
              <span>{ytdCurrentYear} Total</span>
              <strong>{formatARR(drilldownCurrentTotal)}</strong>
            </div>
            <div>
              <span>{ytdCurrentYear} Outbound / Inbound</span>
              <strong>
                {formatARR(selectedArrDrilldown[currentYearOutboundKey])} /{" "}
                {formatARR(selectedArrDrilldown[currentYearInboundKey])}
              </strong>
            </div>
            <div>
              <span>{ytdPreviousYear} Total</span>
              <strong>{formatARR(drilldownPreviousTotal)}</strong>
            </div>
            <div>
              <span>YoY Growth</span>
              <strong
                className={
                  drilldownGrowth === null || drilldownGrowth >= 0
                    ? "positive"
                    : "negative"
                }
              >
                {formatPercentage(drilldownGrowth)}
              </strong>
            </div>
          </div>
        )}
      </div>
    </div>
  )}

  {salesAnalyticsTab === "teamArr" && (
    <div className="team-arr-panel">
      <div className="team-arr-header">
        <div>
          <h2>Team ARR</h2>
          <p>
            Team leader totals from the Team column for {selectedArrPeriod}
          </p>
        </div>

        <div className="team-arr-summary">
          <span>{teamArrData.summary.teamCount || 0} Teams</span>
          <strong>{teamArrData.summary.topTeam?.team || "N/A"}</strong>
        </div>
      </div>

      <div className="devices-filter-row">
        <select
          value={selectedMonth}
          onChange={handleMonthChange}
          style={{
            padding: "10px",
            borderRadius: "10px",
            background: "#0f172a",
            color: "#fff",
            border: "1px solid #334155"
          }}
        >
          <option value="">All Months</option>
          <option value="01">January</option>
          <option value="02">February</option>
          <option value="03">March</option>
          <option value="04">April</option>
          <option value="05">May</option>
          <option value="06">June</option>
          <option value="07">July</option>
          <option value="08">August</option>
          <option value="09">September</option>
          <option value="10">October</option>
          <option value="11">November</option>
          <option value="12">December</option>
        </select>

        <select
          value={selectedYear}
          onChange={handleYearChange}
          style={{
            padding: "10px",
            borderRadius: "10px",
            background: "#0f172a",
            color: "#fff",
            border: "1px solid #334155"
          }}
        >
          <option value="">All Years</option>
          <option value="2025">2025</option>
          <option value="2026">2026</option>
          <option value="2027">2027</option>
        </select>

        <button
          onClick={() => fetchTeamArrData(selectedMonth, selectedYear)}
          disabled={teamArrLoading}
          style={{
            background: "#2563eb",
            color: "#fff",
            border: "none",
            padding: "10px 16px",
            borderRadius: "10px",
            cursor: teamArrLoading ? "not-allowed" : "pointer",
            fontWeight: "bold",
            opacity: teamArrLoading ? 0.75 : 1
          }}
        >
          {teamArrLoading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {teamArrError && <p className="zoom-error">{teamArrError}</p>}

      {teamArrLoading && teamChartData.length === 0 ? (
        <p className="empty">Loading team ARR...</p>
      ) : teamChartData.length === 0 ? (
        <p className="empty">
          Add a Team column in the ARR sheets to see team-wise ARR.
        </p>
      ) : (
        <>
          <div className="team-arr-chart">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={teamChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis
                  dataKey="team"
                  stroke="#94a3b8"
                  interval={0}
                  angle={-12}
                  textAnchor="end"
                  height={64}
                />
                <YAxis stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{
                    background: "#0f172a",
                    border: "1px solid #334155",
                    borderRadius: "12px",
                    color: "#fff"
                  }}
                />
                <Legend />
                <Bar
                  dataKey="inboundARR"
                  stackId="arr"
                  fill="#22c55e"
                  name="Inbound ARR"
                  onClick={(entry) =>
                    setSelectedTeamArrName(entry?.payload?.team || "")
                  }
                  cursor="pointer"
                />
                <Bar
                  dataKey="outboundARR"
                  stackId="arr"
                  fill="#38bdf8"
                  name="Outbound ARR"
                  onClick={(entry) =>
                    setSelectedTeamArrName(entry?.payload?.team || "")
                  }
                  cursor="pointer"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="team-arr-comparison">
            {teamArrData.teams.slice(0, 6).map((team) => (
              <div
                className={`team-arr-row ${
                  selectedTeamArr?.team === team.team ? "active" : ""
                }`}
                key={team.team}
                onClick={() => setSelectedTeamArrName(team.team)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelectedTeamArrName(team.team);
                  }
                }}
              >
                <div>
                  <strong>#{team.rank} {team.team}</strong>
                  <span>{team.agentCount} agents - {team.share}% share</span>
                </div>

                <div>
                  <b>{formatARR(team.totalARR)}</b>
                  <span
                    className={
                      Number(team.change || 0) >= 0 ? "positive" : "negative"
                    }
                  >
                    {team.changePercent === null
                      ? "New"
                      : `${formatPercentage(team.changePercent)} vs prev`}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="ops-split-grid">
            <div className="ops-panel">
              <div className="ops-panel-heading">
                <h3>Outbound Target Rank</h3>
                <span>{teamTargetLeaders.length} teams</span>
              </div>

              {teamTargetLeaders.length === 0 ? (
                <p className="empty">No team target data found.</p>
              ) : (
                teamTargetLeaders.map((team) => (
                  <div className="ops-row" key={team.team}>
                    <div>
                      <strong>{team.team}</strong>
                      <span>
                        {formatARR(team.outboundARR)} / {formatARR(team.target)}
                      </span>
                    </div>
                    <b>{formatPercentage(team.targetProgress)}</b>
                  </div>
                ))
              )}
            </div>

            <div className="ops-panel">
              <div className="ops-panel-heading">
                <h3>Close To Target</h3>
                <span>{closeToTargetAgents.length} agents</span>
              </div>

              {closeToTargetAgents.length === 0 ? (
                <p className="empty">No agents are between 70% and 99% yet.</p>
              ) : (
                closeToTargetAgents.map((agent) => (
                  <div className="ops-row" key={`${agent.team}-${agent.name}`}>
                    <div>
                      <strong>{agent.name}</strong>
                      <span>{agent.team}</span>
                    </div>
                    <b>{formatPercentage(agent.targetProgress)}</b>
                  </div>
                ))
              )}
            </div>
          </div>

          {selectedTeamArr && (
            <div className="team-arr-drilldown">
              <div className="team-arr-drilldown-header">
                <div>
                  <h3>{selectedTeamArr.team}</h3>
                  <p>Agent-wise ARR for {selectedArrPeriod}</p>
                </div>

                <div>
                  <span>Outbound Target Progress</span>
                  <strong>
                    {selectedTeamArr.targetProgress == null
                      ? "N/A"
                      : formatPercentage(selectedTeamArr.targetProgress)}
                  </strong>
                </div>
              </div>

              <div className="team-arr-drilldown-metrics">
                <div>
                  <span>Total ARR</span>
                  <strong>{formatARR(selectedTeamArr.totalARR)}</strong>
                </div>

                <div>
                  <span>Outbound</span>
                  <strong>{formatARR(selectedTeamArr.outboundARR)}</strong>
                </div>

                <div>
                  <span>Inbound</span>
                  <strong>{formatARR(selectedTeamArr.inboundARR)}</strong>
                </div>

                <div>
                  <span>Outbound Target</span>
                  <strong>
                    {Number(selectedTeamArr.target || 0) > 0
                      ? formatARR(selectedTeamArr.target)
                      : "N/A"}
                  </strong>
                </div>
              </div>

              {selectedTeamAgents.length === 0 ? (
                <p className="empty">No agent rows found for this team.</p>
              ) : (
                <div className="team-arr-agent-list">
                  {selectedTeamAgents.map((agent) => (
                    <div className="team-arr-agent-row" key={agent.name}>
                      <div>
                        <strong>#{agent.rank} {agent.name}</strong>
                        <span>
                          Outbound {formatARR(agent.outboundARR)} - Inbound{" "}
                          {formatARR(agent.inboundARR)}
                        </span>
                      </div>

                      <div>
                        <b>{formatARR(agent.outboundARR)}</b>
                        <span>
                          Outbound Target{" "}
                          {Number(agent.target || 0) > 0
                            ? `${formatARR(agent.target)} (${formatPercentage(
                                agent.targetProgress
                              )})`
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )}

  {salesAnalyticsTab === "targetPace" && (
    <div className="ops-panel target-pace-panel">
      <div className="ops-header">
        <div>
          <h2>Target Pace</h2>
          <p>
            Outbound target progress for {selectedArrPeriod}.{" "}
            {targetPaceDaysRemaining} day
            {targetPaceDaysRemaining === 1 ? "" : "s"} left in this month.
          </p>
        </div>

        <button
          type="button"
          onClick={() => fetchTeamArrData(selectedMonth, selectedYear)}
          disabled={teamArrLoading}
        >
          {teamArrLoading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {teamArrError && <p className="zoom-error">{teamArrError}</p>}

      <div className="ops-summary-grid">
        <div>
          <span>Outbound Target</span>
          <strong>{formatARR(targetPaceSummary.target)}</strong>
        </div>
        <div>
          <span>Outbound Closed</span>
          <strong>{formatARR(targetPaceSummary.outbound)}</strong>
        </div>
        <div>
          <span>Remaining</span>
          <strong>{formatARR(targetPaceSummary.remaining)}</strong>
        </div>
        <div>
          <span>Target Hit</span>
          <strong>{targetPaceSummary.hit}</strong>
        </div>
        <div>
          <span>Need Push</span>
          <strong>{targetPaceSummary.needsPush}</strong>
        </div>
      </div>

      {targetPaceAgents.length === 0 ? (
        <p className="empty">No outbound target rows found for this period.</p>
      ) : (
        <div className="target-pace-list">
          {targetPaceAgents.map((agent) => (
            <div
              className={`target-pace-row ${agent.remaining <= 0 ? "hit" : ""}`}
              key={`${agent.team}-${agent.name}`}
            >
              <div className="target-pace-main">
                <div>
                  <strong>{agent.name}</strong>
                  <span>{agent.team}</span>
                </div>
                <b>{agent.status}</b>
              </div>

              <div className="target-progress-track">
                <i style={{ width: `${Math.min(agent.progress, 100)}%` }} />
              </div>

              <div className="target-pace-stats">
                <span>Closed {formatARR(agent.outboundARR)}</span>
                <span>Target {formatARR(agent.target)}</span>
                <span>
                  Daily Need{" "}
                  {agent.remaining <= 0
                    ? "$0"
                    : formatARR(agent.dailyNeeded)}
                </span>
                <strong>{formatPercentage(agent.progress)}</strong>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )}

  {salesAnalyticsTab === "salesTimeline" && (
    <div className="ops-panel sales-timeline-panel">
      <div className="ops-header">
        <div>
          <h2>Sales Timeline</h2>
          <p>Latest sales rows from Total Sale for {selectedArrPeriod}.</p>
        </div>

        <div className="devices-filter-row">
          <button
            type="button"
            onClick={() => fetchArrData(selectedMonth, selectedYear)}
            disabled={arrLoading}
          >
            {arrLoading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {recentSalesFeed.length === 0 ? (
        <p className="empty">No sales rows found for this period.</p>
      ) : (
        <div className="sales-timeline-list expanded">
          {arrData.salesFeed.map((sale) => (
            <div className="sales-timeline-row" key={`timeline-${sale.id}`}>
              <div>
                <strong>{sale.representative}</strong>
                <span>
                  {sale.date || "No date"}
                  {sale.team ? ` - ${sale.team}` : ""}
                  {sale.role ? ` - ${sale.role}` : ""}
                </span>
                <span>
                  {sale.plan || "Plan not set"}
                  {sale.devices ? ` - ${sale.devices} devices` : ""}
                  {sale.lgs ? ` - LGS ${sale.lgs}` : ""}
                </span>
              </div>
              <div>
                <b>{formatARR(sale.totalARR)}</b>
                <span>
                  Outbound {formatARR(sale.outboundARR)} / Inbound{" "}
                  {formatARR(sale.inboundARR)}
                </span>
                <span>{sale.source || "No source"}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )}

  {salesAnalyticsTab === "opsCenter" && (
    <div className="ops-center-tabs">
      {[
        { key: "actionCenter", label: "Action Center" },
        { key: "monthlyReport", label: "Monthly Report" },
        { key: "agentProfile", label: "Agent Profile" }
      ].map((tab) => (
        <button
          type="button"
          key={tab.key}
          className={opsCenterView === tab.key ? "active" : ""}
          onClick={() => setOpsCenterView(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )}

  {salesAnalyticsTab === "opsCenter" && opsCenterView === "actionCenter" && (
    <div className="ops-panel manager-action-center">
      <div className="ops-header">
        <div>
          <h2>Manager Action Center</h2>
          <p>Quick admin actions without searching through every panel.</p>
        </div>

        <div className="devices-filter-row">
          <select
            value={managerActionAgentId}
            onChange={(e) => setManagerActionAgentId(e.target.value)}
          >
            {displayAgents.map((agent) => (
              <option key={agent._id} value={agent._id}>
                {agent.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedManagerAgent ? (
        <>
          <div className="action-agent-card">
            <div>
              <span>Selected Agent</span>
              <strong>{selectedManagerAgent.name}</strong>
              <p>
                {selectedManagerAgent.statusLabel || selectedManagerAgent.status}
                {selectedManagerAgent.breakType
                  ? ` - ${selectedManagerAgent.breakType}`
                  : ""}
              </p>
            </div>

            <div>
              <span>Timer</span>
              <strong>
                {selectedManagerAgent.status === "Break"
                  ? formatTodayTimer(selectedManagerAgent.statusStartTime)
                  : formatTimer(selectedManagerAgent.statusStartTime)}
              </strong>
              <p>{selectedManagerAgent.zoomEmail || "No Zoom email mapped"}</p>
            </div>
          </div>

          <div className="action-button-grid">
            <button
              type="button"
              onClick={() => adminUpdateAgentStatus(selectedManagerAgent, "Available")}
              disabled={Boolean(adminStatusLoadingId)}
            >
              Mark Available
            </button>
            <button
              type="button"
              onClick={() =>
                adminUpdateAgentStatus(
                  selectedManagerAgent,
                  "Break",
                  "Bio Break"
                )
              }
              disabled={Boolean(adminStatusLoadingId)}
            >
              Start Bio Break
            </button>
            <button
              type="button"
              onClick={() =>
                adminUpdateAgentStatus(
                  selectedManagerAgent,
                  "Break",
                  "Main Break"
                )
              }
              disabled={Boolean(adminStatusLoadingId)}
            >
              Start Main Break
            </button>
            <button
              type="button"
              onClick={() => adminUpdateAgentStatus(selectedManagerAgent, "Meeting")}
              disabled={Boolean(adminStatusLoadingId)}
            >
              Mark Meeting
            </button>
          </div>
        </>
      ) : (
        <p className="empty">No agent selected.</p>
      )}

      <div className="ops-split-grid">
        <div className="ops-panel inner">
          <div className="ops-panel-heading">
            <h3>Admin Shortcuts</h3>
            <span>Common controls</span>
          </div>
          <div className="action-button-grid compact">
            <button type="button" onClick={openUserManagement}>
              User Management
            </button>
            <button type="button" onClick={() => setShowShiftManagement(true)}>
              Shift Management
            </button>
            <button
              type="button"
              onClick={() => {
                setShowPasswordRequests(true);
                fetchPasswordResetRequests();
              }}
            >
              Password Requests
            </button>
            <button
              type="button"
              onClick={() => {
                setShowHistoricalReports(true);
                fetchHistoricalReports(selectedReportStartDate, selectedReportEndDate);
              }}
            >
              Historical Breaks
            </button>
          </div>
        </div>

        <div className="ops-panel inner">
          <div className="ops-panel-heading">
            <h3>Live Alerts</h3>
            <span>{notificationItems.length} active</span>
          </div>
          {notificationItems.length === 0 ? (
            <p className="empty">No current action needed.</p>
          ) : (
            notificationItems.slice(0, 5).map((item, index) => (
              <div className={`notification-row ${item.tone}`} key={`${item.type}-${index}`}>
                <span>{item.type}</span>
                <div>
                  <strong>{item.title}</strong>
                  <p>{item.detail}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )}

  {salesAnalyticsTab === "opsCenter" && opsCenterView === "monthlyReport" && (
    <div className="ops-panel monthly-report-panel">
      <div className="ops-header">
        <div>
          <h2>Monthly Performance Report</h2>
          <p>
            One export for ARR, team targets, Zoom, breaks, devices, and LGS for{" "}
            {selectedArrPeriod}.
          </p>
        </div>

        <div className="devices-filter-row">
          <button
            type="button"
            onClick={refreshMonthlyPerformancePreview}
            disabled={monthlyReportLoading}
          >
            {monthlyReportLoading ? "Refreshing..." : "Refresh Preview"}
          </button>
          <button
            type="button"
            onClick={downloadMonthlyPerformanceReport}
            disabled={monthlyReportLoading}
          >
            CSV
          </button>
        </div>
      </div>

      {monthlyReportError && <p className="zoom-error">{monthlyReportError}</p>}

      <div className="ops-summary-grid">
        <div>
          <span>Total ARR</span>
          <strong>{formatARR(totalARR)}</strong>
        </div>
        <div>
          <span>Outbound ARR</span>
          <strong>{formatARR(outboundARR)}</strong>
        </div>
        <div>
          <span>Team Count</span>
          <strong>{teamArrData.summary.teamCount || 0}</strong>
        </div>
        <div>
          <span>Outbound Calls</span>
          <strong>{zoomTrendData.summary.totalOutboundCalls || 0}</strong>
        </div>
        <div>
          <span>Break Exceeded</span>
          <strong>{monthlyBreakData.summary.exceededBreaks || 0}</strong>
        </div>
        <div>
          <span>LGS Conversion</span>
          <strong>{lgsData.conversionRate || 0}%</strong>
        </div>
      </div>

      <div className="report-preview-grid">
        <div className="ops-panel inner">
          <div className="ops-panel-heading">
            <h3>Top Teams</h3>
            <span>{selectedArrPeriod}</span>
          </div>
          {teamArrData.teams.slice(0, 4).map((team) => (
            <div className="ops-row compact" key={team.team}>
              <div>
                <strong>{team.team}</strong>
                <span>{team.agentCount} agents</span>
              </div>
              <div>
                <b>{formatARR(team.outboundARR)}</b>
                <span>Outbound</span>
              </div>
            </div>
          ))}
        </div>

        <div className="ops-panel inner">
          <div className="ops-panel-heading">
            <h3>Target Attention</h3>
            <span>{targetPaceAgents.length} targets</span>
          </div>
          {targetPaceAgents.slice(0, 4).map((agent) => (
            <div className="ops-row compact" key={`${agent.team}-${agent.name}`}>
              <div>
                <strong>{agent.name}</strong>
                <span>{agent.team}</span>
              </div>
              <div>
                <b>{formatARR(agent.remaining)}</b>
                <span>Remaining</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )}

  {salesAnalyticsTab === "opsCenter" && opsCenterView === "agentProfile" && (
    <div className="ops-panel agent-profile-panel">
      <div className="ops-header">
        <div>
          <h2>Agent Profile</h2>
          <p>One agent view for ARR, calls, breaks, target progress, and live status.</p>
        </div>

        <div className="devices-filter-row">
          <select
            value={agentProfileId}
            onChange={(e) => setAgentProfileId(e.target.value)}
          >
            {displayAgents.map((agent) => (
              <option key={agent._id} value={agent._id}>
                {agent.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedProfileAgent ? (
        <>
          <div className="profile-hero">
            <div>
              <span>Agent</span>
              <strong>{selectedProfileAgent.name}</strong>
              <p>
                {selectedProfileAgent.statusLabel || selectedProfileAgent.status} -{" "}
                {selectedProfileAgent.status === "Break"
                  ? formatTodayTimer(selectedProfileAgent.statusStartTime)
                  : formatTimer(selectedProfileAgent.statusStartTime)}
              </p>
            </div>
            <div>
              <span>Team</span>
              <strong>{selectedProfileArr?.team || "N/A"}</strong>
              <p>{selectedProfileAgent.shiftType || "regular"} shift</p>
            </div>
          </div>

          <div className="ops-summary-grid">
            <div>
              <span>Outbound ARR</span>
              <strong>{formatARR(selectedProfileArr?.outboundARR || 0)}</strong>
            </div>
            <div>
              <span>Inbound ARR</span>
              <strong>{formatARR(selectedProfileArr?.inboundARR || 0)}</strong>
            </div>
            <div>
              <span>Target Progress</span>
              <strong>
                {selectedProfileArr?.targetProgress == null
                  ? "N/A"
                  : formatPercentage(selectedProfileArr.targetProgress)}
              </strong>
            </div>
            <div>
              <span>Dials</span>
              <strong>{selectedProfileZoomStats?.totalCalls || 0}</strong>
            </div>
            <div>
              <span>Breaks</span>
              <strong>{selectedProfileBreakReport?.breakCount || 0}</strong>
            </div>
            <div>
              <span>Exceeded</span>
              <strong>{selectedProfileBreakReport?.exceededBreaks || 0}</strong>
            </div>
          </div>

          <div className="ops-split-grid">
            <div className="ops-panel inner">
              <div className="ops-panel-heading">
                <h3>Break Detail</h3>
                <span>{getMonthLabel(breakReportMonth)} {breakReportYear}</span>
              </div>
              {selectedProfileBreakReport?.days?.length ? (
                selectedProfileBreakReport.days.slice(0, 5).map((day) => (
                  <div className="ops-row compact" key={day.date}>
                    <div>
                      <strong>{day.date}</strong>
                      <span>{day.breakCount} breaks</span>
                    </div>
                    <div>
                      <b>{formatMilliseconds(day.totalBreakTime)}</b>
                      <span>{day.exceededBreaks} exceeded</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="empty">No break records for this month.</p>
              )}
            </div>

            <div className="ops-panel inner">
              <div className="ops-panel-heading">
                <h3>Zoom Detail</h3>
                <span>{zoomDate}</span>
              </div>
              <div className="ops-mini-grid">
                <div>
                  <span>Outbound</span>
                  <strong>{selectedProfileZoomStats?.outboundCalls || 0}</strong>
                </div>
                <div>
                  <span>Inbound</span>
                  <strong>{selectedProfileZoomStats?.inboundCalls || 0}</strong>
                </div>
                <div>
                  <span>Connected</span>
                  <strong>{selectedProfileZoomStats?.connectedCalls || 0}</strong>
                </div>
                <div>
                  <span>Talk Time</span>
                  <strong>
                    {formatSeconds(selectedProfileZoomStats?.totalTalkSeconds || 0)}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="empty">No agent selected.</p>
      )}
    </div>
  )}

  {salesAnalyticsTab === "breakReports" && (
    <div className="ops-panel">
      <div className="ops-header">
        <div>
          <h2>Monthly Break Reports</h2>
          <p>Click an agent to see every break they took date-wise.</p>
        </div>

        <div className="devices-filter-row">
          <select
            value={breakReportMonth}
            onChange={(e) => setBreakReportMonth(e.target.value)}
          >
            <option value="01">January</option>
            <option value="02">February</option>
            <option value="03">March</option>
            <option value="04">April</option>
            <option value="05">May</option>
            <option value="06">June</option>
            <option value="07">July</option>
            <option value="08">August</option>
            <option value="09">September</option>
            <option value="10">October</option>
            <option value="11">November</option>
            <option value="12">December</option>
          </select>

          <select
            value={breakReportYear}
            onChange={(e) => setBreakReportYear(e.target.value)}
          >
            <option value="2025">2025</option>
            <option value="2026">2026</option>
            <option value="2027">2027</option>
          </select>

          <select
            value={breakReportAgent}
            onChange={(e) => setBreakReportAgent(e.target.value)}
          >
            <option value="">All Agents</option>
            {agents.map((agent) => (
              <option key={agent._id} value={agent.name}>
                {agent.name}
              </option>
            ))}
          </select>

          <button onClick={fetchMonthlyBreakReport} disabled={breakReportLoading}>
            {breakReportLoading ? "Loading..." : "Refresh"}
          </button>

          <button
            onClick={downloadMonthlyBreakReport}
            disabled={monthlyBreakData.agents.length === 0}
          >
            CSV
          </button>
        </div>
      </div>

      {breakReportError && <p className="zoom-error">{breakReportError}</p>}

      <div className="ops-summary-grid">
        <div>
          <span>Agents</span>
          <strong>{monthlyBreakData.summary.agentCount || 0}</strong>
        </div>
        <div>
          <span>Total Breaks</span>
          <strong>{monthlyBreakData.summary.breakCount || 0}</strong>
        </div>
        <div>
          <span>Break Exceeded</span>
          <strong>{monthlyBreakData.summary.exceededBreaks || 0}</strong>
        </div>
        <div>
          <span>Days With Breaks</span>
          <strong>{monthlyBreakData.summary.daysWithBreaks || 0}</strong>
        </div>
        <div>
          <span>Avg Break</span>
          <strong>{formatMilliseconds(monthlyAverageBreakMs)}</strong>
        </div>
      </div>

      <div className="ops-list">
        {monthlyBreakData.agents.length === 0 ? (
          <p className="empty">No monthly break records found.</p>
        ) : (
          monthlyBreakData.agents.map((agent) => (
            <div key={agent.agentName}>
              <div
                className={`ops-row clickable ${
                  selectedBreakReportAgent === agent.agentName ? "active" : ""
                } ${agent.exceededBreaks > 0 ? "exceeded" : ""}`}
                onClick={() =>
                  setSelectedBreakReportAgent((current) =>
                    current === agent.agentName ? "" : agent.agentName
                  )
                }
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelectedBreakReportAgent((current) =>
                      current === agent.agentName ? "" : agent.agentName
                    );
                  }
                }}
              >
                <div>
                  <strong>{agent.agentName}</strong>
                  <span>
                    {Number(agent.breakCount || 0)} breaks across {(agent.days || []).length} days
                  </span>
                </div>
                <div>
                  <b>{Number(agent.exceededBreaks || 0)} exceeded</b>
                  <span>{formatMilliseconds(agent.totalBreakTime)} total</span>
                </div>
              </div>

              {selectedBreakReportAgent === agent.agentName && (
                <div className="break-detail-panel">
                  {(agent.days || []).map((day) => (
                    <div
                      className={`break-day-card ${
                        day.dailyLimitExceeded ? "exceeded" : ""
                      }`}
                      key={day.date}
                    >
                      <div className="break-day-header">
                        <div>
                          <strong>{day.date}</strong>
                          <span>
                            {day.breakCount} breaks -{" "}
                            {formatMilliseconds(day.totalBreakTime)}
                          </span>
                          <span>{day.team || 'No Team'} - Allowed {formatMilliseconds(day.breakLimitMs)}
                            {day.limitSource === 'legacy-default' ? ' (legacy fallback)' : ''}</span>
                        </div>
                        <b>
                          {day.dailyLimitExceeded
                            ? `Exceeded by ${formatMilliseconds(day.overageMs)}`
                            : `${Number(day.exceededBreaks || 0)} exceeded`}
                        </b>
                      </div>

                      <div className="break-session-list">
                        {day.sessions.map((session, sessionIndex) => {
                          const sequence = Number(session.sequence || sessionIndex + 1);
                          const suffix =
                            sequence === 1
                              ? "st"
                              : sequence === 2
                              ? "nd"
                              : sequence === 3
                              ? "rd"
                              : "th";

                          return (
                            <div
                              className={`break-session-row ${
                                session.exceeded ? "exceeded" : ""
                              }`}
                              key={session.id || `${day.date}-${sessionIndex}`}
                            >
                              <div>
                                <strong>
                                  {sequence}
                                  {suffix} Break
                                </strong>
                                <span>{session.breakType || "Break"}</span>
                                <span>{session.team || 'No Team'} - Allowed {formatMilliseconds(session.breakLimitMs)}</span>
                              </div>
                              <div>
                                <b>{formatMilliseconds(session.durationMs)}</b>
                                <span>
                                  {session.startLabel || "Start time missing"} -{" "}
                                  {session.endLabel || "End time missing"}
                                  {session.pushedDailyLimit ? " - limit crossed" : ""}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )}

  {salesAnalyticsTab === "zoomTrends" && (
    <div className="ops-panel">
      <div className="ops-header">
        <div>
          <h2>Zoom Trends</h2>
          <p>Daily inbound and outbound calls from Zoom call history.</p>
        </div>

        <div className="devices-filter-row">
          <input
            type="date"
            value={zoomTrendFrom}
            onChange={(e) => setZoomTrendFrom(e.target.value)}
          />
          <input
            type="date"
            value={zoomTrendTo}
            onChange={(e) => setZoomTrendTo(e.target.value)}
          />
          <select
            value={zoomTrendAgentId}
            onChange={(e) => setZoomTrendAgentId(e.target.value)}
          >
            <option value="">All Agents</option>
            {agents.map((agent) => (
              <option key={agent._id} value={agent._id}>
                {agent.name}
              </option>
            ))}
          </select>
          <button onClick={fetchZoomTrends} disabled={zoomTrendLoading}>
            {zoomTrendLoading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {zoomTrendError && <p className="zoom-error">{zoomTrendError}</p>}
      {!zoomTrendError && zoomTrendData.summary.warning && (
        <p className="zoom-warning">{zoomTrendData.summary.warning}</p>
      )}

      <div className="ops-summary-grid">
        <div>
          <span>Outbound</span>
          <strong>{zoomTrendData.summary.totalOutboundCalls || 0}</strong>
        </div>
        <div>
          <span>Inbound</span>
          <strong>{zoomTrendData.summary.totalInboundCalls || 0}</strong>
        </div>
        <div>
          <span>Total Calls</span>
          <strong>{zoomTrendData.summary.totalCalls || 0}</strong>
        </div>
        <div>
          <span>Active Agents</span>
          <strong>{zoomTrendData.summary.activeAgents || 0}</strong>
        </div>
      </div>

      <div className="team-arr-chart">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={zoomTrendData.daily}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="date" stroke="#94a3b8" />
            <YAxis stroke="#94a3b8" />
            <Tooltip
              contentStyle={{
                background: "#0f172a",
                border: "1px solid #334155",
                borderRadius: "12px",
                color: "#fff"
              }}
            />
            <Legend />
            <Bar dataKey="outboundCalls" fill="#38bdf8" name="Outbound" />
            <Bar dataKey="inboundCalls" fill="#22c55e" name="Inbound" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="ops-list">
        {zoomTrendData.agents.slice(0, 10).map((agent) => (
          <div className="ops-row" key={agent.agentId || agent.name}>
            <div>
              <strong>{agent.name}</strong>
              <span>{agent.activeDays} active days</span>
            </div>
            <div>
              <b>{agent.outboundCalls} outbound</b>
              <span>{agent.totalCalls} total</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )}

  {salesAnalyticsTab === "devices" && (
    <div className="devices-analytics-panel">
      <h2 style={{ color: "#fff", marginTop: 0 }}>
        📦 Devices by Source
      </h2>
<div className="devices-filter-row">
  <select
    value={devicesMonth}
    onChange={(e) => setDevicesMonth(e.target.value)}
    style={{
      padding: "10px",
      borderRadius: "10px",
      background: "#0f172a",
      color: "#fff",
      border: "1px solid #334155"
    }}
  >
    <option value="01">January</option>
    <option value="02">February</option>
    <option value="03">March</option>
    <option value="04">April</option>
    <option value="05">May</option>
    <option value="06">June</option>
    <option value="07">July</option>
    <option value="08">August</option>
    <option value="09">September</option>
    <option value="10">October</option>
    <option value="11">November</option>
    <option value="12">December</option>
  </select>

  <select
    value={devicesYear}
    onChange={(e) => setDevicesYear(e.target.value)}
    style={{
      padding: "10px",
      borderRadius: "10px",
      background: "#0f172a",
      color: "#fff",
      border: "1px solid #334155"
    }}
  >
    <option value="2025">2025</option>
    <option value="2026">2026</option>
    <option value="2027">2027</option>
  </select>
</div>
      <p style={{ color: "#94a3b8" }}>
        This will count devices from the Source column in Total Sale sheet.
      </p>

      <div className="devices-content-grid">
  <div className="devices-chart">
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={devicesBySource.sources}
          dataKey="count"
          nameKey="source"
          cx="50%"
          cy="50%"
          outerRadius={110}
          label
        >
          {devicesBySource.sources.map((entry, index) => (
            <Cell
              key={index}
              fill={
                PIE_COLORS[
                  index % PIE_COLORS.length
                ]
              }
            />
          ))}
        </Pie>

        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  </div>

  <div className="devices-stats-grid">
    <div
      style={{
        background: "#0f172a",
        padding: "18px",
        borderRadius: "18px",
        marginBottom: "18px",
        border: "1px solid rgba(255,255,255,0.08)"
      }}
    >
      <h3 style={{ marginTop: 0 }}>
        Total Devices
      </h3>

      <p
        style={{
          fontSize: "42px",
          fontWeight: "bold",
          margin: 0,
          color: "#38bdf8"
        }}
      >
        {devicesBySource.totalDevices}
      </p>
    </div>

    <div
      style={{
        background: "#0f172a",
        padding: "18px",
        borderRadius: "18px",
        marginBottom: "18px",
        border: "1px solid rgba(255,255,255,0.08)"
      }}
    >
      <h3 style={{ marginTop: 0 }}>
        Top Source
      </h3>

      <p
        style={{
          fontSize: "28px",
          fontWeight: "bold",
          marginBottom: "6px",
          color: "#22c55e"
        }}
      >
        {devicesBySource.topSource?.source ||
          "N/A"}
      </p>

      <p
        style={{
          color: "#94a3b8",
          margin: 0
        }}
      >
        {devicesBySource.topSource?.count || 0} Devices
      </p>
    </div>

    <div
      style={{
        background: "#0f172a",
        padding: "18px",
        borderRadius: "18px",
        border: "1px solid rgba(255,255,255,0.08)"
      }}
    >
      <h3 style={{ marginTop: 0 }}>
        Source Leaderboard
      </h3>

      {devicesBySource.sources.map(
        (item, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              marginBottom: "10px",
              paddingBottom: "10px",
              borderBottom:
                "1px solid rgba(255,255,255,0.05)"
            }}
          >
            <span>{item.source}</span>

            <strong>{item.count}</strong>
          </div>
        )
      )}
    </div>
  </div>
</div>
    </div>
  )}
{salesAnalyticsTab === "lgs" && (
  <div>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "16px",
        marginBottom: "24px",
        flexWrap: "wrap"
      }}
    >
      <div>
        <h2 style={{ color: "#fff", marginBottom: "8px" }}>
          📞 LGS Conversion Analytics
        </h2>

        <p style={{ color: "#94a3b8", margin: 0 }}>
          Track transferred and converted leads by LGS
        </p>
      </div>

      <div
        style={{
          display: "flex",
          gap: "12px",
          flexWrap: "wrap"
        }}
      >
        <select
          value={lgsMonth}
          onChange={(e) => setLgsMonth(e.target.value)}
          style={{
            padding: "12px",
            borderRadius: "12px",
            background: "#0f3460",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.1)",
            fontWeight: "bold"
          }}
        >
          <option value="01">January</option>
          <option value="02">February</option>
          <option value="03">March</option>
          <option value="04">April</option>
          <option value="05">May</option>
          <option value="06">June</option>
          <option value="07">July</option>
          <option value="08">August</option>
          <option value="09">September</option>
          <option value="10">October</option>
          <option value="11">November</option>
          <option value="12">December</option>
        </select>

        <select
          value={lgsYear}
          onChange={(e) => setLgsYear(e.target.value)}
          style={{
            padding: "12px",
            borderRadius: "12px",
            background: "#0f3460",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.1)",
            fontWeight: "bold"
          }}
        >
          <option value="2025">2025</option>
          <option value="2026">2026</option>
          <option value="2027">2027</option>
        </select>
      </div>
    </div>

    <div className="analytics-summary-grid lgs-summary-grid">
      <div className="summary-card">
        <h3>Total Transferred</h3>
        <p>{lgsData.totalTransferred || 0}</p>
      </div>

      <div className="summary-card">
        <h3>Total Converted</h3>
        <p>{lgsData.totalConverted || 0}</p>
      </div>

      <div className="summary-card">
        <h3>Conversion Rate</h3>
        <p>{lgsData.conversionRate || 0}%</p>
      </div>

      <div className="summary-card">
        <h3>Top LGS</h3>
        <p style={{ fontSize: "24px" }}>
          {lgsData.topLGS?.name || "N/A"}
        </p>
      </div>
    </div>

    <div
      style={{
        background: "#081028",
        borderRadius: "18px",
        padding: "20px",
        border: "1px solid rgba(148,163,184,0.18)"
      }}
    >
      <h3 style={{ color: "#fff", marginTop: 0 }}>
        LGS Leaderboard
      </h3>

      {lgsData.leaderboard?.map((lgs, index) => (
        <div
          key={index}
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "12px 0",
            borderBottom:
              "1px solid rgba(148,163,184,0.12)",
            color: "#fff"
          }}
        >
          <div>
            <strong>
              #{index + 1} {lgs.name}
            </strong>
          </div>

          <div
            style={{
              display: "flex",
              gap: "20px",
              fontWeight: "bold"
            }}
          >
            <span>
              Transferred: {lgs.transferred}
            </span>

            <span>
              Converted: {lgs.converted}
            </span>

            <span style={{ color: "#22c55e" }}>
              {lgs.conversionRate}%
            </span>
          </div>
        </div>
      ))}
    </div>
  </div>
)}
  {false && salesAnalyticsTab === "hubspot" && (
    <div className="hubspot-panel">
      <div className="hubspot-header">
        <div>
          <h2>HubSpot Lead Status</h2>
          <p>
            Contacts owned by agents with Lead Status as Follow Up, Pipeline, or In Process
          </p>
        </div>

        <div className="hubspot-filter-row">
          <select
            value={hubspotStatus}
            onChange={(e) => setHubspotStatus(e.target.value)}
          >
            <option value="all">All Target Statuses</option>
            <option value="follow_up">Follow Up</option>
            <option value="pipeline">Pipeline</option>
            <option value="in_process">In Process</option>
          </select>

          <select
            value={hubspotOwnerId}
            onChange={(e) => setHubspotOwnerId(e.target.value)}
          >
            <option value="all">All Owners</option>
            {hubspotFollowups.owners.map((owner) => (
              <option key={owner.id} value={owner.id}>
                {owner.name}
              </option>
            ))}
          </select>

          <button onClick={fetchHubspotFollowups} disabled={hubspotLoading}>
            {hubspotLoading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {hubspotError && (
        <div className="hubspot-error">
          {hubspotError}
        </div>
      )}

      {!hubspotFollowups.countsExact && (
        <div className="hubspot-warning">
          HubSpot returned more matching leads than can be grouped in one request.
          Totals are exact, but the owner list may be partial. Select one owner
          for exact agent counts.
        </div>
      )}

      {hubspotOwnerId === "all" && !hubspotLoading && !hubspotError && (
        <div className="hubspot-info">
          Agent counts are exact for the selected lead status filter.
        </div>
      )}

      <div className="hubspot-summary-grid">
        <div className="summary-card">
          <h3>Total Leads</h3>
          <p>{hubspotFollowups.summary.total}</p>
        </div>

        <div className="summary-card">
          <h3>Follow Up</h3>
          <p>{hubspotFollowups.summary.followUp}</p>
        </div>

        <div className="summary-card">
          <h3>Pipeline</h3>
          <p>{hubspotFollowups.summary.pipeline}</p>
        </div>

        <div className="summary-card">
          <h3>In Process</h3>
          <p>{hubspotFollowups.summary.inProcess}</p>
        </div>
      </div>

      <div className="hubspot-content-grid">
        <div className="hubspot-leaderboard">
          <div className="hubspot-section-heading">
            <h3>Agent Lead Status</h3>
            <span>{hubspotFollowups.leaderboard.length} owners</span>
          </div>

          {hubspotLoading && (
            <p className="hubspot-empty">Loading HubSpot lead status...</p>
          )}

          {!hubspotLoading && hubspotFollowups.leaderboard.length === 0 && (
            <p className="hubspot-empty">No matching leads found for this filter.</p>
          )}

          {hubspotFollowups.leaderboard.map((owner, index) => (
            <div className="hubspot-owner-row" key={owner.ownerId}>
              <div className="hubspot-rank">#{index + 1}</div>

              <div className="hubspot-owner-main">
                <div className="hubspot-owner-top">
                  <strong>{owner.name}</strong>
                  <span>{owner.total} total</span>
                </div>

                <div className="hubspot-progress-track">
                  <i
                    style={{
                      width: `${Math.min(owner.targetShare || 0, 100)}%`
                    }}
                  />
                </div>

                <div className="hubspot-owner-stats">
                  <span>Follow Up {owner.followUp}</span>
                  <span>Pipeline {owner.pipeline}</span>
                  <span>In Process {owner.inProcess}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )}
  {salesAnalyticsTab === "insights" && (
    <div className="insights-panel">
      <div className="insights-header">
        <div>
          <h2>Executive Insights</h2>
          <p>{selectedArrPeriod}</p>
        </div>

        <div className="insights-periods">
          <span>ARR {selectedArrPeriod}</span>
          <span>Devices {selectedDevicesPeriod}</span>
          <span>LGS {selectedLgsPeriod}</span>
        </div>
      </div>

      <div className="insights-metric-grid">
        <div className="analytics-metric-card insight-metric-card">
          <h3>Selected ARR</h3>
          <p>{formatARR(totalARR)}</p>
          <span>
            {formatInsightPercent(outboundShare)} outbound /{" "}
            {formatInsightPercent(inboundShare)} inbound
          </span>
        </div>

        <div className="analytics-metric-card insight-metric-card">
          <h3>{ytdCurrentYear} YTD Outbound ARR</h3>
          <p>{formatARR(arrYtdTotals.currentOutboundYtd)}</p>
          <span>
            Through {ytdThroughMonthLabel}. {ytdPreviousYear} outbound YTD:{" "}
            {formatARR(arrYtdTotals.previousOutboundYtd)} -{" "}
            {formatPercentage(arrYtdTotals.outboundGrowth)}
          </span>
        </div>

        <div className="analytics-metric-card insight-metric-card">
          <h3>Top Source</h3>
          <p>{devicesBySource.topSource?.source || "N/A"}</p>
          <span>
            {devicesBySource.topSource?.count || 0} of{" "}
            {devicesBySource.totalDevices || 0} devices
          </span>
        </div>

        <div className="analytics-metric-card insight-metric-card">
          <h3>Top LGS</h3>
          <p>{lgsData.topLGS?.name || "N/A"}</p>
          <span>
            {lgsData.topLGS?.converted || 0} converted /{" "}
            {lgsData.topLGS?.transferred || 0} transferred
          </span>
        </div>
      </div>

      <div className="insights-grid">
        <div className="insights-list">
          <div className="insights-section-heading">
            <h3>Priority Signals</h3>
            <span>{bestArrMonth?.month || "No"} peak month</span>
          </div>

          {insightRows.map((item) => (
            <div className="insight-row" key={item.label}>
              <div>
                <span>{item.label}</span>
                <strong>{item.title}</strong>
                <p>{item.detail}</p>
              </div>

              <b>{item.value}</b>
            </div>
          ))}
        </div>

        <div className="insights-side-panel">
          <div className="insights-section-heading">
            <h3>Sales Leaders</h3>
            <span>Outbound</span>
          </div>

          <div className="insight-spotlight">
            <span>Top Outbound Performer</span>
            <strong>{topArrPerformer?.name || "N/A"}</strong>
            <p>{formatARR(topArrPerformer?.value || 0)}</p>
          </div>

          <div className="insight-mini-list">
            {(lgsData.leaderboard || []).slice(0, 4).map((lgs, index) => (
              <div className="insight-mini-row" key={`${lgs.name}-${index}`}>
                <span>#{index + 1} {lgs.name}</span>
                <strong>
                  {lgs.converted} / {lgs.transferred}
                </strong>
              </div>
            ))}

            {(lgsData.leaderboard || []).length === 0 && (
              <p className="insight-empty">No LGS data for this period</p>
            )}
          </div>

          <div className="insight-progress-block">
            <div>
              <span>LGS follow-up gap</span>
              <strong>{lgsConversionGap}</strong>
            </div>

            <div className="insight-progress-track">
              <div
                className="insight-progress-fill"
                style={{
                  width: `${Math.min(Number(lgsData.conversionRate || 0), 100)}%`
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="insights-source-strip">
        {(devicesBySource.sources || []).slice(0, 5).map((source) => {
          const sourceWidth =
            devicesBySource.totalDevices > 0
              ? (source.count / devicesBySource.totalDevices) * 100
              : 0;

          return (
            <div className="source-strip-row" key={source.source}>
              <span>{source.source}</span>
              <div>
                <i style={{ width: `${Math.min(sourceWidth, 100)}%` }} />
              </div>
              <strong>{source.count}</strong>
            </div>
          );
        })}
      </div>
    </div>
  )}
  {/*
    <div
      style={{
        background: "rgba(15,23,42,0.92)",
        border: "1px solid rgba(168,85,247,0.25)",
        borderRadius: "22px",
        padding: "24px",
        minHeight: "320px"
      }}
    >
      <h2 style={{ color: "#fff", marginTop: 0 }}>
        💡 Executive Insights
      </h2>

      <p style={{ color: "#94a3b8" }}>
        This will show best month, best source, top LGS, growth trends, and weak points.
      </p>

      <div
        style={{
          height: "220px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          border: "1px dashed rgba(148,163,184,0.35)",
          borderRadius: "18px",
          color: "#a855f7",
          fontWeight: "bold"
        }}
      >
        Executive Insights Coming Next
      </div>
    </div>
  */}
</div>
    </div>
  );

}
export default AdminPage;
